/* eslint-disable prefer-const */
import {
  reportCatalogResponseSchema,
  reportDetailResponseSchema,
  reportHistoryResponseSchema,
  reportExecutionResponseSchema,
  reportQueuedResponseSchema,
  reportScheduleInputSchema,
  reportScheduleListResponseSchema,
  reportScheduleResponseSchema,
  type ReportScheduleInput,
  type ReportFormat,
  type ReportParameterValues,
  User,
  Party,
  Account,
  type MeFeatures,
  type AccountProvisioning,
  type OnboardingState,
  Transfer,
  Beneficiary,
  Alert,
  SystemNotice,
  AuditEvent,
  Money,
  type AlertStatus,
  type AuditCategory,
  type AuthResponse,
  type PaginatedResponse,
  type PaginationMeta,
  type DashboardMetrics,
  type JaakVerificationRequest,
  type JaakVerificationResponse,
  type JaakDocumentAnalysisRequest,
  type JaakDocumentAnalysisResponse,
  type JaakLivenessRequest,
  type JaakLivenessResponse,
  type JaakFaceMatchRequest,
  type JaakFaceMatchResponse,
  type JaakCreateSessionRequest,
  type JaakCreateSessionResponse,
  isPmTaxRegime,
} from '@tankef/core';
import { config, logger, getJaakConfig } from '@tankef/env';
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { TokenManager } from './auth';
import type { LoginResponse } from './auth/types';
// Tipos de Relays/Tandas reubicados a ./types/relays (A2 · sin cambio de runtime).
// Re-export para consumidores + import local para las firmas de clientApi.
export type * from './types/relays';
export type * from './types/monthlyStatement';
import type { MonthlyStatement } from './types/monthlyStatement';
import type {
  RelayPaymentFrequency,
  ActiveTandasResponse,
  RelaySummary,
  RelayDetail,
  RelayInvitationPreview,
  RelayAcceptInvitationResponse,
  RelayInvitation,
  CreateRelayInvitationInput,
  CreateRelayInvitationResponse,
  CreateRelayInput,
  TurnRequest,
  TandaMyStatus,
  TandaBusinessRules,
  RelayRecalculateMode,
  RelayPaymentState,
  RelayPayment,
  FeeBreakdown,
} from './types/relays';

// Re-export OAuth utilities so consumers can import from '@tankef/api-client'
export {
  exchangeCodeForTokens,
  retrievePKCEParams,
  clearPKCEParams,
  buildAuthorizeUrl,
  refreshOAuthToken,
  revokeOAuthToken,
  getUserInfo,
  storePKCEParams,
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  type OAuthTokenResponse,
  type OAuthUserInfo,
} from './oauth/index';

// Re-export Auth API for login, logout, and password reset
export { authApi, TokenManager, oauthClient, StorageCleaner } from './auth';

export type {
  LoginRequest,
  LoginResponse,
  PasswordResetChannel,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetVerifyRequest,
  PasswordResetVerifyResponse,
  PasswordResetConfirmRequest,
  PasswordResetConfirmResponse,
  PasswordResetErrorCode,
  PasswordResetErrorBody,
  AuthTokens,
  AuthState,
  AuthUser,
} from './auth/types';

// ============================================================================
// TYPES
// ============================================================================

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

// ============================================================================
// STEP-UP MFA TYPES
// ============================================================================

/**
 * Metadata returned by backend when Step-Up MFA is required for high-value operations
 */
/**
 * Bloque `verification` del OTP de teléfono (submit/resend). Ver RFC 0021 §4.
 * `channel` es el canal EFECTIVO por el que salió el código; `fell_back` marca
 * que el SMS falló y el BE reintentó por WhatsApp. Los campos `whatsapp_*`
 * sólo vienen cuando el envío fue por SMS y el canal WhatsApp está habilitado:
 * son la fuente de verdad del timer de 60s (nunca calcularlo en el cliente).
 */
export interface PhoneOtpVerification {
  sent: boolean;
  channel: 'sms' | 'whatsapp';
  fell_back?: boolean;
  expires_in_seconds?: number;
  whatsapp_available_at?: string;
  whatsapp_available_in_seconds?: number;
}

/**
 * Empresa como la lista `GET /api/v1/admin/companies`. Los campos son los que
 * el backend ya devuelve; se tipan como opcionales los que dependen de qué tan
 * avanzado esté el expediente (una empresa recién capturada no tiene nombre
 * comercial ni nivel de KYC).
 */
export interface AdminCompany {
  id: string;
  legal_name: string;
  rfc: string | null;
  trade_name?: string | null;
  /** pending_verification | active | suspended | … */
  status: string;
  kyc_status?: string | null;
  kyc_level?: string | null;
  /** low | medium | high | prohibited — alimenta la prioridad de revisión. */
  risk_level?: string | null;
  screening_status?: string | null;
  created_at: string;
  deleted_at?: string | null;
}

/**
 * Documentos del expediente KYB. Los `document_type` válidos los fija el
 * backend (`DOCUMENT_STEP_IDS`); mandar otro responde `PM_DOC_TYPE_INVALID`.
 */
export interface AdminCompanyDocument {
  document_id: string;
  document_type: string;
  /** `pending` · `processing` · `accepted` · `rejected`. */
  status?: string | null;
  /** Lo dice el backend, no el FE: los tres del KYB interno son opcionales. */
  optional?: boolean;
  /**
   * Puede venir `false` con `uploaded_at` presente: el registro del documento
   * existe pero el archivo aún no está adjunto. Mostrarlo como "cargado" en ese
   * caso haría creer que el expediente está completo cuando no lo está.
   */
  has_file?: boolean;
  uploaded_at?: string | null;
  updated_at?: string | null;
  error_code?: string | null;
}

/** Los tres últimos son del expediente KYB interno y no viajan al proveedor. */
export const COMPANY_DOCUMENT_TYPES = [
  'company_csf',
  'comprobante_domicilio_pm',
  'acta_constitutiva',
  'poder_notarial',
  'estructura_accionaria',
  'organigrama',
  'propietario_real',
] as const;

export const OPTIONAL_COMPANY_DOCUMENT_TYPES = [
  'estructura_accionaria',
  'organigrama',
  'propietario_real',
] as const;

export interface AdminBeneficialOwner {
  full_name: string;
  curp?: string | null;
  rfc?: string | null;
  date_of_birth?: string | null;
  nationality?: string | null;
  birth_country_code?: string | null;
  ownership_percentage?: number | string | null;
  control_type?: string | null;
  is_pep?: boolean | null;
  pep_position?: string | null;
  id_document_type?: string | null;
  id_document_number?: string | null;
  address?: Record<string, string> | null;
}

/**
 * Filtros de la cola de empresas — exactamente los que `filtered_scope` acepta
 * en el backend.
 *
 * No hay búsqueda parcial por razón social: el campo está cifrado con
 * encriptación no determinística y no se puede consultar con `LIKE`. El RFC sí
 * busca, pero **exacto**, contra la columna determinística `tax_id_hash`.
 * Ofrecer un buscador difuso aquí sería prometer algo que el backend no puede
 * cumplir, y el operador culparía a sus datos, no al filtro.
 */
export interface AdminCompanyFilters {
  page?: number;
  per_page?: number;
  /** RFC completo. Coincidencia exacta, no parcial. */
  rfc?: string;
  status?: string;
  kyc_status?: string;
  kyc_level?: string;
  risk_level?: string;
  screening_status?: string;
  /** ISO date. Filtra por fecha de alta. */
  created_from?: string;
  created_to?: string;
  /** Incluye empresas dadas de baja (`deleted_at` presente). */
  include_deleted?: string;
}

/**
 * Detalle 360. `granted_actions` viene del backend según el rol de quien
 * consulta: la interfaz pinta los botones que esa lista autoriza en vez de
 * duplicar el RBAC del lado del cliente.
 */
export interface AdminCompanyDetail extends AdminCompany {
  fiscal_address?: Record<string, string | null> | null;
  incorporation_date?: string | null;
  deed_number?: string | null;
  notary_number?: string | null;
  notary_state?: string | null;
  tax_regime_code?: string | null;
  /** Acciones que el rol de quien consulta tiene permitidas sobre esta empresa. */
  granted_actions?: string[];
  counts?: {
    documents?: number;
    beneficial_owners?: number;
    open_alerts?: number;
    blacklist_checks?: number;
  };
  kyc?: {
    status?: string | null;
    level?: string | null;
    verified_at?: string | null;
    expires_at?: string | null;
  };
  risk?: {
    level?: string | null;
    score?: number | null;
    calculated_at?: string | null;
  };
  screening?: {
    status?: string | null;
    last_screened_at?: string | null;
    next_screening_at?: string | null;
  };
  /** Beneficiarios controladores: agregado, no la lista (los nombres son PII). */
  ubo?: {
    verified?: boolean;
    verified_at?: string | null;
    count?: number;
  };
  accounts?: Array<Record<string, unknown>>;
  onboarding?: {
    session_id?: string | null;
    current_step?: string | null;
    state?: string | null;
    origin?: string | null;
  } | null;
}

/**
 * Acciones del ciclo de vida del expediente PM (hoja BO-PM 03).
 *
 * El orden no es libre: `submit_for_review → assign_review →
 * recommend_approval → approve|reject`. El backend impone el maker-checker
 * (quien envía a revisión no puede aprobar) y devuelve la lista de
 * precondiciones incumplidas; aquí sólo se transportan.
 */
export type CompanyLifecycleAction =
  | 'submit_for_review'
  | 'assign_review'
  | 'recommend_approval'
  | 'approve'
  | 'reject'
  | 'suspend'
  | 'unsuspend'
  | 'deactivate'
  | 'reactivate'
  | 'run_screening';

export interface CompanyLifecyclePayload {
  /** Obligatorio en `recommend_approval`; opcional en `approve`. */
  notes?: string;
  /** Obligatorio en `reject`, `deactivate` y `suspend`. */
  reason_code?: string;
  reason?: string;
}

/**
 * Precondición incumplida que devuelve `approve` en
 * `error.details.blockers`. Se pintan como lista: el backend las evalúa TODAS
 * y las manda juntas para no arreglar una por intento.
 */
export interface CompanyApprovalBlocker {
  code: string;
  message: string;
}

/** Roles de la cuenta del cliente, no del backoffice (`InviteUserCommand::ROLES`). */
export const COMPANY_INVITATION_ROLES = ['administrator', 'authorizer', 'operator'] as const;
export type CompanyInvitationRole = (typeof COMPANY_INVITATION_ROLES)[number];

/** `email` llega como `[FILTERED]` sin permiso `bo_companies.view_pii`. */
export interface AdminCompanyInvitation {
  invitation_id: string;
  email: string;
  role: string;
  /** pending · accepted · expired · revoked — derivado por el backend. */
  status: string;
  expires_at?: string | null;
  accepted_at?: string | null;
  revoked_at?: string | null;
  created_at?: string | null;
}

export interface StepUpMFARequirement {
  reason: 'high_value_transfer' | 'sensitive_operation';
  purpose: 'operation_confirmation';
  mfa_enabled: boolean;
  mfa_channel: 'totp' | 'sms' | 'email';
  mfa_setup_required: boolean;
  challenge_sent: boolean;
  masked_destination: string | null;
  challenge_endpoint: string;
  verify_endpoint: string;
  setup_endpoint: string | null;
  step_up_token_header: string;
  token_ttl_seconds: number;
  threshold: number;
}

/**
 * Error response when Step-Up MFA is required (SEC-005)
 */
export interface StepUpMFAErrorResponse {
  success: false;
  error: {
    code: 'SEC-005';
    message: string;
  };
  step_up: StepUpMFARequirement;
  correlation_id: string;
}

/**
 * Type guard to check if an error requires Step-Up MFA verification
 */
export function isStepUpMFARequired(error: unknown): error is AxiosError<StepUpMFAErrorResponse> {
  if (!axios.isAxiosError(error)) return false;
  const data = error.response?.data;
  return data?.error?.code === 'SEC-005' && data?.step_up !== undefined;
}

/**
 * Login MFA Challenge response (403 Forbidden during login when MFA is required)
 */
export interface LoginMFAChallengeResponse {
  mfa_token: string;
  channel: 'totp' | 'sms' | 'email';
  masked_destination?: string;
  error_description?: string;
}

/**
 * Type guard to check if an error requires MFA verification during login
 */
export function isLoginMFARequired(error: unknown): error is AxiosError<LoginMFAChallengeResponse> {
  if (!axios.isAxiosError(error)) return false;
  const data = error.response?.data;
  return error.response?.status === 403 && data?.mfa_token !== undefined;
}

// ============================================================================
// TOKEN UTILITIES
// ============================================================================

// ============================================================================
// SECURITY HEADERS
// ============================================================================

const FINANCIAL_POST_ROUTES = [
  '/api/v1/transfers',
  '/api/v1/payment-orders',
  '/api/v1/beneficiaries',
];

// Rutas KYC que el FE puede reintentar automáticamente (retry on transient
// errors). Mandar Idempotency-Key evita que un retry duplique side-effects
// en BE (doble llamada a JAAK blacklist, doble update del party, doble
// auditoría de corrección manual).
//
// Requisito FATF/CNBV: KYC debe ser auditable y trazable; sin idempotency
// un retry crearía 2 registros de "check" y dificultaría la auditoría.
const IDEMPOTENT_KYC_POST_ROUTES = ['/kyc/confirm-data', '/kyc/check-blacklist'];

function isFinancialPostRoute(url: string | undefined, method: string | undefined): boolean {
  if (method?.toLowerCase() !== 'post' || !url) return false;
  return FINANCIAL_POST_ROUTES.some(route => url.includes(route));
}

function isIdempotentKycPostRoute(url: string | undefined, method: string | undefined): boolean {
  if (method?.toLowerCase() !== 'post' || !url) return false;
  return IDEMPOTENT_KYC_POST_ROUTES.some(route => url.includes(route));
}

// ============================================================================
// RATE LIMITING & BACKOFF
// ============================================================================

const MAX_RETRY_COUNT = 3;

const authBackoff = { failureCount: 0, lastFailureAt: 0 };

function isAuthEndpointUrl(url: string): boolean {
  return url.includes('/auth/login') || url.includes('/oauth/token');
}

/**
 * Endpoints que el BE expone con auth OPCIONAL: aceptan tanto requests
 * autenticadas como anónimas. Un 401 en cualquiera de estos NO debe
 * disparar logout global — sólo significa que el token enviado expiró,
 * pero el endpoint en sí es público. Dejamos que la query reaccione
 * localmente (mostrar Alert, hacer login-on-action, etc.) en lugar de
 * destruir la sesión y forzar un redirect a /login.
 *
 * Caso típico: `GET /api/v1/relays/by-token/<token>` para preview de
 * invitación. Si un usuario aterriza acá vía link compartido con un
 * token caducado en storage, antes el interceptor lo sacaba a /login;
 * ahora la página renderiza el preview público sin sesión.
 *
 * Para agregar un endpoint nuevo: añadirlo a la lista + escribir un
 * comment explicando por qué es público.
 */
const PUBLIC_OPTIONAL_AUTH_ROUTES = [
  '/api/v1/relays/by-token/', // preview público de invitación de tanda
  // Catálogo SEPOMEX de códigos postales: público por diseño, se consulta desde
  // formularios de dirección que corren tanto con sesión como sin ella (alta de
  // empresa antes de que exista token de onboarding). Un 401 acá no debe sacar
  // al usuario del flujo.
  '/api/v1/catalogs/postal-codes/',
] as const;

function isPublicOptionalAuthRoute(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_OPTIONAL_AUTH_ROUTES.some(prefix => url.includes(prefix));
}

function getAuthBackoffDelay(): number {
  if (authBackoff.failureCount === 0) return 0;
  const elapsed = Date.now() - authBackoff.lastFailureAt;
  const requiredDelay = Math.min(Math.pow(2, authBackoff.failureCount - 1) * 1000, 300_000);
  return Math.max(requiredDelay - elapsed, 0);
}

function recordAuthFailure(): void {
  authBackoff.failureCount++;
  authBackoff.lastFailureAt = Date.now();
}

function resetAuthBackoff(): void {
  authBackoff.failureCount = 0;
  authBackoff.lastFailureAt = 0;
}

// ============================================================================
// HS256 DEPRECATION DETECTION
// ============================================================================

let hs256DeprecationWarned = false;

// ============================================================================
// TOKEN STORAGE — single source of truth: TokenManager
// Legacy helpers kept for external consumers; internally we use TokenManager.
// ============================================================================

const TOKEN_KEY = 'tankef_auth_token';
const REFRESH_TOKEN_KEY = 'tankef_refresh_token';

/** @deprecated prefer TokenManager.getAccessToken() internally */
export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  // TokenManager writes to sessionStorage; localStorage is the long-lived copy.
  return TokenManager.getAccessToken() ?? localStorage.getItem(TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  // localStorage = rememberMe=true; sessionStorage = rememberMe=false (tab-scoped)
  return localStorage.getItem(REFRESH_TOKEN_KEY) ?? sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setStoredTokens(token: string, refreshToken: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearStoredTokens(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ============================================================================
// REFRESH QUEUE — prevents concurrent 401 storms (token rotation attack surface)
// All simultaneous 401s share one in-flight refresh Promise.
// ============================================================================

/** Resolves to the new access_token; null when no refresh token is available. */
let refreshQueue: Promise<string | null> | null = null;

async function executeTokenRefresh(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const { refreshOAuthToken } = await import('./oauth/oauth-client');
    const resp = await refreshOAuthToken(refreshToken);
    TokenManager.updateTokens(
      resp.access_token,
      resp.refresh_token,
      resp.expires_in,
      resp.scope ?? ''
    );
    logger.info('[Auth] Token refreshed via queue');
    return resp.access_token;
  } catch (err) {
    // Multi-tab rotation guard: if another tab already refreshed and stored a
    // new refresh token, our attempt failed because the server rotated it.
    // Use the already-valid token from storage instead of logging the user out.
    const currentRefreshToken = getStoredRefreshToken();
    if (currentRefreshToken && currentRefreshToken !== refreshToken) {
      logger.warn('[Auth] Refresh token rotated by another tab — using current token');
      return getStoredToken();
    }

    logger.error('[Auth] Token refresh failed:', err);
    clearStoredTokens();
    return null;
  }
}

/**
 * Returns a shared Promise for the ongoing refresh, or starts a new one.
 * Callers await this; only one HTTP request is made regardless of concurrency.
 */
function getOrCreateRefreshQueue(): Promise<string | null> {
  if (!refreshQueue) {
    refreshQueue = executeTokenRefresh().finally(() => {
      refreshQueue = null;
    });
  }
  return refreshQueue;
}

// ============================================================================
// ACCEPT-LANGUAGE
// ============================================================================

/**
 * Idioma con el que el backend localiza `error.message` / `reason`.
 *
 * Sin este header manda el navegador: un Chrome en inglés recibía la copy del
 * 422 en inglés dentro de una UI en español (visto en DEV 2026-09-09,
 * FE_BE_ALIGNMENT §9.e del backend). El fallback es `es` porque es el idioma
 * por defecto del producto.
 *
 * La fuente es la misma que usa `@tankef/i18n` para recordar la elección del
 * usuario (`localStorage['tankef-language']`, `lookupLocalStorage` del
 * detector). Se lee por request, no al crear el cliente, para que un cambio de
 * idioma en caliente aplique sin recargar; y se lee de storage en vez de
 * importar el paquete de i18n para no meterle a `api-client` una dependencia de
 * React que no necesita.
 */
const ACCEPT_LANGUAGE_FALLBACK = 'es';
const LANGUAGE_STORAGE_KEY = 'tankef-language';
const SUPPORTED_LANGUAGES = ['es', 'en'];

export function resolveAcceptLanguage(): string {
  try {
    if (typeof window === 'undefined') return ACCEPT_LANGUAGE_FALLBACK;
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    // `es-MX` → `es`: el detector guarda el código completo del navegador.
    const base = stored ? stored.split('-')[0] : null;
    if (base && SUPPORTED_LANGUAGES.includes(base)) return base;
  } catch {
    // Storage bloqueado (modo privado, políticas del navegador): al fallback.
  }
  return ACCEPT_LANGUAGE_FALLBACK;
}

/**
 * Registra el header en un cliente. Respeta un `Accept-Language` explícito por
 * request: quien lo pase gana (p.ej. un flujo que deba forzar un idioma).
 */
function applyAcceptLanguage(client: AxiosInstance): AxiosInstance {
  client.interceptors.request.use(requestConfig => {
    if (requestConfig.headers && !requestConfig.headers['Accept-Language']) {
      requestConfig.headers['Accept-Language'] = resolveAcceptLanguage();
    }
    return requestConfig;
  });
  return client;
}

// ============================================================================
// AXIOS CLIENT
// ============================================================================

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: config.apiUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Request interceptor — proactive refresh + auth headers + security headers
  client.interceptors.request.use(
    async requestConfig => {
      const url = requestConfig.url || '';
      const extendedConfig = requestConfig as ExtendedAxiosRequestConfig;

      // Auth backoff — wait if there were consecutive auth failures
      if (isAuthEndpointUrl(url)) {
        const backoffDelay = getAuthBackoffDelay();
        if (backoffDelay > 0) {
          logger.warn(`[Auth] Backoff: waiting ${Math.round(backoffDelay / 1000)}s`);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
        }
      }

      // Post-refresh retries: the response interceptor already stamped the new
      // token on the request config. Don't re-read storage — concurrent retries
      // flushing storage while this interceptor runs is the source of AUTH-001
      // ("Missing or invalid authentication credentials") on valid tokens.
      if (!extendedConfig._retry) {
        // Proactive token refresh: renew before expiry instead of waiting for a 401.
        // Skipped on auth endpoints to avoid circular refresh calls.
        let token = TokenManager.getAccessToken();
        if (token && TokenManager.isAccessTokenExpired() && !isAuthEndpointUrl(url)) {
          logger.info('[Auth] Proactive refresh: token expired, refreshing before request');
          const newToken = await getOrCreateRefreshQueue();
          if (!newToken) {
            if (typeof window !== 'undefined') window.location.href = '/login';
            return Promise.reject(new Error('Session expired'));
          }
          token = newToken;
        }

        // Fall back to localStorage copy for backwards-compat (e.g. first load before
        // sessionStorage is hydrated from a rememberMe=true login).
        if (!token) token = localStorage.getItem(TOKEN_KEY);

        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }
      }

      // X-Correlation-ID for distributed tracing
      requestConfig.headers['X-Correlation-ID'] = crypto.randomUUID();

      // Idempotency-Key for financial POST operations
      if (isFinancialPostRoute(requestConfig.url, requestConfig.method)) {
        requestConfig.headers['Idempotency-Key'] = crypto.randomUUID();
      }

      // A3: Idempotency-Key para KYC POST que el FE puede retry-ear.
      // Si el caller pasa un header explícito (ej. retry manual con misma key),
      // lo respetamos — solo generamos uno cuando no existe.
      if (
        isIdempotentKycPostRoute(requestConfig.url, requestConfig.method) &&
        !requestConfig.headers['Idempotency-Key']
      ) {
        requestConfig.headers['Idempotency-Key'] = crypto.randomUUID();
      }

      logger.debug(`API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`);
      return requestConfig;
    },
    error => {
      logger.error('API Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle deprecation warnings, rate limits, 401 refresh
  client.interceptors.response.use(
    response => {
      logger.debug(`API Response: ${response.status} ${response.config.url}`);

      // HS256 deprecation detection (log once per session)
      if (!hs256DeprecationWarned && response.headers['x-deprecated-auth']) {
        hs256DeprecationWarned = true;
        const sunset = response.headers['sunset'] || 'unknown';
        const guide = response.headers['x-migration-guide'] || '';
        logger.warn(
          `[AUTH DEPRECATION] HS256 tokens are deprecated. Sunset: ${sunset}. ${guide ? `Migration guide: ${guide}` : 'Please migrate to OAuth ES256.'}`
        );
      }

      // Reset auth backoff on successful auth requests
      const url = response.config.url || '';
      if (isAuthEndpointUrl(url)) {
        resetAuthBackoff();
      }

      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;
      const requestUrl = originalRequest?.url || '';

      // --- Handle 429 Too Many Requests ---
      // Only auto-retry idempotent methods. Retrying POST/PATCH/DELETE can cause
      // duplicate side-effects (e.g. multiple transfers triggering RATE-001).
      const method = (originalRequest?.method || 'get').toLowerCase();
      const isIdempotentMethod = method === 'get' || method === 'head' || method === 'options';

      if (error.response?.status === 429 && originalRequest && isIdempotentMethod) {
        const retryCount = originalRequest._retryCount ?? 0;
        if (retryCount < MAX_RETRY_COUNT) {
          originalRequest._retryCount = retryCount + 1;
          const retryAfter = error.response.headers['retry-after'];
          const delaySeconds = retryAfter ? parseInt(retryAfter, 10) : Math.pow(2, retryCount);
          const delayMs = (isNaN(delaySeconds) ? Math.pow(2, retryCount) : delaySeconds) * 1000;

          logger.warn(
            `Rate limited (429). Retrying in ${delayMs / 1000}s (attempt ${retryCount + 1}/${MAX_RETRY_COUNT})`
          );
          await new Promise(resolve => setTimeout(resolve, delayMs));
          return client(originalRequest);
        }
        logger.error('Rate limit exceeded: max retries reached');
      }

      // --- Track auth endpoint failures for backoff ---
      if (error.response && isAuthEndpointUrl(requestUrl)) {
        recordAuthFailure();
      }

      // --- Handle 401 Unauthorized ---
      const isAuthEndpoint =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/register') ||
        requestUrl.includes('/auth/refresh') ||
        requestUrl.includes('/oauth/token');

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint &&
        // Endpoints públicos con auth opcional NO deben disparar logout
        // global ante un 401. Ver doc en `PUBLIC_OPTIONAL_AUTH_ROUTES`.
        !isPublicOptionalAuthRoute(requestUrl)
      ) {
        originalRequest._retry = true;

        // Correlation ID para trazar exactamente qué request causó el
        // logout en logs de producción. Antes el log decía solo
        // "redirecting to login" — sin pista de qué endpoint disparó.
        const correlationId = originalRequest.headers?.['X-Correlation-ID'];

        if (!getStoredRefreshToken()) {
          logger.warn('[Auth] No refresh token — redirecting to login', {
            url: requestUrl,
            correlationId,
            hasRefreshToken: false,
          });
          clearStoredTokens();
          if (typeof window !== 'undefined') window.location.href = '/login';
          return Promise.reject(error);
        }

        // Shared queue: N concurrent 401s → 1 refresh call, not N.
        const newToken = await getOrCreateRefreshQueue();

        if (!newToken) {
          logger.error('[Auth] Refresh failed — redirecting to login', {
            url: requestUrl,
            correlationId,
            hasRefreshToken: true,
          });
          if (typeof window !== 'undefined') window.location.href = '/login';
          return Promise.reject(error);
        }

        logger.info('[Auth] Token refreshed via queue, retrying original request');
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      }

      // Log other errors.
      //
      // Un non-2xx que trae el contrato de error del API
      // ({ success: false, error: { code } } — ServiceError del BE) es un
      // RESULTADO de dominio: la pantalla lo renderiza (validación,
      // precondición, conflicto) y se registra como warn. Antes esto era una
      // allowlist de 3 URLs mantenida a mano: cada respuesta de dominio nueva
      // (409 "sin ticket de revisión", 422 con precondiciones de aprobación…)
      // caía a logger.error hasta que alguien la agregara, y el overlay de
      // issues del backoffice acumulaba "errores" en flujos perfectamente
      // felices — ruido que además esconde los fallos reales.
      //
      // logger.error queda reservado a fallos de sistema: 5xx, o respuestas
      // sin contrato (HTML de un proxy, JSON ajeno). La allowlist vieja se
      // conserva como respaldo para respuestas legacy sin contrato.
      if (error.response) {
        const status = error.response.status;
        const payload = error.response.data as
          | { success?: boolean; error?: { code?: string } }
          | undefined;
        const hasErrorContract = payload?.success === false && Boolean(payload?.error?.code);
        const isExpectedFlow =
          hasErrorContract ||
          (status === 422 && requestUrl.includes('/auth/password/reset/confirm')) ||
          (status === 409 &&
            requestUrl.includes('/onboarding/sessions/') &&
            requestUrl.includes('/email')) ||
          (status === 404 && requestUrl.includes('/feature_flags'));
        const logFn = status >= 500 || !isExpectedFlow ? logger.error : logger.warn;
        logFn(`API Error: ${status} ${requestUrl}`, error.response.data);
      } else if (error.request) {
        logger.error('API Error: No response received', error.request);
      } else {
        logger.error('API Error:', error.message);
      }

      return Promise.reject(error);
    }
  );

  return client;
}

export const apiClient = applyAcceptLanguage(createApiClient());

// ============================================================================
// ONBOARDING API CLIENT
// ============================================================================

function createOnboardingClient(): AxiosInstance {
  const client = axios.create({
    baseURL: config.onboardingApiUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Request interceptor - Add correlation ID
  client.interceptors.request.use(
    requestConfig => {
      // requestConfig.headers['X-Correlation-ID'] = crypto.randomUUID();
      logger.debug(
        `Onboarding API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`
      );
      return requestConfig;
    },
    error => {
      logger.error('Onboarding API Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - Log responses
  client.interceptors.response.use(
    response => {
      logger.debug(`Onboarding API Response: ${response.status} ${response.config.url}`);
      return response;
    },
    (error: AxiosError) => {
      if (error.response) {
        const status = error.response.status;
        // 4xx business errors (conflict, validation) are expected flows — log as warn.
        // 5xx and network failures are real errors.
        const logFn = status >= 500 ? logger.error : logger.warn;
        logFn(`Onboarding API Error: ${status} ${error.config?.url}`, error.response.data);
      } else if (error.request) {
        logger.error('Onboarding API Error: No response received', error.request);
      } else {
        logger.error('Onboarding API Error:', error.message);
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const onboardingClient = applyAcceptLanguage(createOnboardingClient());

// ============================================================================
// DIRECT KYC CLIENT (bypasses MSW for FormData uploads)
// ============================================================================

/**
 * Cliente directo para uploads de KYC (sin pasar por MSW)
 * MSW tiene un bug conocido donde passthrough() pierde el body de FormData
 * Este cliente no es interceptado por MSW y envía FormData correctamente
 */
function createDirectKycClient(): AxiosInstance {
  return axios.create({
    baseURL: config.onboardingApiUrl,
    timeout: 60000, // Mayor timeout para uploads de archivos
    // NO establecer Content-Type para que Axios lo detecte automáticamente con FormData
    headers: {
      Accept: 'application/json',
    },
  });
}

const directKycClient = applyAcceptLanguage(createDirectKycClient());

/**
 * Cliente directo al API principal para uploads multipart (p.ej. comprobante
 * de pago de tanda). El `apiClient` fuerza `Content-Type: application/json`
 * por default, lo que impide que axios calcule el boundary del
 * `multipart/form-data` → el backend recibe el FormData sin el campo y
 * responde 422 ("payment_receipt requerido"). Este cliente NO fija
 * Content-Type, así axios lo detecta solo. Auth se adjunta por request.
 */
function createDirectUploadClient(): AxiosInstance {
  return axios.create({
    baseURL: config.apiUrl,
    timeout: 60000,
    headers: {
      Accept: 'application/json',
    },
  });
}

const directUploadClient = applyAcceptLanguage(createDirectUploadClient());

// ============================================================================
// ONBOARDING SESSION HELPERS
// ============================================================================

/**
 * Obtiene el session_id del sessionStorage
 * @throws Error si no hay session_id
 */
function getOnboardingSessionId(): string {
  if (typeof window === 'undefined') throw new Error('No window object');
  // Use TokenManager for consistency
  const sessionId = TokenManager.getOnboardingSessionId();
  if (!sessionId) {
    throw new Error('No onboarding session found. Please start the registration process.');
  }
  return sessionId;
}

/**
 * Construye la URL de un endpoint de onboarding con session_id
 */
function buildOnboardingUrl(path: string): string {
  const sessionId = getOnboardingSessionId();
  return `/api/v1/onboarding/sessions/${sessionId}/${path}`;
}

// ============================================================================
// CONTRATO: POST /api/v1/onboarding/sessions/{id}/kyc/documents
// ============================================================================

/**
 * Banderas del veredicto de JAAK, sin PII. Vienen del bloque
 * `document_evaluation` del 422 (swagger `kyc/documents`, respuesta 422;
 * FE_BE_ALIGNMENT §9.e y §9.g del backend).
 */
export interface KycDocumentEvaluation {
  evaluation?: string;
  documentValidity?: boolean;
  /**
   * `false` = llegó la misma cara en las dos posiciones. Acompaña a `KYC-003`,
   * que es el código del rechazo; el BE puede emitirlo sin esta bandera cuando
   * la recaptura la dispara el mensaje del detector de lados.
   */
  documentCompleteSides?: boolean;
  documentExpired?: boolean;
  /** Tipo clasificado por JAAK (`MX_INE`…). */
  documentType?: string | null;
  /**
   * Lado clasificado por JAAK. INFORMATIVO: está pendiente que el proveedor
   * confirme si describe la imagen enviada o el lado que falló. No decidir copy
   * sobre él (§9.e).
   */
  detectedSide?: string | null;
  /** `status` de `/v4/document/extract` normalizado. Ausente en el contrato previo. */
  extraction_status?: string;
  /** Diagnóstico del proveedor. Puede enumerar NOMBRES de campo, nunca valores. */
  message?: string;
  /**
   * Motivo cerrado del rechazo: `image_quality | expired | invalid_document |
   * incomplete_sides | authenticity | unreadable | unknown`. Copia del de la
   * raíz del 422. Ausente en BE previos al campo.
   */
  rejection_reason?: string;
  /** Banderas de JAAK, sólo si el proveedor las devolvió. */
  imageQuality?: boolean;
  dataConsistent?: boolean;
}

/** Body del 200 (dentro de `data`). Campos `warnings`/`verification_state` de §9.f. */
export interface OnboardingKycDocumentsResult {
  step: string;
  status: string;
  next_step: { block: string; step: string };
  progress: number;
  document_verified?: boolean;
  /** Avisos sin PII, p.ej. `["ocr_retried_after_blurry"]`. `[]` si no hubo. */
  warnings?: string[];
  /** Banderas de `/v3/document/verify`. `{}` si el proveedor no las devolvió. */
  verification_state?: Record<string, unknown>;
  // Evaluación del documento por el proveedor de identidad (JAAK). Presente
  // cuando el BE reporta un rechazo suave (p.ej. INE vencida) en el body de
  // una respuesta 2xx. Ver `isDocumentRejected` en apps/web (kyc/model).
  evaluation?: string;
  documentValidity?: boolean;
  documentExpired?: boolean;
  personalIdNumberMatch?: boolean;
  message?: string;
  document_evaluation?: KycDocumentEvaluation;
}

/**
 * Body del 422. `retryable`, `next_action` y `attempts_left` viven en la RAÍZ,
 * no dentro de `error` — decidir por ellos y por `error.code`, nunca por el
 * texto de `error.message`, que el BE localiza con `Accept-Language`.
 */
export interface OnboardingKycDocumentsRejection {
  success?: boolean;
  error?: { code?: string; message?: string };
  step?: string;
  step_status?: string;
  /** `false` sólo en `KYC-005` (intentos agotados): estado terminal. */
  retryable?: boolean;
  /** `retry` | `block` | `proceed`. `block` en `KYC-005`. */
  next_action?: string;
  /** Capturas restantes en la sesión (3 → 0). Ausente si el error no consume intento. */
  attempts_left?: number;
  reason?: string;
  /**
   * Motivo cerrado del rechazo (`image_quality | expired | invalid_document |
   * incomplete_sides | authenticity | unreadable | unknown`). Ausente en
   * `KYC-007`, en errores `ONBOARDING-*` y en BE previos al campo: decidir
   * entonces por `error.code`. La compuerta de calidad de imagen sólo manda
   * este campo, sin `document_evaluation`.
   */
  rejection_reason?: string;
  evaluation?: string | null;
  document_evaluation?: KycDocumentEvaluation | null;
}

/**
 * Espera a que el BE termine de verificar la captura de INE (RFC 0028).
 *
 * Con `kyc_async_capture` encendido, `POST kyc/documents` responde 202 y la
 * verificación con el proveedor sigue en un job. `GET kyc/status` ya publica
 * el estado por paso, así que no hace falta endpoint nuevo: se consulta hasta
 * verlo `completed` o `failed`.
 *
 * Devuelve la misma forma que la respuesta síncrona para que la UI no tenga
 * que saber cuál de los dos caminos ocurrió.
 */
const KYC_CAPTURE_POLL_DEFAULT_MS = 2000;
const KYC_CAPTURE_POLL_TIMEOUT_MS = 240_000;

/** Lo que el sondeo lee de `data.steps[<paso>]` de `GET .../kyc/status`. */
interface KycCaptureStatusStep {
  status?: string;
  /** Identificador interno del motivo (`document_unreadable`, …). */
  error?: string;
  /** Campos que el BE aún NO publica por paso; se usan si algún día llegan. */
  error_code?: string;
  reason?: string;
  retryable?: boolean;
  next_action?: string;
  attempts_left?: number;
}

/**
 * Traducción del identificador interno del paso al contrato estable del 422.
 *
 * PUENTE, no contrato: `GET .../kyc/status` publica hoy sólo `status`, `label`,
 * `required`, `started_at` y `completed_at` por paso —
 * `OnboardingFlowEngine#ui_blocks_status` del backend construye ese hash y deja
 * fuera todo lo demás—, así que ni `error_code`, ni `retryable`, ni
 * `attempts_left` viajan por ahí. El motivo sí se persiste en la sesión
 * (`blocks_status.kyc.steps.ine_capture.error`, escrito por `fail_step!`), que
 * es lo que este mapa espera ver el día que el BE lo exponga.
 *
 * Se retira cuando `kyc/status` publique `error_code` / `retryable` /
 * `next_action` / `attempts_left` por paso: la lectura de esos campos ya está
 * escrita abajo y gana sobre el mapa.
 */
const KYC_CAPTURE_STEP_ERROR_CONTRACT: Record<
  string,
  {
    code?: string;
    retryable: boolean;
    nextAction: string;
    attemptsLeft?: number;
    documentCompleteSides?: false;
  }
> = {
  // Mismo par que el 422 síncrono del BE: el código `KYC-003` —vigente, lo
  // emite `SubmitDocumentsCommand#recapture_required?`— y la bandera del
  // proveedor. El FE decide por el código y usa la bandera de respaldo.
  document_incomplete_sides: {
    code: 'KYC-003',
    retryable: true,
    nextAction: 'retry',
    documentCompleteSides: false,
  },
  document_unreadable: { code: 'KYC-002', retryable: true, nextAction: 'retry' },
  document_rejected: { code: 'KYC-004', retryable: true, nextAction: 'retry' },
  document_attempts_exhausted: {
    code: 'KYC-005',
    retryable: false,
    nextAction: 'block',
    attemptsLeft: 0,
  },
};

/**
 * Texto de último recurso. NO se compone con el identificador del paso: ése es
 * interno (`document_attempts_exhausted`) y enseñárselo al usuario fue
 * exactamente el defecto que este puente cierra. La copy que se ve en pantalla
 * la resuelve la app por CÓDIGO (`kycMessageOverrides`), no este módulo, que no
 * tiene i18n.
 */
const KYC_CAPTURE_REJECTED_FALLBACK = 'La verificación del documento falló';

/** El rechazo asíncrono, con la MISMA forma que el 422 del camino síncrono. */
type KycCaptureRejectionError = Error & {
  /** `axios.isAxiosError` sólo mira esta bandera, y de ella depende que
   *  `handleKycError` clasifique el rechazo por status y por código en vez de
   *  caer a la rama "error no HTTP" y pintar el mensaje crudo. */
  isAxiosError: true;
  response: { status: number; data: OnboardingKycDocumentsRejection & { step_error?: string } };
};

function buildKycCaptureRejection(
  stepKey: string,
  step: KycCaptureStatusStep
): KycCaptureRejectionError {
  const stepError = typeof step.error === 'string' ? step.error.trim() : '';
  const mapped = stepError ? KYC_CAPTURE_STEP_ERROR_CONTRACT[stepError] : undefined;

  const code = step.error_code?.trim() || mapped?.code;
  const retryable =
    typeof step.retryable === 'boolean' ? step.retryable : (mapped?.retryable ?? true);
  const nextAction = step.next_action?.trim() || mapped?.nextAction || 'retry';
  const attemptsLeft =
    typeof step.attempts_left === 'number' ? step.attempts_left : mapped?.attemptsLeft;
  const message = step.reason?.trim() || KYC_CAPTURE_REJECTED_FALLBACK;

  const error = new Error(message) as KycCaptureRejectionError;
  error.name = 'KycCaptureRejected';
  error.isAxiosError = true;
  error.response = {
    status: 422,
    data: {
      success: false,
      // Sin código inventado: un motivo que el mapa no conoce llega sin
      // `code`, igual que los caminos del BE que aún no lo mandan.
      error: code ? { code, message } : { message },
      step: stepKey,
      step_status: 'rejected',
      retryable,
      next_action: nextAction,
      ...(attemptsLeft === undefined ? {} : { attempts_left: attemptsLeft }),
      reason: message,
      ...(mapped?.documentCompleteSides === false
        ? { document_evaluation: { documentCompleteSides: false } }
        : {}),
      // Identificador interno, para el log del step. No se muestra.
      ...(stepError ? { step_error: stepError } : {}),
    },
  };
  return error;
}

async function pollKycCaptureUntilResolved({
  stepKey,
  retryAfterMs,
}: {
  stepKey?: string;
  retryAfterMs?: number;
}): Promise<{
  step: string;
  status: string;
  next_step: { block: string; step: string };
  progress: number;
}> {
  const intervalMs =
    typeof retryAfterMs === 'number' && retryAfterMs > 0
      ? retryAfterMs
      : KYC_CAPTURE_POLL_DEFAULT_MS;
  const deadline = Date.now() + KYC_CAPTURE_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, intervalMs));

    const response = await onboardingClient.get(buildOnboardingUrl('kyc/status'), {
      headers: getOnboardingAuthHeaders(),
    });
    const data = response.data?.data ?? response.data;
    // El BE resuelve el nombre real del paso según el flujo (en PM es
    // `ine_capture_admin`), y lo manda en el 202. Si no vino, se cae al
    // canónico.
    const key = stepKey || 'ine_capture';
    const step = data?.steps?.[key];

    if (step?.status === 'completed') {
      return {
        step: key,
        status: 'completed',
        next_step: data.next_step,
        progress: data.progress,
      };
    }

    if (step?.status === 'failed') {
      // Se lanza, no se resuelve: el camino síncrono devuelve 4xx cuando el
      // documento se rechaza, y las pantallas ya saben tratar eso. Un rechazo
      // que llegara como resolución silenciosa dejaría al usuario avanzando
      // con la captura reprobada.
      //
      // Y se lanza CON cuerpo: sin `response.data` el step lee `null` en
      // `readKycRejectionSignals`, el estado terminal de KYC-005 deja de
      // detectarse y la pantalla ofrece recapturar contra un paso cerrado.
      throw buildKycCaptureRejection(key, step as KycCaptureStatusStep);
    }
  }

  const timeout = new Error(
    'La verificación del documento sigue en proceso. Vuelve a intentarlo en unos minutos.'
  );
  timeout.name = 'KycCaptureTimeout';
  throw timeout;
}

/**
 * Obtiene los headers de autenticación para onboarding
 * Usa TokenManager para acceso seguro al token (retorna null si no existe)
 */
function getOnboardingAuthHeaders(): Record<string, string> {
  const token = TokenManager.getOnboardingToken();
  if (!token) {
    // Retorna headers vacíos en lugar de lanzar error
    // Algunos endpoints pueden funcionar sin autenticación
    logger.warn('[getOnboardingAuthHeaders] No onboarding token found');
    return {};
  }
  // H2: si el token ya caducó client-side, no lo mandamos. El BE rebotaría
  // 401 igual pero ahorramos round-trip y damos contexto al caller (puede
  // redirigir a /register para retomar). El FE log identifica el origen
  // para no confundir con un 401 genuino del servidor.
  if (TokenManager.isOnboardingTokenExpired()) {
    logger.warn('[getOnboardingAuthHeaders] Onboarding token expired client-side');
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

// ============================================================================
// RELAYS / TANDAS — TYPES (per docs/plans/TANDAS_RELAY_SPEC.md)
// ============================================================================

/**
 * Los endpoints de relays envuelven la respuesta del backend en
 * `{ success, data, message }`. Este helper pela esa envoltura: si el cuerpo
 * trae `success` + `data`, devuelve `data`; si ya viene sin envolver, lo
 * devuelve tal cual. Seguro para respuestas envueltas y sin envolver.
 */
function unwrapRelayEnvelope<T>(body: unknown): T {
  if (body !== null && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/**
 * Igual que `unwrapRelayEnvelope`, pero además des-anida la clave `relay`
 * cuando el recurso viene como `data: { relay: {...} }` — el caso de detail,
 * create, join, activate, reorder, update, patch* y search.
 */
function unwrapRelayResource<T>(body: unknown): T {
  const data = unwrapRelayEnvelope<unknown>(body);
  if (data !== null && typeof data === 'object' && 'relay' in data) {
    return (data as { relay: T }).relay;
  }
  return data as T;
}

/**
 * Respuesta de `GET /api/v1/catalogs/postal-codes/{cp}` (catálogo SEPOMEX).
 * Un CP tiene muchas colonias pero un solo municipio y estado.
 */
export interface PostalCodeLookup {
  /** El CP consultado, 5 dígitos. */
  postal_code: string;
  /** Nombre del estado en nomenclatura SEPOMEX. Siempre viene. */
  state: string;
  /** Clave de 3 letras resuelta en servidor (e.g. `AGS`). */
  state_code: string;
  /** Siempre viene. */
  municipality: string;
  /** `null` en ~64% de los CP: es el caso normal, no un error. */
  city: string | null;
  /** Colonias, orden alfabético y sin repetidos. */
  neighborhoods: string[];
}

/**
 * Resumen de tanda devuelto por `GET /api/v1/tandas/active`. Más rico que
 * `RelaySummary`: incluye progreso, rondas y montos ya formateados.
 */

/** Socio tal como lo devuelve el backend. */
export interface OnboardingShareholder {
  id: string;
  name: string;
  shares: number;
  /**
   * Porcentaje calculado por el BE. Llega como STRING para no perder precisión
   * al serializar: coaccionar con `Number()` si hace falta operarlo.
   */
  participation_percentage: string;
}

/**
 * Respuesta de la estructura accionaria. El POST y el GET comparten forma,
 * salvo que el GET añade `captured`/`captured_at` y el POST `next_step`.
 */
export interface OnboardingShareholderStructure {
  /** Sólo en el GET: `false` mientras no se haya capturado nada. */
  captured?: boolean;
  captured_at?: string;
  total_shares: number;
  /**
   * Total declarado por la empresa, o `null` si no se declaró. Se guarda aparte
   * de `captured_total_shares` a propósito: la diferencia entre los dos ES el
   * hallazgo, y con uno solo no se puede reconstruir después.
   */
  company_total_shares?: number | null;
  /** Suma de las acciones efectivamente capturadas. */
  captured_total_shares?: number;
  /** Lo capturado no cubre el total declarado. No bloquea; se reporta. */
  partial_capture?: boolean;
  shareholders: OnboardingShareholder[];
  signed?: boolean;
  /** `null` si el KYC del representante aún no confirma su nombre. */
  signer_name?: string | null;
  step?: string;
  next_step?: { block: string; step: string };
  progress?: number;
}

/**
 * Integrante del organigrama (RFC 0024). Es el `OrgChartMember` del FE sin su
 * `id` local: el backend no lo necesita porque el orden del arreglo ES el orden
 * de dibujo.
 */
export interface OnboardingOrgStructureMember {
  name: string;
  position: string;
  /** 1 = consejo de administración, 2 = dirección. Otro valor → ONB_ORG_003. */
  hierarchy: 1 | 2;
  /** Sólo uno puede serlo, y va en jerarquía 2 (ONB_ORG_004). */
  is_general_manager: boolean;
}

/** Respuesta del organigrama. El POST y el GET comparten forma. */
export interface OnboardingOrgStructure {
  /** Sólo en el GET: `false` si no se capturó, o si ganó el documento subido. */
  captured?: boolean;
  captured_at?: string;
  members: OnboardingOrgStructureMember[];
  step?: string;
  next_step?: { block: string; step: string };
  progress?: number;
}

/**
 * Propietario real tal como viaja en `company/beneficial-owner-identity`
 * (RFC 0025). El único obligatorio es `full_name`.
 *
 * **El identificador fiscal ya NO se exige** (2026-08-20): la sección fiscal
 * está marcada opcional en el diseño y no cuenta para el avance, así que el 422
 * caía sobre un formulario que la UI mostraba completo. En su lugar, a quien
 * venga sin `curp` / `rfc` / `foreign_tax_id` se le levanta un hallazgo en el
 * ticket de revisión legal — sin identificador sólo se le puede cruzar por
 * nombre normalizado, y un homónimo o un "Ma. Luisa" contra "María Luisa"
 * rompen ese cruce.
 *
 * Sólo `full_name` es obligatorio: un `{ full_name: "Ana Ruiz" }` es un POST
 * válido, lo que permite guardar parcial sin pelear con el servidor. El resto
 * de reglas son CONDICIONALES, no de presencia (ver cada campo).
 *
 * Seis nombres NO son la traducción literal del campo del form, y conviene
 * mirarlos antes de mapear:
 *
 * | Pantalla            | API                    |
 * | ------------------- | ---------------------- |
 * | `birthDate`         | `date_of_birth`        |
 * | `birthCountry`      | `birth_country_code`   |
 * | `birthCountryOther` | `birth_country_code`   |
 * | `birthState`        | `birth_state_code`     |
 * | `taxIdNumber`       | `foreign_tax_id`       |
 * | `fielSerial`        | `fiel_serial_number`   |
 * | `livesAbroad`       | `resides_abroad`       |
 */
export interface OnboardingBeneficialOwnerIdentity {
  full_name: string;
  gender?: string;
  /** ISO `YYYY-MM-DD`. Si viene: real, pasada y mayor de 18. */
  date_of_birth?: string;
  /**
   * Recibe DOS campos de la pantalla, nunca los dos a la vez: el código del
   * catálogo `countries`, o el texto libre que el usuario escribe al elegir
   * "Otro país". `birthCountryOther` no existe como columna aparte.
   */
  birth_country_code?: string;
  birth_state_code?: string;
  /**
   * Texto libre. El backend normaliza a `MX` cuando reconoce el valor y guarda
   * tal cual cuando no: la columna dejó de ser `varchar(2)`, que reventaba con
   * un 500 al recibir "Mexicana".
   */
  nationality?: string;
  occupation?: string;
  /** Texto plano: la columna dejó de ser `jsonb`, que es lo que manda el Textarea. */
  address?: string;
  phones?: string[];
  email?: string;
  /** 18 caracteres si viene. */
  curp?: string;
  rfc?: string;
  foreign_tax_id?: string;
  /** Obligatorio si hay `foreign_tax_id`. */
  foreign_tax_id_country?: string;
  fiel_serial_number?: string;
  resides_abroad?: boolean;
  /**
   * El único **nullable de verdad** de los 18: `null` es "no contestó" y
   * `false` es "declaró que no tiene domicilio en México". Si el checkbox no se
   * ha tocado va `null`, no `false` — para el expediente no es lo mismo.
   */
  has_mexican_address?: boolean | null;
  /** Texto plano, mismo caso que `address`. */
  mexican_address?: string;
}

/** Respuesta de la identificación de propietarios reales. */
export interface OnboardingBeneficialOwnerIdentityResponse {
  captured?: boolean;
  captured_at?: string;
  owners: Array<
    OnboardingBeneficialOwnerIdentity & {
      id: string;
      /**
       * Si esta persona ya pasó por la declaración de beneficiarios. El
       * PORCENTAJE no viene aquí: es contrato de `beneficial-owners`.
       */
      declared_ownership?: boolean;
    }
  >;
  signed?: boolean;
  signer_name?: string | null;
  step?: string;
  next_step?: { block: string; step: string };
  progress?: number;
}

/**
 * Documentos del expediente de un propietario real. **Cuelgan de la persona**,
 * no de la sesión: `onboarding_step_results` tiene índice único por
 * `(tenant_id, session_id, step_id)`, así que un slot por sesión no alcanzaba
 * para dos propietarios.
 *
 * Cuatro son **condicionales** y NO cuentan para `missing`: nadie es extranjero
 * y mexicano a la vez, así que exigirlos todos dejaría cualquier expediente sin
 * poder cerrarse.
 */
export type OnboardingBeneficialOwnerDocumentType =
  | 'identificacion_oficial'
  | 'comprobante_domicilio_pr'
  | 'declaracion_cuenta_propia'
  // Condicionales:
  | 'constancia_curp'
  | 'csf_pr'
  | 'pasaporte'
  | 'documento_inm';

/** Expediente documental de un propietario real, tal como lo devuelve el GET. */
export interface OnboardingBeneficialOwnerDocuments {
  owner_id: string;
  full_name: string;
  uploaded: Array<{
    document_type: OnboardingBeneficialOwnerDocumentType;
    document_id: string;
    uploaded_at?: string;
  }>;
  /** Sólo los NO condicionales que faltan. */
  missing: OnboardingBeneficialOwnerDocumentType[];
  complete: boolean;
}

/**
 * Tipos de documento del bloque `company_docs` (Persona Moral). Son los ocho de
 * `ALLOWED_DOCUMENT_TYPES` en el controller del BE (RFC 0019 §4.2.2).
 *
 * Obligatorios: `csf`, `acta_constitutiva`, `comprobante_domicilio_pm`.
 * `poder_notarial` es condicional (sólo si `power_of_attorney_required`).
 * El resto son opcionales y se pueden subir incluso después de cerrar el
 * bloque KYB.
 *
 * `rfc_homoclave` es la cédula del RFC como documento propio, aparte de la CSF
 * (Producto, 31-jul-2026: son ocho documentos, no siete). El FE lo retiró el
 * 2026-08-04 por una nota equivocada —decía que lo habíamos inventado— y se
 * repuso el 2026-08-17 tras verificarlo en `origin/develop`: está en el
 * controller, en `moral_v1.yml:196` y en RFC 0019.
 *
 * Cuatro no pasan por OCR (`NO_OCR_DOCUMENT_TYPES` del BE): `rfc_homoclave`,
 * `estructura_accionaria`, `organigrama` y `propietario_real`. Su step nace
 * terminal al subirse, con `extracted_data: null` — no hay nada que pollear.
 */
export type OnboardingCompanyDocumentType =
  | 'csf'
  | 'rfc_homoclave'
  | 'acta_constitutiva'
  | 'comprobante_domicilio_pm'
  | 'poder_notarial'
  | 'estructura_accionaria'
  | 'organigrama'
  | 'propietario_real'
  // ── SONDEO (2026-08-19) — documentos del propietario real ────────────────
  // Estos siete NO están en el contrato: los nombra el diseño y los ids son del
  // FE. Se habilitan para comprobar contra el BE si ya existen y con qué
  // nombre. Si el upload responde ONB_DOC_TYPE_INVALID, se revierten a
  // `supported: false` en `companyDocumentsCatalog.ts`.
  | 'identificacion_oficial'
  | 'constancia_curp'
  | 'csf_pr'
  | 'comprobante_domicilio_pr'
  | 'pasaporte'
  | 'documento_inm'
  | 'declaracion_cuenta_propia';

export interface BatchTransfer {
  id: string;
  status: string;
  filename: string;
  total_count: number;
  processed_count: number;
  succeeded_count: number;
  failed_count: number;
  total_amount_cents: number;
  currency_code: string;
  progress_percentage: number;
  created_at: string;
  completed_at?: string | null;
  source_account_id?: string | null;
  validation_errors?: Array<{ code?: string; message: string }>;
}

export interface BatchTransferItem {
  id: string;
  row_number: number;
  status: string;
  beneficiary_clabe: string | null;
  beneficiary_name: string | null;
  amount_cents: number;
  concept?: string | null;
  tracking_key?: string | null;
  validation_errors?: Array<{ field?: string; message: string }>;
  error_code?: string | null;
  error_message?: string | null;
}

export interface BatchTransferResultsPage {
  items: BatchTransferItem[];
  meta?: PaginationMeta;
}

export interface BatchTransferListPage {
  items: BatchTransfer[];
  meta?: PaginationMeta;
}

export const clientApi = {
  // --- Auth ---
  login: async (email: string, password: string): Promise<AuthResponse> => {
    // Backend movió login a OAuth /oauth/token (RFC 6749 password grant).
    // El endpoint viejo /api/v1/auth/login fue removido. Mismo patrón que
    // loginWithMFA abajo, pero sin mfa_token/mfa_code (login directo cuando
    // el usuario NO tiene MFA habilitado — si lo tiene, backend devuelve
    // mfa_required y el flujo continúa por loginWithMFA).
    const params = new URLSearchParams({
      grant_type: 'password',
      client_id: config.oauth.clientId,
      username: email,
      password,
    });
    if (config.oauth.clientSecret) {
      params.set('client_secret', config.oauth.clientSecret);
    }
    const response = await apiClient.post('/oauth/token', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const { access_token, refresh_token, expires_in } = response.data;
    setStoredTokens(access_token, refresh_token);

    return {
      token: access_token,
      refresh_token,
      expires_at: new Date(Date.now() + (expires_in ?? 0) * 1000).toISOString(),
      user: undefined,
    };
  },

  /**
   * POST /oauth/token (MFA completion)
   * Completa el login con código MFA cuando el usuario tiene 2FA habilitado
   */
  loginWithMFA: async (mfaToken: string, mfaCode: string): Promise<AuthResponse> => {
    const params = new URLSearchParams({
      grant_type: 'password',
      client_id: config.oauth.clientId || 'tankef-web-app',
      mfa_token: mfaToken,
      mfa_code: mfaCode,
    });
    if (config.oauth.clientSecret) {
      params.set('client_secret', config.oauth.clientSecret);
    }
    const response = await apiClient.post('/oauth/token', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    const { access_token, refresh_token } = response.data;
    setStoredTokens(access_token, refresh_token);
    return {
      token: access_token,
      refresh_token,
      expires_at: '',
      user: undefined,
    };
  },

  logout: async (): Promise<void> => {
    // Backend movió logout a OAuth /oauth/revoke (RFC 7009). El endpoint viejo
    // /api/v1/auth/logout fue removido — llamarlo causaba 404. Delegamos al
    // helper canónico que ya maneja client_id + form-urlencoded + errores
    // no-bloqueantes. Independientemente de si revoke server-side falla, el
    // cliente debe limpiar su estado local.
    try {
      const token = getStoredToken();
      if (token) {
        const { revokeOAuthToken } = await import('./oauth/oauth-client');
        await revokeOAuthToken(token, 'access_token');
      }
    } finally {
      clearStoredTokens();
    }
  },

  refreshToken: async (): Promise<AuthResponse> => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) throw new Error('No refresh token');

    // Use OAuth endpoint with grant_type=refresh_token
    const { refreshOAuthToken } = await import('./oauth/oauth-client');
    const oauthResponse = await refreshOAuthToken(refreshToken);

    setStoredTokens(oauthResponse.access_token, oauthResponse.refresh_token);

    // Map OAuth response to AuthResponse format for compatibility
    return {
      token: oauthResponse.access_token,
      refresh_token: oauthResponse.refresh_token,
      expires_at: new Date(Date.now() + oauthResponse.expires_in * 1000).toISOString(),
    };
  },

  // --- Password Reset (3-step OTP flow) ---
  requestPasswordReset: async (data: {
    email: string;
    channel?: 'sms' | 'email';
  }): Promise<{
    success: boolean;
    data: { channel: 'sms' | 'email'; message: string };
  }> => {
    const response = await apiClient.post('/api/v1/auth/password/reset', data);
    return response.data;
  },

  verifyPasswordReset: async (data: {
    email: string;
    code: string;
  }): Promise<{
    success: boolean;
    data: { reset_session_token: string; expires_in: number };
  }> => {
    const response = await apiClient.post('/api/v1/auth/password/reset/verify', data);
    return response.data;
  },

  /**
   * RFC 0022 — canje del enlace de activación de una cuenta migrada.
   *
   * No recibe email a diferencia de `verifyPasswordReset`: el enlace del correo
   * no lo lleva (sería PII en el query string) y el token trae el tenant
   * embebido. Devuelve el mismo `reset_session_token`, así que el siguiente
   * paso es `confirmPasswordReset` sin cambios.
   */
  verifyAccountClaim: async (data: {
    token: string;
  }): Promise<{
    success: boolean;
    data: { reset_session_token: string; expires_in: number };
  }> => {
    const response = await apiClient.post('/api/v1/auth/claim/verify', data);
    return response.data;
  },

  confirmPasswordReset: async (data: {
    reset_session_token: string;
    password: string;
    password_confirmation: string;
  }): Promise<{
    success: boolean;
    data: { message: string; kyc_pending: boolean };
  }> => {
    const response = await apiClient.post('/api/v1/auth/password/reset/confirm', data);
    return response.data;
  },

  /**
   * UUID del **usuario** autenticado (`data.user.id` de `/api/v1/me`).
   *
   * Existe aparte de `me()` porque aquél mapea a `Party` con
   * `id: party.id ?? user.id`: para quien necesita el id de usuario (p. ej.
   * `user_id_existing` del alta de representantes legales) ese fallback
   * devolvería el id de party, que es otro UUID. Aquí no hay ambigüedad.
   *
   * Acepta `tokenOverride` para poder llamarlo durante el onboarding, donde el
   * token en curso es el de onboarding y no el access_token de sesión.
   */
  currentUserId: async (tokenOverride?: string): Promise<string | null> => {
    const token = tokenOverride || getStoredToken();
    if (!token) throw new Error('No auth token');

    const response = await axios.get(`${config.apiUrl}/api/v1/me`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const data = response.data.data ?? response.data;
    const id = (data?.user as { id?: unknown } | undefined)?.id;
    return typeof id === 'string' ? id : null;
  },

  // --- Me (Current Party) ---
  me: async (tokenOverride?: string): Promise<Party> => {
    const token = tokenOverride || getStoredToken();
    if (!token) throw new Error('No auth token');

    // Backend removió el shim /api/v1/auth/me. Usamos el endpoint canónico
    // /api/v1/me que retorna estructura nested { user, party, account, ... }.
    // Adaptamos a la forma flat que espera Party.fromApi.
    const response = await axios.get(`${config.apiUrl}/api/v1/me`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const data = response.data.data ?? response.data;
    const user = (data.user ?? {}) as Record<string, unknown>;
    const party = (data.party ?? {}) as Record<string, unknown>;

    return Party.fromApi({
      id: party.id ?? user.id,
      party_id: party.id,
      email: user.email,
      phone: user.phone ?? party.phone ?? '',
      // KYC name (party object) takes precedence over email-derived user.name.
      // Party.fromApi falls back to splitting full_name when first/last are absent.
      first_name: party.first_name ?? user.first_name,
      last_name: party.last_name ?? user.last_name,
      full_name: user.name ?? user.full_name,
      kyc_level: party.kyc_level,
      kyc_status: party.kyc_status,
      risk_level: party.risk_level,
      // Tipo de persona canónico del BE (pf | pfae | pm). El FE lo consume vía
      // useAccountPersonType() para gatear UI exclusiva de PM.
      person_type: party.person_type,
      status: user.status,
      created_at: user.created_at,
    });
  },

  /**
   * Disponibilidad de productos según los feature flags del BE (bloque
   * `features` de `/api/v1/me`). El FE gatea las acciones de dinero con esto
   * para no prometer movimientos que el rail PagaCel no procesa hoy.
   * GET /api/v1/me → data.features
   */
  meFeatures: async (): Promise<MeFeatures> => {
    const response = await apiClient.get('/api/v1/me');
    const data = response.data?.data ?? response.data;
    return (data?.features ?? {}) as MeFeatures;
  },

  /**
   * Estado real de la cuenta según el BE (bloque `account_provisioning` de
   * `/api/v1/me`), incluido el siguiente paso con su etiqueta y su ruta.
   *
   * `clientApi.me` aplana el payload a `Party.fromApi` y descarta este bloque,
   * por eso hace falta leerlo aparte — mismo patrón que `meFeatures`. Sin esto
   * el cintillo tenía que adivinar el estado a partir de `kycLevel` y `clabe`,
   * y adivinaba mal: anunciaba "la CLABE tarda de 10 min a 1 hora" a cuentas
   * que ni siquiera habían solicitado una.
   *
   * GET /api/v1/me → data.account_provisioning
   */
  meProvisioning: async (): Promise<AccountProvisioning | null> => {
    const response = await apiClient.get('/api/v1/me');
    const data = response.data?.data ?? response.data;
    return (data?.account_provisioning ?? null) as AccountProvisioning | null;
  },

  /**
   * Bloque `onboarding` de `/api/v1/me`: qué compuertas regulatorias faltan.
   *
   * Mismo motivo de existir que `meProvisioning` — `clientApi.me` aplana el
   * payload a `Party.fromApi` y tira el bloque. Se lee aparte porque el menú
   * de perfil necesita las banderas sueltas (`kyc_verified`,
   * `contracts_signed`), no sólo el `next_action` que consume el cintillo.
   *
   * GET /api/v1/me → data.onboarding
   */
  meOnboarding: async (): Promise<OnboardingState | null> => {
    const response = await apiClient.get('/api/v1/me');
    const data = response.data?.data ?? response.data;
    return (data?.onboarding ?? null) as OnboardingState | null;
  },

  updateProfile: async (data: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  }): Promise<Party> => {
    const response = await apiClient.patch('/api/v1/me', data);
    return Party.fromApi(response.data);
  },

  // --- Accounts ---
  accounts: async (): Promise<Account[]> => {
    const response = await apiClient.get('/api/v1/accounts');
    // El backend envuelve la lista en `{ data: { accounts: [...] } }`. Antes
    // se asumía un array pelado → `.map` tronaba y `useAccounts` quedaba
    // vacío. Desenvolvemos defensivamente cubriendo todas las formas:
    // `{data:{accounts:[]}}`, `{accounts:[]}`, `{data:[]}` y array pelado.
    const root = (response.data ?? {}) as Record<string, unknown>;
    const inner = (root.data ?? root) as Record<string, unknown>;
    const raw = inner.accounts ?? inner;
    const list = Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [];
    return list.map(Account.fromApi);
  },

  primaryAccount: async (): Promise<Account> => {
    const response = await apiClient.get('/api/v1/accounts/primary');
    const accountData = response.data?.data ?? response.data;
    return Account.fromApi(accountData);
  },

  /**
   * GET /api/v1/accounts/{id}/monthly-statement?period=YYYY-MM
   * Sin `period` el BE devuelve el mes en curso hasta hoy. 422 si el periodo
   * está mal formado o es futuro; 403 si la cuenta no es del usuario.
   */
  accountMonthlyStatement: async (
    accountId: string,
    period?: string
  ): Promise<MonthlyStatement> => {
    const response = await apiClient.get(`/api/v1/accounts/${accountId}/monthly-statement`, {
      params: period ? { period } : undefined,
    });
    return (response.data?.data ?? response.data) as MonthlyStatement;
  },

  // --- Transfers ---
  transfers: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
  }): Promise<{ transfers: Transfer[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/transfers',
      {
        params,
      }
    );
    return {
      transfers: response.data.data.map(Transfer.fromApi),
      meta: response.data.meta,
    };
  },

  transfer: async (id: string): Promise<Transfer> => {
    const response = await apiClient.get(`/api/v1/transfers/${id}`);
    return Transfer.fromApi(response.data);
  },

  createTransfer: async (data: {
    sender_account_id: string;
    receiver_account_id: string;
    contact_id?: string;
    amount_cents: number;
    currency_code: string;
    description?: string;
    reference?: string;
    latitude?: number;
    longitude?: number;
    metadata?: Record<string, unknown>;
  }): Promise<Transfer> => {
    const response = await apiClient.post('/api/v1/transfers', data);
    // Backend returns { success, data: { transaction_id, hold_id, status, ... } }
    // Without the original request fields (amount, recipient, etc.), so we
    // merge the response with the request payload to satisfy Transfer.fromApi.
    const body = response.data?.data ?? response.data ?? {};
    return Transfer.fromApi({
      id: body.transaction_id ?? body.id,
      transaction_id: body.transaction_id,
      status: body.status,
      amount_cents: data.amount_cents,
      currency: data.currency_code,
      concept: data.description,
      display_name: data.description,
      recipient_name: data.description ?? '',
      recipient_clabe: data.receiver_account_id,
      created_at: new Date().toISOString(),
      ...body,
    });
  },

  cancelTransfer: async (id: string): Promise<Transfer> => {
    const response = await apiClient.post(`/api/v1/transfers/${id}/cancel`, {});
    return Transfer.fromApi(response.data);
  },

  transferSummary: async (): Promise<{
    this_month_count: number;
    this_month_total_cents: number;
    pending_count: number;
    currency: string;
    period_start: string;
  }> => {
    const response = await apiClient.get('/api/v1/transfers/summary');
    return response.data?.data ?? response.data;
  },

  // --- Bulk disbursements (contract from the current BE serializers) ---
  batchTransfers: async (params?: {
    page?: number;
    per_page?: number;
  }): Promise<BatchTransferListPage> => {
    const response = await apiClient.get('/api/v1/batch-transfers', { params });
    const body = response.data?.data ?? response.data;
    if (!Array.isArray(body)) throw new Error('Invalid batch transfer response');
    return {
      items: body as BatchTransfer[],
      meta: response.data?.meta as PaginationMeta | undefined,
    };
  },

  createBatchTransfer: async (
    file: File,
    sourceAccountId: string,
    onProgress?: (progress: number) => void,
    idempotencyKey?: string
  ): Promise<BatchTransfer> => {
    const form = new FormData();
    form.append('file', file);
    form.append('source_account_id', sourceAccountId);
    const token = getStoredToken();
    const response = await directUploadClient.post('/api/v1/batch-transfers', form, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Idempotency-Key': idempotencyKey ?? crypto.randomUUID(),
      },
      onUploadProgress: event => {
        if (event.total && onProgress)
          onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
      },
    });
    const body = response.data?.data ?? response.data;
    return body as BatchTransfer;
  },

  batchTransfer: async (id: string): Promise<BatchTransfer> => {
    const response = await apiClient.get(`/api/v1/batch-transfers/${id}`);
    return (response.data?.data ?? response.data) as BatchTransfer;
  },

  processBatchTransfer: async (id: string, idempotencyKey?: string): Promise<unknown> => {
    const response = await apiClient.post(
      `/api/v1/batch-transfers/${id}/process`,
      {},
      {
        headers: { 'Idempotency-Key': idempotencyKey ?? crypto.randomUUID() },
      }
    );
    return response.data?.data ?? response.data;
  },

  retryFailedBatchTransfer: async (id: string, idempotencyKey?: string): Promise<unknown> => {
    const response = await apiClient.post(
      `/api/v1/batch-transfers/${id}/retry-failed`,
      {},
      {
        headers: { 'Idempotency-Key': idempotencyKey ?? crypto.randomUUID() },
      }
    );
    return response.data?.data ?? response.data;
  },

  batchTransferResults: async (
    id: string,
    params?: { page?: number; per_page?: number }
  ): Promise<BatchTransferResultsPage> => {
    const response = await apiClient.get(`/api/v1/batch-transfers/${id}/results`, { params });
    const body = response.data?.data ?? response.data;
    if (!Array.isArray(body)) throw new Error('Invalid batch transfer response');
    return {
      items: body as BatchTransferItem[],
      meta: response.data?.meta as PaginationMeta | undefined,
    };
  },

  downloadBatchTransferResults: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/api/v1/batch-transfers/${id}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // --- Beneficiaries ---
  beneficiaries: async (): Promise<Beneficiary[]> => {
    const response = await apiClient.get('/api/v1/beneficiaries');
    return (response.data as Record<string, unknown>[]).map(Beneficiary.fromApi);
  },

  createBeneficiary: async (data: {
    alias: string;
    name: string;
    clabe: string;
    email?: string;
  }): Promise<Beneficiary> => {
    const response = await apiClient.post('/api/v1/beneficiaries', data);
    return Beneficiary.fromApi(response.data);
  },

  deleteBeneficiary: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/beneficiaries/${id}`);
  },

  // --- Notices ---
  notices: async (): Promise<SystemNotice[]> => {
    const response = await apiClient.get('/api/v1/notices');
    return (response.data as Record<string, unknown>[]).map(SystemNotice.fromApi);
  },

  // --- Notifications ---
  notifications: async (params?: {
    read?: boolean;
    category?: string;
    page?: number;
    per_page?: number;
  }): Promise<{
    data: Array<{
      id: string;
      notification_type: string;
      category: string;
      title: string;
      body: string;
      read: boolean;
      priority: string;
      mandatory: boolean;
      read_at: string | null;
      created_at: string;
      action_url: string | null;
      channel: string;
      status: string;
      context_data: Record<string, unknown>;
      sent_at: string | null;
      expires_at: string | null;
    }>;
    meta: {
      current_page: number;
      total_pages: number;
      total_count: number;
      per_page: number;
      unread_count: number;
    };
  }> => {
    const response = await apiClient.get('/api/v1/notifications', { params });
    return {
      data: response.data.data,
      meta: response.data.meta,
    };
  },

  getNotification: async (
    id: string
  ): Promise<{
    id: string;
    notification_type: string;
    category: string;
    title: string;
    body: string;
    read: boolean;
    priority: string;
    mandatory: boolean;
    read_at: string | null;
    created_at: string;
    action_url: string | null;
    channel: string;
    status: string;
    context_data: Record<string, unknown>;
    sent_at: string | null;
    expires_at: string | null;
  }> => {
    const response = await apiClient.get(`/api/v1/notifications/${id}`);
    return response.data.data;
  },

  markNotificationAsRead: async (id: string): Promise<void> => {
    await apiClient.post(`/api/v1/notifications/${id}/read`, {});
  },

  markAllNotificationsAsRead: async (): Promise<{ marked_as_read: number; message: string }> => {
    const response = await apiClient.post('/api/v1/notifications/read-all', {});
    return response.data.data;
  },

  getNotificationPreferences: async (): Promise<{
    channels: Record<
      string,
      {
        enabled: boolean;
        categories: Record<string, boolean>;
      }
    >;
    categories: Record<
      string,
      {
        mandatory: boolean;
        default_channels: string[];
        description: string;
      }
    >;
    mandatory_categories: string[];
    optional_categories: string[];
  }> => {
    const response = await apiClient.get('/api/v1/notifications/preferences');
    return response.data.data;
  },

  updateNotificationPreferences: async (data: {
    channels?: Record<
      string,
      {
        enabled: boolean;
        categories?: Record<string, boolean>;
      }
    >;
  }): Promise<{
    channels: Record<
      string,
      {
        enabled: boolean;
        categories: Record<string, boolean>;
      }
    >;
    categories: Record<string, Record<string, unknown>>;
    mandatory_categories: string[];
    optional_categories: string[];
  }> => {
    const response = await apiClient.put('/api/v1/notifications/preferences', data);
    return response.data.data;
  },

  // --- Wallet ---
  walletBalance: async (): Promise<{
    available_cents: number;
    held_cents: number;
    total_cents: number;
    currency: string;
    account_id: string;
    clabe: string;
  }> => {
    const response = await apiClient.get('/api/v1/wallet/balance');
    return response.data;
  },

  walletSummary: async (): Promise<{
    this_month_inflow_cents: number;
    this_month_outflow_cents: number;
    this_month_movement_count: number;
    currency: string;
    period_start: string;
  }> => {
    const response = await apiClient.get('/api/v1/wallet/summary');
    return response.data?.data ?? response.data;
  },

  movements: async (params?: {
    page?: number;
    per_page?: number;
    start_date?: string;
    end_date?: string;
    movement_type?: 'cargo' | 'abono';
    direction?: 'debit' | 'credit';
    status?: string;
    entry_type?: string;
  }): Promise<{
    success: boolean;
    data: {
      movements: Array<{
        id: string;
        journal_entry_id: string;
        entry_number: string;
        entry_date: string;
        movement_type: 'cargo' | 'abono';
        direction: 'debit' | 'credit';
        amount_cents: number;
        amount: string;
        folio: string;
        status: string;
        status_label: string;
        description: string;
        reference: string | null;
        entry_type: string;
        created_at: string;
        fee_breakdown: FeeBreakdown | null;
        /**
         * Saldo contable de la cuenta tras el movimiento, sobre todo su
         * historial (filtros y paginación no lo cambian). RFC 0039.
         */
        balance_after_cents?: number | string | null;
        /** Fecha contable (publicación del asiento, o su creación si está pendiente). Orden del saldo. */
        occurred_at?: string | null;
        /** Cuenta de la fila: cada fila trae el saldo de SU cuenta. */
        account_id?: string | null;
        /** Moneda de esa cuenta. */
        currency_code?: string | null;
        /**
         * La otra parte del movimiento: beneficiario en un cargo, ordenante en
         * un abono. `null` en movimientos sin contraparte (aportaciones, inversiones).
         */
        counterparty?: {
          name: string | null;
          bank_name: string | null;
          clabe: string | null;
        } | null;
        /** Clave de rastreo SPEI. `null` si el movimiento no fue SPEI. */
        spei_tracking_key?: string | null;
        /**
         * Tipo de operación: 15 valores fijos del BE (`spei_in`, `spei_out`,
         * `internal_transfer`, `savings_contribution`, …, `other`). Va como string
         * porque un valor desconocido se trata como `other`.
         */
        operation_type?: string | null;
        /**
         * Referencia numérica SPEI (hasta 7 dígitos, del banco que envía). Sólo en
         * `spei_in`. `reference` es un id interno: no se muestra como referencia.
         */
        numeric_reference?: string | null;
      }>;
    };
    meta: {
      total: number;
      page: number;
      per_page: number;
    };
  }> => {
    const response = await apiClient.get('/api/v1/wallet/movements', { params });
    return response.data;
  },

  accountTransactions: async (
    accountId: string,
    params?: {
      page?: number;
      per_page?: number;
      direction?: 'debit' | 'credit';
      date_from?: string;
      date_to?: string;
      entry_type?: string;
    }
  ): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      journal_entry_id: string;
      entry_number: string;
      entry_date: string;
      movement_type: 'cargo' | 'abono';
      direction: 'debit' | 'credit';
      amount_cents: number;
      amount: string;
      folio: string;
      status: string;
      status_label: string;
      description: string;
      reference: string | null;
      entry_type: string;
      created_at: string;
      type?: 'income' | 'expense';
      date?: string;
      fee_breakdown?: FeeBreakdown | null;
      /**
       * Saldo contable de la cuenta tras el movimiento, sobre todo su
       * historial (filtros y paginación no lo cambian). RFC 0039.
       */
      balance_after_cents?: number | string | null;
      /** Fecha contable (publicación del asiento, o su creación si está pendiente). Orden del saldo. */
      occurred_at?: string | null;
      account_id?: string | null;
      currency_code?: string | null;
      /** Igual que en `movements`: la otra parte del movimiento, o `null`. */
      counterparty?: {
        name: string | null;
        bank_name: string | null;
        clabe: string | null;
      } | null;
      /** Clave de rastreo SPEI. `null` si el movimiento no fue SPEI. */
      spei_tracking_key?: string | null;
      /** Igual que en `movements`. */
      operation_type?: string | null;
      /** Igual que en `movements`. */
      numeric_reference?: string | null;
    }>;
    meta: {
      current_page: number;
      per_page: number;
      total_count: number;
      total_pages: number;
    };
  }> => {
    const response = await apiClient.get(`/api/v1/accounts/${accountId}/transactions`, { params });
    return response.data;
  },

  downloadWalletReport: async (params: {
    type: 'movements' | 'statements';
    start_date: string;
    end_date: string;
    format?: 'pdf' | 'csv' | 'xlsx';
  }): Promise<{
    success: boolean;
    message: string;
    download_url: string;
  }> => {
    const response = await apiClient.post('/api/v1/wallet/reports/download', params);
    // BE envuelve la respuesta en { success, data: { download_url }, message }
    // (mismo envelope que el resto de la API) — no un objeto plano.
    const body = (response.data ?? {}) as Record<string, unknown>;
    const inner = (body.data ?? {}) as Record<string, unknown>;
    return {
      success: (body.success as boolean) ?? true,
      message: (body.message as string) ?? '',
      download_url: (inner.download_url as string) ?? '',
    };
  },

  // --- Feature Flags ---
  featureFlags: async (): Promise<Record<string, boolean>> => {
    const response = await apiClient.get('/api/v1/feature_flags');
    return response.data;
  },

  // --- Catalogs ---
  getCatalogs: async (
    keys?: string[]
  ): Promise<{
    catalogs: Record<
      string,
      {
        searchable: boolean;
        grouped?: boolean;
        group_by?: string;
        items: {
          /**
           * Entero en los catálogos PM (`pm_funds_origins` y hermanos), string
           * en los demás. El store lo normaliza a string.
           */
          code: string | number;
          label: string;
          category?: string;
          description?: string;
          risk_level?: string;
          /** Sector SCIAN (scian_classes, scian_subsectors). */
          sector?: string;
          /** Subsector SCIAN (scian_classes). */
          subsector?: string;
        }[];
      }
    >;
    requested_keys: string[];
    version: string;
  }> => {
    const response = await onboardingClient.get('/api/v1/catalogs', {
      params: keys?.length ? { keys: keys.join(',') } : undefined,
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Autocompletado de dirección por código postal (catálogo SEPOMEX).
   * `GET /api/v1/catalogs/postal-codes/{cp}` — público, sin token.
   *
   * Notas del contrato (2026-08):
   * - `city` es `null` en ~64% de los CP del país: es el caso normal, no un
   *   error. El campo de ciudad debe quedar editable en el form.
   * - `state`/`state_code` vienen resueltos por el servidor con la
   *   nomenclatura de SEPOMEX ("México", "Coahuila de Zaragoza"). No armar
   *   mapa propio de estados en el cliente.
   * - El 404 es ambiguo: significa "no existe" o "el catálogo no está cargado
   *   en este ambiente". El mensaje al usuario debe ser neutro.
   */
  getPostalCode: async (cp: string): Promise<PostalCodeLookup> => {
    const response = await apiClient.get(`/api/v1/catalogs/postal-codes/${encodeURIComponent(cp)}`);
    return unwrapRelayEnvelope<PostalCodeLookup>(response.data);
  },

  // --- Registration ---
  register: async (data: {
    email: string;
    phone: string;
    password: string;
    location: {
      latitude: number;
      longitude: number;
    };
    personalData?: {
      nombres: string;
      apellidoPaterno: string;
      apellidoMaterno?: string;
    };
  }): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/api/v1/auth/register', {
      email: data.email,
      phone: data.phone,
      password: data.password,
      latitude: data.location.latitude,
      longitude: data.location.longitude,
      first_name: data.personalData?.nombres,
      last_name: data.personalData?.apellidoPaterno,
      mother_last_name: data.personalData?.apellidoMaterno,
    });
    const { token, refresh_token } = response.data;
    setStoredTokens(token, refresh_token);
    return response.data;
  },

  // --- Onboarding Session (Tankef Pay Service) ---
  /**
   * Creates a new onboarding session.
   * This endpoint should be called at the start of the registration flow.
   * @returns Session token, blocks status, and next step
   */
  createOnboardingSession: async (params?: {
    /** Discriminador de tipo de persona (RFC 0001). 'pm' = Persona Moral.
     * Si se omite, el BE asume PF (comportamiento previo). */
    kind?: 'pf' | 'pm' | 'pfae';
  }): Promise<{
    session_id: string;
    onboarding_token: string;
    flow_version: string;
    status: string;
    kind?: string;
    blocks: {
      registration: {
        status: string;
        order: number;
        label: string;
        steps: Record<string, unknown>;
      };
      kyc: { status: string; order: number; label: string; steps: Record<string, unknown> };
      profile: { status: string; order: number; label: string; steps: Record<string, unknown> };
      closing: { status: string; order: number; label: string; steps: Record<string, unknown> };
    };
    next_step: { block: string; step: string };
    expires_at: string;
  }> => {
    const response = await onboardingClient.post(
      '/api/v1/onboarding/sessions',
      params?.kind ? { kind: params.kind } : {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data.data;
    logger.debug('createOnboardingSession response', {
      sessionId: data.session_id,
      hasToken: Boolean(data.onboarding_token),
    });
    return data;
  },

  /**
   * Obtiene el estado de la sesión de onboarding por token — sin session_id en la URL.
   * Usar después de resume/confirm donde el token ya está en storage pero el flujo
   * canónico es GET /sessions/current (no POST /sessions).
   * GET /api/v1/onboarding/sessions/current
   */
  getOnboardingSessionCurrent: async (): Promise<{
    session_id: string;
    /** `pf` | `pm`. Discriminador explícito del flujo, junto a `flow_version`. */
    kind?: string;
    /** `pf_v3` | `pm_v1`. */
    flow_version?: string;
    status: string;
    progress_percentage: number;
    next_step: { block: string; step: string } | null;
    allowed_actions: string[];
    blocks: Record<string, { status: string; steps: Record<string, { status: string }> }>;
    session_meta: {
      expires_at: string;
      time_remaining_seconds: number;
    };
  }> => {
    const headers = getOnboardingAuthHeaders();
    const response = await onboardingClient.get('/api/v1/onboarding/sessions/current', { headers });
    return response.data.data;
  },

  /**
   * Obtiene el estado actual de la sesión de onboarding
   * GET /api/v1/onboarding/sessions/{session_id}
   */
  getOnboardingSession: async (): Promise<{
    session_id: string;
    kind?: string;
    flow_version?: string;
    status: string;
    progress_percentage: number;
    next_step: { block: string; step: string };
    allowed_actions: string[];
    blocks: Record<string, unknown>;
    session_meta: {
      expires_at: string;
      time_remaining_seconds: number;
    };
  }> => {
    const sessionId = getOnboardingSessionId();
    const headers = getOnboardingAuthHeaders();

    const response = await onboardingClient.get(`/api/v1/onboarding/sessions/${sessionId}`, {
      headers,
    });
    return response.data.data;
  },

  /**
   * Obtiene el estado de una sesión de onboarding específica (para recuperación)
   * GET /api/v1/onboarding/sessions/{session_id}
   * Usado cuando se recupera una sesión después de 409 Conflict o connection loss
   */
  getOnboardingSessionById: async (
    sessionId: string
  ): Promise<{
    session_id: string;
    status: string;
    current_step?: string;
    progress_percentage: number;
    next_step: { block: string; step: string };
    allowed_actions: string[];
    blocks: Record<string, unknown>;
    session_meta: {
      expires_at: string;
      time_remaining_seconds: number;
    };
  }> => {
    const headers = getOnboardingAuthHeaders();

    logger.debug('[getOnboardingSessionById] Recovering session:', sessionId);
    logger.debug('[getOnboardingSessionById] Headers:', headers);

    const response = await onboardingClient.get(`/api/v1/onboarding/sessions/${sessionId}`, {
      headers,
    });
    return response.data.data;
  },

  /**
   * Solicitar recuperación de sesión de onboarding por email
   * POST /api/v1/onboarding/sessions/resume/request
   * Sin autenticación - primer paso del flujo de recuperación
   * Envía OTP al email del usuario (respuesta neutral si email no existe)
   */
  requestSessionRecovery: async (data: {
    channel: 'email' | 'phone';
    value: string;
    tenant_id?: string;
  }): Promise<{
    message: string;
    challenge_id: string;
    channel: string;
    expires_in: number;
  }> => {
    const tenantId = data.tenant_id || config.tenantId;
    const payload = {
      channel: data.channel,
      value: data.value,
      tenant_id: tenantId,
    };

    logger.debug('[requestSessionRecovery] Requesting recovery', {
      channel: data.channel,
      value: data.value.substring(0, 5) + '***',
    });

    const response = await onboardingClient.post(
      '/api/v1/onboarding/sessions/resume/request',
      payload
    );
    return response.data.data;
  },

  /**
   * Confirmar recuperación de sesión con OTP
   * POST /api/v1/onboarding/sessions/resume/confirm
   * Sin autenticación - segundo paso del flujo de recuperación
   * Retorna access_token + session_id si OTP es válido
   */
  confirmSessionRecovery: async (data: {
    challenge_id: string;
    otp_code: string;
  }): Promise<{
    access_token: string;
    token_type: string;
    /** Vida real de `access_token` en segundos. */
    expires_in: number;
    /** Token de onboarding con la vida de la sesión (si el onboarding sigue en curso). */
    onboarding_token?: string | null;
    onboarding_token_expires_in?: number | null;
    onboarding_session_id: string;
    current_step: string;
    progress: number;
    completed_steps: string[];
    message: string;
    session_data: {
      email?: string;
      phone?: string;
      location?: string;
      document_type?: string;
    };
  }> => {
    logger.debug('[confirmSessionRecovery] Confirming recovery with OTP');

    const response = await onboardingClient.post('/api/v1/onboarding/sessions/resume/confirm', {
      challenge_id: data.challenge_id,
      otp_code: data.otp_code,
    });
    return response.data.data;
  },

  // ─── ONBOARDING WITH SESSION ID ─────────────────────────────────────────────
  // Pattern: POST /api/v1/onboarding/sessions/{session_id}/{step}
  // All these methods require a valid session created with createOnboardingSession

  /**
   * Registrar email en el onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/email
   */
  onboardingEmail: async (data: {
    email: string;
  }): Promise<{
    next_step: { block: string; step: string };
    verification_sent: boolean;
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('email'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Verificar código de email
   * POST /api/v1/onboarding/sessions/{session_id}/email/verify
   */
  onboardingEmailVerify: async (data: {
    code: string;
  }): Promise<{
    verified: boolean;
    next_step: { block: string; step: string };
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('email/verify'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Reenviar código de verificación de email
   * POST /api/v1/onboarding/sessions/{session_id}/email/resend
   */
  onboardingEmailResend: async (): Promise<{
    verification_sent: boolean;
    expires_at: string;
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl('email/resend'),
      {},
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Registrar teléfono en el onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/phone
   *
   * `verification` viene del BE (RFC 0021 §4): `channel` es el canal EFECTIVO
   * (si el SMS falló y salió por WhatsApp, aquí dice whatsapp y
   * `fell_back: true`), y `whatsapp_available_in_seconds` es la cuenta
   * regresiva servida por el servidor para habilitar el reenvío por WhatsApp —
   * el FE nunca hardcodea los 60s.
   */
  onboardingPhone: async (data: {
    phone: string;
    channel: 'sms' | 'whatsapp';
  }): Promise<{
    next_step: { block: string; step: string };
    verification_sent: boolean;
    verification?: PhoneOtpVerification;
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('phone'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Verificar código de teléfono
   * POST /api/v1/onboarding/sessions/{session_id}/phone/verify
   */
  onboardingPhoneVerify: async (data: {
    code: string;
  }): Promise<{
    verified: boolean;
    next_step: { block: string; step: string };
    step: string;
    status: string;
    auth_token?: string;
    user_id?: string;
    party_id?: string;
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('phone/verify'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Reenviar código de verificación de teléfono
   * POST /api/v1/onboarding/sessions/{session_id}/phone/resend
   */
  onboardingPhoneResend: async (data: {
    channel: 'sms' | 'whatsapp';
  }): Promise<{
    verification_sent: boolean;
    expires_at: string;
    verification?: PhoneOtpVerification;
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('phone/resend'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Establecer contraseña en el onboarding (último paso del bloque registration)
   * POST /api/v1/onboarding/sessions/{session_id}/password
   */
  onboardingPassword: async (data: {
    password: string;
    password_confirmation: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('password'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Iniciar proceso KYC con ubicación
   * POST /api/v1/onboarding/sessions/{session_id}/kyc/start
   */
  onboardingKycStart: async (data: {
    latitude: number;
    longitude: number;
    /** PM (RFC 0001): el KYC del representante legal reusa este endpoint con
     * subject_type='legal_rep'. Omitido → applicant (comportamiento PF previo). */
    subjectType?: 'applicant' | 'legal_rep';
  }): Promise<{
    step: string;
    status: string;
    next_step: { block: string; step: string };
    progress: number;
  }> => {
    const body: Record<string, unknown> = {
      latitude: data.latitude,
      longitude: data.longitude,
    };
    if (data.subjectType) {
      body.subject_type = data.subjectType;
    }
    const response = await onboardingClient.post(buildOnboardingUrl('kyc/start'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Subir documentos de identidad (INE frontal y trasera)
   * POST /api/v1/onboarding/sessions/{session_id}/kyc/documents
   * Usa directKycClient para evitar bug de MSW con FormData passthrough
   */
  onboardingKycDocuments: async (data: {
    documentFront: File;
    documentBack: File;
    documentType?: string;
  }): Promise<OnboardingKycDocumentsResult> => {
    const formData = new FormData();
    formData.append('document_front', data.documentFront);
    formData.append('document_back', data.documentBack);
    formData.append('document_type', data.documentType || 'INE');

    // Usar directKycClient para evitar que MSW pierda el body de FormData
    const response = await directKycClient.post(buildOnboardingUrl('kyc/documents'), formData, {
      headers: getOnboardingAuthHeaders(),
    });
    const body = response.data.data;

    // RFC 0028: con `kyc_async_capture` encendido el BE responde 202 y sigue
    // verificando en background, porque la llamada al proveedor no cabe en el
    // timeout del gateway (30 s medidos, con un caso de 84 s).
    //
    // El polling vive aquí y no en la UI a propósito: así cada pantalla que ya
    // llama a este método sigue viendo lo mismo —una promesa que resuelve con
    // el paso terminado, o que lanza si fue rechazado— y no hay dos contratos
    // distintos según el estado de un flag del servidor.
    if (response.status === 202 || body?.status === 'processing') {
      return pollKycCaptureUntilResolved({
        stepKey: body?.step,
        retryAfterMs: body?.retry_after_ms,
      });
    }

    return body;
  },

  /**
   * Subir selfie para verificación biométrica
   * POST /api/v1/onboarding/sessions/{session_id}/kyc/selfie
   * Usa directKycClient para evitar bug de MSW con FormData passthrough
   *
   * @param selfie - Imagen de selfie (requerido)
   * @param video - Video de liveness de Jaak Visage (opcional)
   */
  onboardingKycSelfie: async (data: {
    selfie: File;
    video?: File;
  }): Promise<{
    step: string;
    status: string;
    next_step: { block: string; step: string };
    progress: number;
  }> => {
    const formData = new FormData();
    formData.append('selfie_image', data.selfie); // Backend espera 'selfie_image'

    // Enviar video de liveness si está disponible (de Jaak Visage SDK)
    if (data.video) {
      formData.append('video', data.video);
    }

    // Usar directKycClient para evitar que MSW pierda el body de FormData
    const response = await directKycClient.post(buildOnboardingUrl('kyc/selfie'), formData, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Obtener datos extraídos del documento después del proceso KYC
   * GET /api/v1/onboarding/sessions/{session_id}/kyc/extracted-data
   */
  getOnboardingKycExtractedData: async (): Promise<{
    extracted_data: {
      curp: string;
      gender: string;
      address: {
        city: string;
        state: string;
        street: string;
        country: string;
        postal_code: string;
        neighborhood: string;
        exterior_number: string;
        interior_number?: string;
      };
      last_name: string;
      birth_date: string;
      first_name: string;
      nationality: string;
      maternal_name: string;
    };
    address_detected: boolean;
    document_verification: {
      verified: boolean;
      confidence: number;
      submitted_at: string;
      document_type: string;
      provider_reference: string;
      /**
       * Avisos del proveedor sobre el expediente (p.ej. `documentCompleteSides`
       * cuando se recibió el mismo lado dos veces). El BE los manda desde
       * siempre; el tipo no los declaraba y el FE era ciego a ellos.
       */
      warnings?: string[];
      review_status?: string;
      /** Estado de la extracción OCR. `documentCompleteSides: false` = falta una cara. */
      extraction_state?: {
        message?: string;
        ageValid?: boolean;
        ageValidMessage?: string;
        documentExpired?: boolean;
        documentCompleteSides?: boolean;
      };
      /** Validaciones del documento. Repite `documentCompleteSides`. */
      verification_state?: {
        message?: string;
        imageQuality?: boolean;
        documentValidity?: boolean;
        documentLiveness?: boolean;
        documentCompleteSides?: boolean;
      };
    };
  }> => {
    try {
      const sessionId = getOnboardingSessionId();
      const authHeaders = getOnboardingAuthHeaders();
      logger.debug('KYC extracted-data request', {
        sessionId,
        hasToken: !!authHeaders.Authorization,
      });
      const response = await onboardingClient.get(buildOnboardingUrl('kyc/extracted-data'), {
        headers: authHeaders,
      });
      return response.data.data;
    } catch (error) {
      logger.error('KYC extracted-data error', { error: String(error) });
      throw error;
    }
  },

  /**
   * Confirmar datos personales extraídos del KYC (validación de identidad)
   * POST /api/v1/onboarding/sessions/{session_id}/kyc/confirm-data
   */
  kycConfirmData: async (
    data: {
      first_name: string;
      middle_name?: string;
      paternal_surname: string;
      maternal_surname: string;
      birth_date: string;
      gender: string;
      curp: string;
      nationality: string;
      rfc?: string;
      passport_number?: string;
      document_type?: string;
      document_country?: string;
      address?: Record<string, unknown>;
      corrections?: Record<string, unknown>;
      person_type?: 'individual' | 'business';
    },
    opts: { idempotencyKey?: string; signal?: AbortSignal } = {}
  ): Promise<{
    next_step: { block: string; step: string };
  }> => {
    // A3: si el caller mantiene un Idempotency-Key estable entre retries,
    // lo pasa aquí y se envía al BE (que dedupe por esa key). Si no, el
    // interceptor de axios genera uno automáticamente — eso cubre el caso
    // de submit single-shot pero NO un retry React Query.
    const headers: Record<string, string> = { ...getOnboardingAuthHeaders() };
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    // Timeout extendido a 60s — confirm-data tarda más que el default (30s)
    // porque internamente el BE espera resoluciones de KYC/blacklist. Sin
    // este override, axios mataba la conexión antes y devolvía "No response
    // received" aunque el BE eventualmente terminara bien.
    const response = await onboardingClient.post(buildOnboardingUrl('kyc/confirm-data'), data, {
      headers,
      signal: opts.signal,
      timeout: 60000,
    });
    return response.data.data;
  },

  /**
   * Subir comprobante de domicilio
   * POST /api/v1/onboarding/sessions/{session_id}/kyc/proof-of-address
   *
   * Usa directKycClient (no onboardingClient) y NO fija Content-Type — mismo
   * bug que documents/selfie: onboardingClient fuerza `application/json` por
   * default y, si en su lugar se fija `multipart/form-data` a mano (sin
   * boundary), Axios no calcula el boundary real. El backend recibe el
   * FormData sin el campo `file` y responde 422.
   */
  proofOfAddressUpload: async (data: {
    file: File;
  }): Promise<{
    next_step: { block: string; step: string };
    upload_url: string;
    document_id: string;
  }> => {
    const formData = new FormData();
    formData.append('file', data.file);

    const response = await directKycClient.post(
      buildOnboardingUrl('kyc/proof-of-address'),
      formData,
      {
        headers: getOnboardingAuthHeaders(),
      }
    );
    return response.data.data;
  },

  /**
   * Subir constancia de situación fiscal (cuando el RFC no se extrajo del INE)
   * POST /api/v1/onboarding/sessions/{session_id}/fiscal-document
   * Usa directKycClient para evitar bug de MSW con FormData passthrough
   */
  /**
   * Enviar perfil transaccional
   * POST /api/v1/onboarding/sessions/{session_id}/transactional-profile
   */
  onboardingTransactionalProfile: async (data: {
    occupation_code: string;
    economic_activity: string;
    funds_source: string;
    funds_destination: string;
    monthly_amount_range: string;
    estimated_monthly_income: string;
    estimated_monthly_expenses: string;
    operation_frequency: string;
    clabe?: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl('transactional-profile'),
      data,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  onboardingFiscalDocument: async (data: {
    document: File;
    rfc?: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const formData = new FormData();
    formData.append('document', data.document);
    if (data.rfc) {
      formData.append('rfc', data.rfc);
    }

    const response = await directKycClient.post(buildOnboardingUrl('fiscal-document'), formData, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ONBOARDING PERSONA MORAL (PM) — Bloque empresa (F4, plan persona-moral)
  // Adapter layer: el FE usa nombres camelCase/plan; el wire usa los nombres
  // reales del BE (RFC 0001): razon_social, rfc, tax_regime_code, csf, etc.
  // Ver docs/plans/persona-moral/07-backend-contract-delta-2026-05-23.md.
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Subir un documento de la empresa (multipart). Cada tipo se sube por
   * separado.
   *
   * El `status` de esta respuesta es el del **ticket de revisión legal** —uno
   * por sesión, compartido por los ocho documentos—, NO el del step. El estado
   * de extracción por documento vive en `company/documents/extracted` y usa
   * otro vocabulario a propósito: son dos máquinas distintas y no se mezclan.
   * POST /api/v1/onboarding/sessions/{session_id}/company/documents
   */
  onboardingCompanyDocuments: async (data: {
    documentType: OnboardingCompanyDocumentType;
    file: File;
  }): Promise<{
    document_id: string;
    /** Ticket de revisión legal de la sesión; `null` si aún no se abrió. */
    ticket_id: string | null;
    status: 'pending_legal_review';
    estimated_completion_hours: number;
  }> => {
    const formData = new FormData();
    formData.append('document_type', data.documentType);
    formData.append('file', data.file);

    // directKycClient evita el bug de MSW perdiendo el body de FormData.
    const response = await directKycClient.post(buildOnboardingUrl('company/documents'), formData, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Guardar la estructura accionaria (multipart). **Reemplazo completo**: cada
   * envío sustituye la tabla entera, no hace merge — si el usuario borró una
   * fila y reenvía, esa fila desaparece.
   *
   * `shareholders` viaja como STRING JSON, no como objeto: es multipart.
   * No se manda `participation_percentage` (lo calcula el BE a partir de las
   * acciones; guardarlo lo desfasaría al primer ajuste) ni `signer_name` (lo
   * resuelve del usuario autenticado, que es el representante legal).
   * POST /api/v1/onboarding/sessions/{session_id}/company/shareholder-structure
   */
  onboardingCompanyShareholderStructure: async (data: {
    shareholders: Array<{ name: string; shares: number }>;
    /**
     * Total de acciones de la empresa. Opcional: sin él, el BE calcula los
     * porcentajes contra la suma de lo capturado, como antes. Con él, el
     * denominador es el capital declarado y una captura parcial levanta
     * hallazgo en la revisión legal (ONB_SHARE_005 si es cero,
     * ONB_SHARE_006 si lo capturado lo excede).
     */
    companyTotalShares?: number;
    /** PNG de la firma. Opcional; máx 2 MB (ONB_SHARE_004). */
    signature?: File;
  }): Promise<OnboardingShareholderStructure> => {
    const formData = new FormData();
    formData.append('shareholders', JSON.stringify(data.shareholders));
    if (data.companyTotalShares != null) {
      formData.append('company_total_shares', String(data.companyTotalShares));
    }
    if (data.signature) formData.append('signature', data.signature);

    // directKycClient evita el bug de MSW perdiendo el body de FormData.
    const response = await directKycClient.post(
      buildOnboardingUrl('company/shareholder-structure'),
      formData,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Leer la estructura accionaria capturada. La firma NO se devuelve ni se
   * expone su ruta en storage: sólo `signed`. Para cambiarla hay que volver a
   * firmar.
   * GET /api/v1/onboarding/sessions/{session_id}/company/shareholder-structure
   */
  getOnboardingCompanyShareholderStructure: async (): Promise<OnboardingShareholderStructure> => {
    const response = await onboardingClient.get(
      buildOnboardingUrl('company/shareholder-structure'),
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Guardar el organigrama (RFC 0024). JSON, sin firma — la del socio
   * mayoritario acompaña a la estructura accionaria, no a ésta.
   *
   * **Reemplazo completo.** El ORDEN del arreglo es el orden de dibujo, así que
   * no viaja como campo. Sustituye al PDF que el FE armaba con jspdf.
   * POST /api/v1/onboarding/sessions/{session_id}/company/org-structure
   */
  onboardingCompanyOrgStructure: async (data: {
    members: OnboardingOrgStructureMember[];
  }): Promise<OnboardingOrgStructure> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl('company/org-structure'),
      { members: data.members },
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Leer el organigrama capturado. `captured: false` cuando la empresa subió su
   * propio documento por `company/documents`: ese camino descarta la captura.
   * GET /api/v1/onboarding/sessions/{session_id}/company/org-structure
   */
  getOnboardingCompanyOrgStructure: async (): Promise<OnboardingOrgStructure> => {
    const response = await onboardingClient.get(buildOnboardingUrl('company/org-structure'), {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Guardar la identificación de los propietarios reales (RFC 0025, multipart).
   *
   * `owners` viaja como STRING JSON; `signer_name` y `signature` van SUELTOS en
   * el multipart, fuera del arreglo: la firma es una por pantalla, no por
   * propietario. Lo único obligatorio por propietario es `full_name`.
   *
   * ⚠️ Al cablear esto: **la firma hay que sacarla del `ref`**. En la pantalla
   * el `SignaturePad` es por ref y `handleSubmit` no lo lee, así que el trazo se
   * dibuja y se descarta. `signerName` sí está en el form (`majorityPartnerName`)
   * y llega solo; la firma no.
   * POST /api/v1/onboarding/sessions/{session_id}/company/beneficial-owner-identity
   */
  onboardingCompanyBeneficialOwnerIdentity: async (data: {
    owners: OnboardingBeneficialOwnerIdentity[];
    signerName?: string;
    /** PNG de la firma. Opcional; máx 2 MB (ONB_BOID_005). */
    signature?: File;
  }): Promise<OnboardingBeneficialOwnerIdentityResponse> => {
    const formData = new FormData();
    formData.append('owners', JSON.stringify(data.owners));
    if (data.signerName) formData.append('signer_name', data.signerName);
    if (data.signature) formData.append('signature', data.signature);

    // directKycClient evita el bug de MSW perdiendo el body de FormData.
    const response = await directKycClient.post(
      buildOnboardingUrl('company/beneficial-owner-identity'),
      formData,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Leer la identificación de propietarios reales capturada. Devuelve
   * `declared_ownership` por persona —quién ya pasó por la declaración de
   * beneficiarios— pero NO el porcentaje: ése es contrato de `beneficial-owners`.
   * GET /api/v1/onboarding/sessions/{session_id}/company/beneficial-owner-identity
   */
  getOnboardingCompanyBeneficialOwnerIdentity:
    async (): Promise<OnboardingBeneficialOwnerIdentityResponse> => {
      const response = await onboardingClient.get(
        buildOnboardingUrl('company/beneficial-owner-identity'),
        { headers: getOnboardingAuthHeaders() }
      );
      return response.data.data;
    },

  /**
   * Subir un documento del expediente de UN propietario real (multipart).
   *
   * Cuelga de la persona, no de la sesión: por eso lleva `ownerId` en la ruta.
   * Los `owner_id` los emite `company/beneficial-owner-identity`, que corre en
   * la pantalla `/onboarding/propietario-real`.
   *
   * ⚠️ **Borrado en cascada**: si la identificación se recaptura sin un
   * propietario, sus documentos se van con él. Correcto —un expediente sin
   * dueño no se revisa— pero la UI tiene que advertirlo antes de confirmar.
   * POST /api/v1/onboarding/sessions/{session_id}/company/beneficial-owners/{owner_id}/documents
   */
  onboardingBeneficialOwnerDocument: async (data: {
    ownerId: string;
    documentType: OnboardingBeneficialOwnerDocumentType;
    file: File;
  }): Promise<{ document_id: string; document_type: string }> => {
    const formData = new FormData();
    formData.append('document_type', data.documentType);
    formData.append('file', data.file);

    // directKycClient evita el bug de MSW perdiendo el body de FormData.
    const response = await directKycClient.post(
      buildOnboardingUrl(`company/beneficial-owners/${data.ownerId}/documents`),
      formData,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Expediente documental de TODOS los propietarios reales en una sola llamada
   * —lo subido, lo faltante y si están completos—, para pintar la pantalla sin
   * hacer N peticiones.
   * GET /api/v1/onboarding/sessions/{session_id}/company/beneficial-owners/documents
   */
  getOnboardingBeneficialOwnerDocuments: async (): Promise<{
    owners: OnboardingBeneficialOwnerDocuments[];
  }> => {
    const response = await onboardingClient.get(
      buildOnboardingUrl('company/beneficial-owners/documents'),
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Perfil transaccional de la PM. Requiere party PM, así que va DESPUÉS de
   * `company/data`.
   *
   * ⚠️ **Sin consumidor todavía, y eso BLOQUEA el alta.** El comando de alta de
   * la empresa aborta con `PM_MISSING_TX_PROFILE` —sin llamar al proveedor— si
   * `party.metadata["transactional_profile"]` está vacío, y este endpoint es el
   * único lugar que lo escribe. Hoy no se nota porque el alta va detrás de la
   * bandera `pagacel_company_registration`; encendida, truena en silencio al
   * final del flujo (backend, 2026-08-27).
   *
   * Desde el 2026-08-27 el monto y el conteo aceptan **cifra exacta O banda**
   * (`operation_amount_range`, `operation_count_range`), así que la pantalla ya
   * puede armar un payload válido con lo que captura hoy. OJO: mandar sólo
   * bandas cierra el step pero **no destraba el alta** — el expediente queda sin
   * monto ni conteo exactos y PagaCel vuelve a responder
   * `PM_MISSING_TX_PROFILE`. Elegir el valor representativo de una banda es
   * decisión de Compliance, no del backend ni del FE; ver
   * docs/qa/pm-inventario-pantallas-endpoints-2026-08-19.md.
   *
   * DINERO: montos en CENTAVOS enteros. El proveedor rechaza `Float`.
   * POST /api/v1/onboarding/sessions/{session_id}/company/transactional-profile
   */
  onboardingCompanyTransactionalProfile: async (data: {
    /**
     * Monto por transacción en CENTAVOS, tope 1_000_000_000 ($10M por
     * operación). Alterna con `operationAmountRange`: hay que mandar uno de los
     * dos o el BE responde ONB_TXPROFILE_001. Si van ambos, se guardan ambos.
     */
    estimatedAmountPerTransactionCents?: number;
    /** Banda de `pm_operation_amount_ranges` (1–4). Alterna con la cifra. */
    operationAmountRange?: number;
    /**
     * Conteo mensual de operaciones. Alterna con `operationCountRange`: uno de
     * los dos es obligatorio — ONB_TXPROFILE_002.
     */
    expectedMonthlyTransactionCount?: number;
    /** Banda de `pm_operation_count_ranges` (1–4). Alterna con el conteo. */
    operationCountRange?: number;
    /**
     * Catálogo `pm_transaction_frequencies`: 1 Diaria, 2 Semanal, 3 Quincenal,
     * 4 Mensual. El único rechazado es el `0` ("No asignado") —
     * ONB_TXPROFILE_003. Es el MISMO catálogo que sirve `GET /api/v1/catalogs`,
     * así que el código del `<select>` se reenvía tal cual, sin traducir
     * (confirmado con backend 2026-08-27; antes se documentaba aquí un
     * "1 baja / 2 media / 3 alta" del proveedor que no existe).
     */
    expectedTransactionFrequency: 1 | 2 | 3 | 4;
    /** Opcional: alimenta el scoring de riesgo interno, no viaja al proveedor. */
    estimatedMonthlyIncomeCents?: number;
    /** Banda de `pm_monthly_income_ranges` (1–4). Alterna con el ingreso. */
    monthlyIncomeRange?: number;
    /** Opcional, mismo caso que el ingreso. */
    estimatedMonthlyExpensesCents?: number;
  }): Promise<{
    step?: string;
    next_step?: { block: string; step: string };
    progress?: number;
  }> => {
    // Sólo se mandan las claves presentes: un `undefined` explícito viaja como
    // `null` en JSON y el BE lo lee como banda inválida (ONB_TXPROFILE_004) en
    // vez de como "mandé la otra forma de este concepto".
    const body: Record<string, number> = {
      expected_transaction_frequency: data.expectedTransactionFrequency,
    };
    const optional: Record<string, number | undefined> = {
      estimated_amount_per_transaction_cents: data.estimatedAmountPerTransactionCents,
      operation_amount_range: data.operationAmountRange,
      expected_monthly_transaction_count: data.expectedMonthlyTransactionCount,
      operation_count_range: data.operationCountRange,
      estimated_monthly_income_cents: data.estimatedMonthlyIncomeCents,
      monthly_income_range: data.monthlyIncomeRange,
      estimated_monthly_expenses_cents: data.estimatedMonthlyExpensesCents,
    };
    for (const [key, value] of Object.entries(optional)) {
      if (value !== undefined) body[key] = value;
    }

    const response = await onboardingClient.post(
      buildOnboardingUrl('company/transactional-profile'),
      body,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Obtener los datos extraídos por documento (NO un merge; cada sub-objeto
   * tiene su propio status). FE hace polling o escucha WS document_extracted.
   * GET /api/v1/onboarding/sessions/{session_id}/company/documents/extracted
   */
  getOnboardingCompanyDocumentsExtracted: async (): Promise<{
    csf?: { status: string; extracted_data: Record<string, unknown> | null; document_id?: string };
    acta_constitutiva?: {
      status: string;
      extracted_data: Record<string, unknown> | null;
      document_id?: string;
    };
    comprobante_domicilio_pm?: {
      status: string;
      extracted_data: Record<string, unknown> | null;
      document_id?: string;
    };
  }> => {
    const response = await onboardingClient.get(buildOnboardingUrl('company/documents/extracted'), {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Persistir datos de la empresa (razón social + RFC + régimen fiscal).
   * POST /api/v1/onboarding/sessions/{session_id}/company/data
   * Adapter: legalName→razon_social, rfcWithHomoclave→rfc, taxRegime→tax_regime_code,
   * scianCode→scian_code, economicActivity→economic_activity.
   *
   * El giro SCIAN viaja aquí porque en el orden del flujo la pantalla de
   * actividad económica va ANTES que ésta y la empresa (party PM) todavía no
   * existe: `company/scian` responde ONB_SESSION_004 hasta que company/data
   * corre. El BE lo registra en la misma llamada (FE_BE_ALIGNMENT_PM §3.1/§3.9).
   *
   * RÉGIMEN FISCAL: desde el 2026-08-27 el BE lo VALIDA cuando viene (antes
   * escribía lo que llegara) y responde ONB_COMPANY_002 con un código de
   * persona física. El campo no se pinta —lo llena el OCR de la CSF—, así que
   * un código mal leído sería un error sin campo donde corregirlo. Como el
   * contrato lo acepta ausente, se OMITE cuando no está en `PM_TAX_REGIMES`.
   */
  onboardingCompanyData: async (data: {
    legalName: string;
    rfcWithHomoclave: string;
    taxRegime?: string;
    scianCode?: string;
    economicActivity?: string;
    incorporationDate?: string;
    registrationState?: string;
    cifId?: string;
    fielSerial?: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const body: Record<string, string> = {
      razon_social: data.legalName,
      rfc: data.rfcWithHomoclave,
    };
    const taxRegime = data.taxRegime?.trim();
    if (isPmTaxRegime(taxRegime)) {
      body.tax_regime_code = taxRegime as string;
    }
    if (data.scianCode && data.economicActivity) {
      body.scian_code = data.scianCode;
      body.economic_activity = data.economicActivity;
    }
    // Datos de constitución/fiscales de la misma pantalla. Antes eran
    // "UI-only" y el expediente del backoffice los mostraba vacíos aunque
    // el usuario los hubiera tecleado. Opcionales: el BE viejo los ignora.
    if (data.incorporationDate) {
      body.incorporation_date = data.incorporationDate;
    }
    if (data.registrationState) {
      body.registration_state = data.registrationState;
    }
    if (data.cifId) {
      body.cif_id = data.cifId;
    }
    if (data.fielSerial) {
      body.fiel_serial = data.fielSerial;
    }
    const response = await onboardingClient.post(buildOnboardingUrl('company/data'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Persistir el domicilio de la empresa (valida + guarda en una sola call;
   * no hay lookup/verify separados — ver 07-delta).
   * POST /api/v1/onboarding/sessions/{session_id}/company/address
   * Nota: el campo es `country` (sin _code); neighborhood + municipality required.
   */
  onboardingCompanyAddress: async (data: {
    street: string;
    exteriorNumber: string;
    interiorNumber?: string;
    neighborhood: string;
    municipality: string;
    state: string;
    postalCode: string;
    country: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const body: Record<string, string> = {
      street: data.street,
      exterior_number: data.exteriorNumber,
      neighborhood: data.neighborhood,
      municipality: data.municipality,
      state: data.state,
      postal_code: data.postalCode,
      country: data.country,
    };
    if (data.interiorNumber) {
      body.interior_number = data.interiorNumber;
    }
    const response = await onboardingClient.post(buildOnboardingUrl('company/address'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Persistir la actividad económica SCIAN de la empresa (GAP 1, contrato #412).
   * Cierra el bloque company_data. Required: scian_code + economic_activity.
   * POST /api/v1/onboarding/sessions/{session_id}/company/scian
   * Adapter: scianCode→scian_code, economicActivity→economic_activity.
   */
  onboardingCompanyScian: async (data: {
    scianCode: string;
    economicActivity: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const body: Record<string, string> = {
      scian_code: data.scianCode,
      economic_activity: data.economicActivity,
    };
    const response = await onboardingClient.post(buildOnboardingUrl('company/scian'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Persistir los datos del acta constitutiva (bloque kyb_validation). Se
   * captura tras subir el acta, pre-llenado con lo que devuelva el OCR.
   * POST /api/v1/onboarding/sessions/{session_id}/company/acta-data
   *
   * Precondición del contrato: `company/data` ya debe haber corrido (la sesión
   * necesita party PM) o el BE responde `ONB_SESSION_004`.
   *
   * La respuesta trae **`power_of_attorney_required`**, que es la señal
   * autoritativa para mostrar u omitir la pantalla del poder notarial — NO el
   * `power_in_articles` que mandó el usuario. Reenviar con datos corregidos es
   * idempotente.
   *
   * Adapter: escrituraNumber→escritura_number, notaryName→notary_name,
   * notaryNumber→notary_number, constitutionDate→constitution_date (ya en
   * `YYYY-MM-DD`; el form la convierte desde `DD/MM/YYYY`),
   * legalRepName→legal_rep_name, powerInArticles→power_in_articles.
   */
  onboardingCompanyActaData: async (data: {
    escrituraNumber: string;
    notaryName: string;
    notaryNumber: string;
    /** ISO `YYYY-MM-DD`, no futura. */
    constitutionDate: string;
    legalRepName: string;
    powerInArticles: boolean;
  }): Promise<{
    step: string;
    constitution_date: string;
    power_in_articles: boolean;
    power_of_attorney_required: boolean;
    next_step: { block: string; step: string };
    progress?: number;
  }> => {
    const body = {
      escritura_number: data.escrituraNumber,
      notary_name: data.notaryName,
      notary_number: data.notaryNumber,
      constitution_date: data.constitutionDate,
      legal_rep_name: data.legalRepName,
      power_in_articles: data.powerInArticles,
    };
    const response = await onboardingClient.post(buildOnboardingUrl('company/acta-data'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Declarar el origen de fondos de la empresa (GAP 2, contrato #412). Va tras
   * beneficial_owners. La ruta NO lleva `/company/`.
   * POST /api/v1/onboarding/sessions/{session_id}/origin-of-funds
   *
   * DOS VOCABULARIOS QUE NO SE TRADUCEN ENTRE SÍ. Hay que mandar uno:
   * - `source`: los 8 slugs de texto de `MORAL_FUNDS_SOURCES` (legado).
   * - `source_code`: los 20 códigos de `pm_funds_origins` (0 rechazado). Es el
   *   que viaja al proveedor, y el que el backend pide para capturas nuevas.
   *
   * El backend NO infiere la correspondencia: cada valor se guarda en su propia
   * columna y la otra queda nula. El código `19` ("Otro origen, especificar")
   * pide además el texto libre, que va en `description`.
   *
   * Adapter: el monto YA viene en centavos (entero) — el form lo convirtió;
   * aquí solo se mapea a snake_case (estimatedMonthlyVolumeCents→
   * estimated_monthly_volume_cents), tope 100_000_000_000 ($1,000M al mes).
   * Errores tipados: ONB_FUNDS_001 (origen inválido/ausente/0), ONB_FUNDS_002.
   */
  onboardingOriginOfFunds: async (data: {
    /** Slug legado. Alterna con `sourceCode`. */
    source?: string;
    /** Código de `pm_funds_origins` (1–19). Alterna con `source`. */
    sourceCode?: number;
    estimatedMonthlyVolumeCents: number;
    /** Descripción libre, opcional en el contrato: sólo se manda si trae texto. */
    description?: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const body: Record<string, string | number> = {
      estimated_monthly_volume_cents: data.estimatedMonthlyVolumeCents,
    };
    if (data.source) {
      body.source = data.source;
    }
    if (data.sourceCode !== undefined) {
      body.source_code = data.sourceCode;
    }
    if (data.description) {
      body.description = data.description;
    }
    const response = await onboardingClient.post(buildOnboardingUrl('origin-of-funds'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Declaración PEP session-scoped (GAP 6, contrato #412). El método legacy
   * `pepDeclaration` pega a `/api/v1/onboarding/pep-declaration` SIN session id
   * (mock viejo); el contrato real del onboarding moderno es session-scoped.
   * PATCH /api/v1/onboarding/sessions/{session_id}/pep-declaration
   * Adapter: isPep→is_pep, pepPosition→pep_position, pepRelationship→pep_relationship.
   */
  onboardingPepDeclaration: async (data: {
    isPep: boolean;
    pepPosition?: string;
    pepRelationship?: string;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const body = {
      is_pep: data.isPep,
      pep_position: data.pepPosition ?? '',
      pep_relationship: data.pepRelationship ?? '',
    };
    const response = await onboardingClient.patch(buildOnboardingUrl('pep-declaration'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Aceptación del aviso de privacidad session-scoped (GAP 5a, contrato #412).
   * Bloque `closing`, step `privacy_notice_acceptance`, antes de la firma. Reusa
   * el endpoint compartido `consent` (params reales: truthful_declaration +
   * data_sharing_consent — verificado en sessions_controller#accept_consent).
   * POST /api/v1/onboarding/sessions/{session_id}/consent
   */
  onboardingPrivacyConsent: async (data: {
    truthfulDeclaration: boolean;
    dataSharingConsent: boolean;
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const body = {
      truthful_declaration: data.truthfulDeclaration,
      data_sharing_consent: data.dataSharingConsent,
    };
    const response = await onboardingClient.post(buildOnboardingUrl('consent'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ONBOARDING PM — Representantes legales + Beneficiarios controladores +
  // reuso de identidad (F5, plan persona-moral). Adapter camelCase → snake_case.
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Declarar los representantes legales (array + política de firma).
   * POST /api/v1/onboarding/sessions/{session_id}/legal-representatives
   * Nota BE: usa `last_name` único (no paterno/materno separados).
   */
  onboardingLegalRepresentatives: async (data: {
    signaturePolicy: 'solo' | 'mancomunado' | 'solidario';
    representatives: Array<{
      userIdExisting?: string | null;
      firstName: string;
      /** El BE usa `last_name` único: se arma con paterno + materno. */
      lastNamePaternal: string;
      lastNameMaternal?: string;
      /** Identidad del representante que hace el onboarding (RFC 0030 §13.1). */
      secondName?: string;
      /** `DD/MM/AAAA` o ISO; el BE acepta ambos. */
      birthDate?: string;
      curp?: string;
      rfc?: string;
      nationality?: string;
      /**
       * Correo y tipo de poder ya no se capturan en pantalla (diseño
       * 2026-08-03). Se mandan sólo si vienen; el BE aún los declara required,
       * así que hasta que actualice el contrato responderá 422.
       */
      email?: string;
      powerType?: 'administracion' | 'dominio' | 'pleitos_y_cobranzas' | 'actos_especiales';
      /** Opcional: el BE usa la fecha actual cuando no se envía. */
      powerGrantedAt?: string | null;
      powerExpiresAt?: string | null;
    }>;
  }): Promise<{ next_step: { block: string; step: string } }> => {
    const body = {
      signature_policy: data.signaturePolicy,
      representatives: data.representatives.map(r => ({
        user_id_existing: r.userIdExisting ?? null,
        first_name: r.firstName,
        last_name: [r.lastNamePaternal, r.lastNameMaternal].filter(Boolean).join(' '),
        // RFC 0030 §13.1: identidad del representante. Los apellidos van también
        // separados para que el BE no tenga que partir `last_name`.
        paternal_surname: r.lastNamePaternal,
        ...(r.lastNameMaternal ? { maternal_surname: r.lastNameMaternal } : {}),
        ...(r.secondName ? { second_name: r.secondName } : {}),
        ...(r.birthDate ? { birth_date: r.birthDate } : {}),
        ...(r.curp ? { curp: r.curp } : {}),
        ...(r.rfc ? { rfc: r.rfc } : {}),
        ...(r.nationality ? { nationality: r.nationality } : {}),
        ...(r.email ? { email: r.email } : {}),
        ...(r.powerType ? { power_type: r.powerType } : {}),
        power_granted_at: r.powerGrantedAt ?? null,
        power_expires_at: r.powerExpiresAt ?? null,
      })),
    };
    const response = await onboardingClient.post(
      buildOnboardingUrl('legal-representatives'),
      body,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Declarar beneficiarios controladores (LFPIORPI Art. 17, obligatorio PM).
   * POST /api/v1/onboarding/sessions/{session_id}/beneficial-owners
   */
  onboardingBeneficialOwners: async (data: {
    owners: Array<{
      subjectType: 'pf' | 'pm';
      existingSubjectId?: string | null;
      name: string;
      rfcOrCurp: string;
      ownershipPercentage: number;
      /** Valores del `CONTROL_TYPE_MAP` del BE: cualquier otro se guarda como
       *  `direct_ownership` sin error. */
      controlType:
        | 'equity'
        | 'direct_ownership'
        | 'indirect_ownership'
        | 'voting'
        | 'voting_rights'
        | 'contract'
        | 'control_by_other_means';
      relationship?: string;
    }>;
  }): Promise<{ next_step: { block: string; step: string } }> => {
    const body = {
      // La clave del body es `owners` (no `beneficial_owners`, que es el nombre
      // del recurso en la URL): el controller hace `params.require(:owners)` y
      // con la otra respondía SYS-001 / ParameterMissing.
      owners: data.owners.map(o => ({
        subject_type: o.subjectType,
        existing_subject_id: o.existingSubjectId ?? null,
        name: o.name,
        rfc_or_curp: o.rfcOrCurp,
        ownership_percentage: o.ownershipPercentage,
        control_type: o.controlType,
        relationship: o.relationship,
      })),
    };
    const response = await onboardingClient.post(buildOnboardingUrl('beneficial-owners'), body, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Obtener los beneficiarios controladores declarados.
   * GET /api/v1/onboarding/sessions/{session_id}/beneficial-owners
   */
  getOnboardingBeneficialOwners: async (): Promise<{
    beneficial_owners: Array<Record<string, unknown>>;
  }> => {
    const response = await onboardingClient.get(buildOnboardingUrl('beneficial-owners'), {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Reusar el KYC de una sesión PF aprobada del mismo tenant (skip captura live).
   * POST /api/v1/onboarding/sessions/{session_id}/attach-kyc-from-existing
   *
   * ⚠️ El body exacto NO está especificado en los docs del plan (solo "requires
   * consent capture"). Implementado con el shape más probable — CONFIRMAR contra
   * tankef-pay-service/swagger/v1/rfc0001_pm_paths.yaml antes de cablear la UI.
   */
  onboardingAttachKycFromExisting: async (data: {
    consentAccepted: boolean;
    sourceSessionId?: string;
  }): Promise<{ next_step: { block: string; step: string } }> => {
    const body: Record<string, unknown> = { consent_accepted: data.consentAccepted };
    if (data.sourceSessionId) {
      body.source_session_id = data.sourceSessionId;
    }
    const response = await onboardingClient.post(
      buildOnboardingUrl('attach-kyc-from-existing'),
      body,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ONBOARDING PM — Multi-user (F7, plan persona-moral). A diferencia del resto
  // de PM, estos NO son endpoints de sesión de onboarding: usan `apiClient`
  // (auth normal con access_token) + header `X-Account-Id` para scoping
  // multi-tenant (ver 08-security). Roles: administrator | authorizer | operator.
  //
  // ⚠️ Los bodies exactos de invitation/accept no están detallados en los docs
  // del plan — implementados con el shape más probable; CONFIRMAR contra el
  // swagger del BE (rfc0001_pm_paths.yaml) antes de cablear la UI.
  // ──────────────────────────────────────────────────────────────────────────

  /** Invitar un usuario a la cuenta. POST /api/v1/accounts/{aid}/invitations */
  createAccountInvitation: async (
    accountId: string,
    data: { email: string; role: 'administrator' | 'authorizer' | 'operator' }
  ): Promise<Record<string, unknown>> => {
    const response = await apiClient.post(
      `/api/v1/accounts/${accountId}/invitations`,
      { email: data.email, role: data.role },
      { headers: { 'X-Account-Id': accountId } }
    );
    return response.data.data;
  },

  /** Listar usuarios de la cuenta. GET /api/v1/accounts/{aid}/users */
  getAccountUsers: async (
    accountId: string
  ): Promise<{ users: Array<Record<string, unknown>> }> => {
    const response = await apiClient.get(`/api/v1/accounts/${accountId}/users`, {
      headers: { 'X-Account-Id': accountId },
    });
    return response.data.data;
  },

  /** Cambiar el rol de un usuario. PATCH /api/v1/accounts/{aid}/users/{uid} */
  updateAccountUserRole: async (
    accountId: string,
    userId: string,
    role: 'administrator' | 'authorizer' | 'operator'
  ): Promise<Record<string, unknown>> => {
    const response = await apiClient.patch(
      `/api/v1/accounts/${accountId}/users/${userId}`,
      { role },
      { headers: { 'X-Account-Id': accountId } }
    );
    return response.data.data;
  },

  /** Remover un usuario de la cuenta. DELETE /api/v1/accounts/{aid}/users/{uid} */
  removeAccountUser: async (accountId: string, userId: string, reason?: string): Promise<void> => {
    await apiClient.delete(`/api/v1/accounts/${accountId}/users/${userId}`, {
      headers: { 'X-Account-Id': accountId },
      params: reason ? { reason } : undefined,
    });
  },

  /**
   * Aceptar una invitación (lado invitee). Token single-use, rate-limited.
   * POST /api/v1/invitations/{token}/accept — sin X-Account-Id (el invitee aún
   * no pertenece a la cuenta).
   */
  acceptInvitation: async (token: string): Promise<Record<string, unknown>> => {
    const response = await apiClient.post(`/api/v1/invitations/${token}/accept`, {});
    return response.data.data;
  },

  /**
   * Confirmar beneficiario del onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/beneficiaries/confirm
   */
  onboardingBeneficiaryConfirm: async (data: {
    first_name: string;
    second_name?: string;
    paternal_surname: string;
    maternal_surname?: string;
    date_of_birth: string;
    phone: string;
    relationship: string;
    percentage: number;
    address?: {
      postal_code?: string;
      country?: string;
      city?: string;
      state?: string;
      municipality?: string;
      neighborhood?: string;
      street?: string;
      exterior_number?: string;
      interior_number?: string;
    };
  }): Promise<{
    next_step: { block: string; step: string };
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl('beneficiaries/confirm'),
      data,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Obtener contratos del onboarding para firma
   * GET /api/v1/onboarding/sessions/{session_id}/contracts
   */
  onboardingContracts: async (): Promise<{
    contracts: {
      type: string;
      title: string;
      version: string;
      required: boolean;
      status: string;
      preview_available: boolean;
    }[];
    signature_channels: string[];
    all_required_accepted: boolean;
  }> => {
    const response = await onboardingClient.get(buildOnboardingUrl('contracts'), {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Obtener preview de un contrato del onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/contracts/preview
   */
  onboardingContractPreview: async (
    contractType: string
  ): Promise<{
    contract_type: string;
    version: string;
    preview_url: string;
    checksum: string;
    generated_at: string;
    expires_in: number;
    personalized: boolean;
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl('contracts/preview'),
      { contract_type: contractType },
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Obtener contenido HTML renderizado de un contrato
   * GET /api/v1/onboarding/sessions/{session_id}/contracts/content/{contract_type}
   *
   * Envía content_hash opcional para soportar cache del lado del servidor:
   * cuando el hash coincide con la versión actual el backend retorna
   * { cached: true, content: null } y el frontend reutiliza la versión guardada.
   */
  onboardingContractContent: async (
    contractType: string,
    contentHash?: string
  ): Promise<{
    contract_type: string;
    title: string;
    version: string;
    content: string | null;
    content_hash: string;
    cached: boolean;
    format?: string;
  }> => {
    const params = contentHash ? { content_hash: contentHash } : undefined;
    const response = await onboardingClient.get(
      buildOnboardingUrl(`contracts/content/${contractType}`),
      { headers: getOnboardingAuthHeaders(), params }
    );
    return response.data.data;
  },

  /**
   * Firmar contratos del onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/contracts/sign
   */
  onboardingContractsSign: async (data: {
    channel: 'sms' | 'whatsapp';
    device_fingerprint: string;
    contracts: { type: string; accepted: boolean }[];
  }): Promise<{
    signing_session_id: string;
    channel: string;
    contracts_count: number;
    message: string;
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('contracts/sign'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Verificar firma de contratos del onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/contracts/verify
   */
  onboardingContractsVerify: async (data: {
    signing_session_id: string;
    code: string;
    device_fingerprint: string;
  }) => {
    const response = await onboardingClient.post(buildOnboardingUrl('contracts/verify'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Iniciar firma electrónica avanzada de contratos
   * POST /api/v1/onboarding/sessions/{session_id}/contracts/esign
   */
  onboardingContractsEsign: async (data: Record<string, unknown>) => {
    const response = await onboardingClient.post(buildOnboardingUrl('contracts/esign'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Obtener estado de firma electrónica
   * GET /api/v1/onboarding/sessions/{session_id}/contracts/esign-status
   */
  onboardingContractsEsignStatus: async () => {
    const response = await onboardingClient.get(buildOnboardingUrl('contracts/esign-status'), {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Cancelar firma electrónica en curso
   * PUT /api/v1/onboarding/sessions/{session_id}/contracts/esign-cancel
   */
  onboardingContractsEsignCancel: async (data: Record<string, unknown>) => {
    const response = await onboardingClient.put(
      buildOnboardingUrl('contracts/esign-cancel'),
      data,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data.data;
  },

  /**
   * Pausa la sesión de onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/pause
   */
  pauseOnboardingSession: async (data: {
    reason: 'will_continue_later' | 'technical_issue' | 'need_documents';
  }): Promise<{
    step: string;
    status: string;
    blocks: Record<string, unknown>;
    next_step: {
      block: string;
      step: string;
      endpoint: string;
      form_schema: Record<string, unknown>;
    };
    progress: number;
  }> => {
    const response = await onboardingClient.post(buildOnboardingUrl('pause'), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * Reanuda la sesión de onboarding
   * POST /api/v1/onboarding/sessions/{session_id}/resume
   */
  resumeOnboardingSession: async (): Promise<{
    step: string;
    status: string;
    blocks: Record<string, unknown>;
    next_step: {
      block: string;
      step: string;
      endpoint: string;
      form_schema: Record<string, unknown>;
    };
    progress: number;
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl('resume'),
      {},
      {
        headers: getOnboardingAuthHeaders(),
      }
    );
    return response.data.data;
  },

  /**
   * Método genérico para enviar datos a cualquier step del onboarding
   * Útil para nuevos endpoints que sigan el mismo patrón
   * POST /api/v1/onboarding/sessions/{session_id}/{step}
   */
  onboardingStep: async <TData, TResponse>(step: string, data: TData): Promise<TResponse> => {
    const response = await onboardingClient.post(buildOnboardingUrl(step), data, {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  // ─── LEGACY ONBOARDING (mantener por compatibilidad) ────────────────────────

  // --- Onboarding Registration (Tankef Pay Service) ---
  onboardingRegister: async (data: {
    email: string;
    password: string;
    phone: string;
    tenant_id: string;
    terms_accepted: boolean;
    privacy_accepted: boolean;
    device_fingerprint: string;
  }): Promise<{ onboarding_token: string }> => {
    const response = await onboardingClient.post('/api/v1/onboarding/register', data);
    return response.data.data;
  },

  onboardingResendCode: async (
    onboardingToken: string
  ): Promise<{
    success: boolean;
    data?: {
      verification_code_id: string;
      expires_at: string;
      channel: string;
      destination_masked: string;
    };
  }> => {
    const response = await onboardingClient.post(
      '/api/v1/onboarding/resend-code',
      {},
      {
        headers: {
          Authorization: `Bearer ${onboardingToken}`,
        },
      }
    );
    return response.data;
  },

  onboardingVerifyPhone: async (
    onboardingToken: string,
    code: string
  ): Promise<{ success: boolean }> => {
    const response = await onboardingClient.post(
      '/api/v1/onboarding/verify-phone',
      { code },
      {
        headers: {
          Authorization: `Bearer ${onboardingToken}`,
        },
      }
    );
    return response.data;
  },

  // --- Email Verification ---
  sendEmailVerification: async (email: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/api/v1/verification/email/send', { email });
    return response.data;
  },

  verifyEmail: async (
    email: string,
    code: string
  ): Promise<{ success: boolean; verified: boolean }> => {
    const response = await apiClient.post('/api/v1/verification/email/verify', { email, code });
    return response.data;
  },

  // --- Phone Verification ---
  sendPhoneVerification: async (phone: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/api/v1/verification/phone/send', { phone });
    return response.data;
  },

  verifyPhone: async (
    phone: string,
    code: string
  ): Promise<{ success: boolean; verified: boolean }> => {
    const response = await apiClient.post('/api/v1/verification/phone/verify', { phone, code });
    return response.data;
  },

  // --- Onboarding  Step 3 ---
  personalInfo: async (data: {
    first_name: string;
    paternal_surname: string;
    maternal_surname?: string;
    date_of_birth: string;
    gender: 'M' | 'F' | 'O';
  }): Promise<{
    current_step: string;
    progress: number;
    ui_step: number;
  }> => {
    const response = await onboardingClient.patch(`/api/v1/onboarding/personal-info`, data);
    return response.data;
  },

  // --- Onboarding  Step 4 ---
  address: async (data: {
    postal_code: string;
    state: string;
    city: string;
    municipality: string;
    neighborhood: string;
    street: string;
    exterior_number: string;
    interior_number: string;
  }): Promise<{
    current_step: string;
    progress: number;
    ui_step: number;
  }> => {
    const response = await onboardingClient.patch(`/api/v1/onboarding/address`, data);
    return response.data;
  },

  // --- Onboarding  Step 5 ---
  occupation: async (data: {
    occupation_code: string;
    occupation_description: string;
    source_of_funds: string;
    purpose_of_funds: string;
  }): Promise<{
    current_step: string;
    progress: number;
    ui_step: number;
  }> => {
    const response = await onboardingClient.patch(`/api/v1/onboarding/occupation`, data);
    return response.data;
  },

  // --- Onboarding  Step 6 ---
  transactionalProfile: async (data: {
    estimated_monthly_income: number;
    estimated_monthly_expenses: number;
    expected_transaction_frequency: string;
  }): Promise<{
    current_step: string;
    progress: number;
    ui_step: number;
  }> => {
    const response = await onboardingClient.patch(`/api/v1/onboarding/address`, data);
    return response.data;
  },

  // --- Onboarding  Step 7 ---
  pepDeclaration: async (data: {
    is_pep: boolean;
    pep_position: string;
    pep_relationship: string;
  }): Promise<{
    current_step: string;
    progress: number;
    ui_step: number;
  }> => {
    const response = await onboardingClient.patch(`/api/v1/onboarding/pep-declaration`, data);
    return response.data;
  },

  // --- Onboarding  Step 8 ---
  consent: async (data: {
    truthful_declaration: boolean;
    data_sharing_consent: boolean;
  }): Promise<{
    current_step: string;
    progress: number;
    ui_step: number;
  }> => {
    const response = await onboardingClient.post(`/api/v1/onboarding/consent`, data);
    return response.data;
  },

  // --- Onboarding  Contracts ---
  getContracts: async (): Promise<{
    contracts: [
      {
        type: string;
        title: string;
        version: string;
        required: boolean;
        status: string;
        preview_available: boolean;
      },
    ];
    signature_channels: ['sms', 'whatsapp'];
    all_required_accepted: boolean;
  }> => {
    const response = await onboardingClient.get(`/api/v1/onboarding/contracts`);

    return response.data;
  },

  contractPreview: async (
    contract_type: 'terms_and_conditions' | 'privacy_policy' | 'service_agreement'
  ): Promise<{
    contract_type: string;
    version: string;
    preview_url: string;
    checksum: string;
    generated_at: string;
    expires_in: number;
    personalized: boolean;
  }> => {
    const response = await onboardingClient.post(`/api/v1/onboarding/contracts/preview`, {
      contract_type,
    });

    return response.data;
  },

  contractSignRequest: async (data: {
    channel: 'whatsapp' | 'sms';
    contracts: [
      {
        type: 'terms_and_conditions' | 'privacy_policy' | 'service_agreement';
        accepted: boolean;
        document_checksum: string;
      },
    ];
  }): Promise<{
    signing_session_id: string;
    code_expires_in: number;
    channel_used: 'whatsapp' | 'sms';
    phone_masked: string;
    contracts_to_sign: number;
    message: string;
  }> => {
    const response = await onboardingClient.post(`/api/v1/onboarding/contracts/sign/request`, data);

    return response.data;
  },

  // --- KYC ---
  kycStatus: async (): Promise<{
    id?: string;
    status: 'not_started' | 'in_progress' | 'pending_review' | 'verified' | 'rejected' | 'expired';
    level: 0 | 1 | 2 | 3;
    documents: Array<{
      id: string;
      type: string;
      status: 'pending' | 'uploaded' | 'approved' | 'rejected';
      url?: string;
      rejection_reason?: string;
      uploaded_at: string;
      reviewed_at?: string;
    }>;
    rejection_reason?: string;
    created_at?: string;
    updated_at?: string;
    verified_at?: string;
  }> => {
    const response = await apiClient.get('/api/v1/kyc/status');
    return response.data;
  },

  startKyc: async (): Promise<{
    id: string;
    status: string;
    level: number;
    documents: Array<Record<string, unknown>>;
    created_at: string;
  }> => {
    const response = await apiClient.post('/api/v1/kyc/start', {});
    return response.data;
  },

  uploadKycDocument: async (
    kycId: string,
    data: FormData
  ): Promise<{
    id: string;
    type: string;
    status: string;
    url?: string;
    uploaded_at: string;
  }> => {
    const response = await apiClient.post(`/api/v1/kyc/${kycId}/upload_document`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  verifyKyc: async (
    kycId: string
  ): Promise<{
    id: string;
    status: string;
    level: number;
    updated_at: string;
  }> => {
    const response = await apiClient.post(`/api/v1/kyc/${kycId}/verify`, {});
    return response.data;
  },

  retryKyc: async (
    kycId: string
  ): Promise<{
    id: string;
    status: string;
    level: number;
    updated_at: string;
  }> => {
    const response = await apiClient.post(`/api/v1/kyc/${kycId}/retry`, {});
    return response.data;
  },

  // --- Payment Orders ---
  paymentOrders: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
  }): Promise<{
    data: Array<{
      id: string;
      folio: string;
      status: 'pending' | 'confirmed' | 'processing' | 'completed' | 'failed' | 'cancelled';
      amount_cents: number;
      currency: string;
      concept: string;
      recipient_name: string;
      recipient_clabe: string;
      reference?: string;
      created_at: string;
      executed_at?: string;
      cancelled_at?: string;
    }>;
    meta: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
      status_counts?: {
        pending: number;
        processing: number;
        completed: number;
        failed: number;
        cancelled: number;
      };
    };
  }> => {
    const response = await apiClient.get('/api/v1/payment-orders', { params });
    return response.data;
  },

  paymentOrdersSummary: async (): Promise<{
    data: {
      total_count: number;
      status_counts: {
        pending: number;
        processing: number;
        completed: number;
        failed: number;
        cancelled: number;
      };
      /** Sum of orders with DB status "completed" only — refunded excluded. */
      sent_amount_cents: number;
      /** Sum of orders actively moving through the payment gateway. */
      in_flight_amount_cents: number;
      /** Sum of reversed/refunded orders (separate from sent). */
      refunded_amount_cents: number;
      /** Sum of failed/dead_letter/risk_rejected orders. */
      failed_amount_cents: number;
      currency: string;
    };
  }> => {
    const response = await apiClient.get('/api/v1/payment-orders/summary');
    return response.data;
  },

  paymentOrder: async (
    id: string
  ): Promise<{
    id: string;
    folio: string;
    status: 'pending' | 'confirmed' | 'processing' | 'completed' | 'failed' | 'cancelled';
    amount_cents: number;
    currency: string;
    concept: string;
    recipient_name: string;
    recipient_clabe: string;
    reference?: string;
    created_at: string;
    executed_at?: string;
    cancelled_at?: string;
  }> => {
    const response = await apiClient.get(`/api/v1/payment-orders/${id}`);
    return response.data;
  },

  createPaymentOrder: async (data: {
    amount_cents: number;
    currency_code: string;
    source_account_id: string;
    beneficiary_id?: string;
    concept: string;
    client_reference?: string;
    latitude?: number;
    longitude?: number;
    metadata?: Record<string, unknown>;
  }): Promise<{
    id: string;
    amount?: string;
    amount_cents: number;
    beneficiary_id: string | null;
    concept: string;
    created_at: string;
    currency_code: string;
    status: string;
    tracking_key?: string;
    updated_at?: string;
  }> => {
    const response = await apiClient.post('/api/v1/payment-orders', data);
    // Backend wraps payload as { success, data: {...} }
    return response.data?.data ?? response.data;
  },

  executePaymentOrder: async (
    id: string
  ): Promise<{
    id: string;
    status: string;
    executed_at: string;
  }> => {
    const response = await apiClient.post(`/api/v1/payment-orders/${id}/execute`, {});
    return response.data?.data ?? response.data;
  },

  cancelPaymentOrder: async (
    id: string,
    body?: { reason?: string; notes?: string }
  ): Promise<{
    id: string;
    status: string;
    cancelled_at?: string;
  }> => {
    const response = await apiClient.post(`/api/v1/payment-orders/${id}/cancel`, body ?? {});
    return response.data?.data ?? response.data;
  },

  // --- Rotating Savings ---
  rotatingSavings: async (): Promise<{
    data: Array<{
      id: string;
      folio: string;
      name: string;
      period: 'weekly' | 'biweekly' | 'monthly';
      start_date: string;
      end_date: string;
      amount_cents: number;
      contribution_cents: number;
      bonus_cents: number;
      status: 'active' | 'pending' | 'completed' | 'cancelled';
      link?: string;
    }>;
    meta: { total: number };
  }> => {
    const response = await apiClient.get('/api/v1/rotating-savings');
    return response.data;
  },

  rotatingSavingsSummary: async (): Promise<{
    total_balance_cents: number;
    active_count: number;
    pending_contributions_cents: number;
  }> => {
    const response = await apiClient.get('/api/v1/rotating-savings/summary');
    // Unwrap Rails envelope { data: {...} } if present.
    return response.data?.data ?? response.data;
  },

  // --- Investments ---
  /**
   * Listar inversiones del usuario
   * GET /api/v1/investments
   */
  investments: async (params?: {
    /** Acepta varios estados: axios los serializa como `status[]=` y el BE
     *  hace `where(status: [...])` → `IN (...)`. Necesario para que la vista
     *  "Activas" incluya los contratos en `pending_funding` (RFC 0029). */
    status?: string | string[];
    sort?: string;
    maturing_before?: string;
    page?: number;
    per_page?: number;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      contract_number: string;
      display_name?: string | null;
      product_version_id?: string;
      product_name?: string | null;
      product_code?: string | null;
      status: string;
      /** BE may return an object {code, name, ...} or a string */
      currency:
        | string
        | {
            code: string;
            name?: string;
            symbol?: string;
            minor_units?: number;
            numeric_code?: number;
            active?: boolean;
            created_at?: string;
            updated_at?: string;
          };
      principal_cents: number;
      interest_rate_bps?: number;
      /** BE returns this as a number */
      interest_rate_pct: number | string;
      interest_payment_frequency?: string;
      term_months: number;
      start_date: string;
      maturity_date: string;
      reinvest_at_maturity: boolean;
      /** RFC 0029 — derivado de `funded_at`. Fuente de verdad sobre si el
       *  principal ya entró; no depender del string de `status`. */
      funded?: boolean;
      created_at: string;
      updated_at?: string;
    }>;
    meta: {
      current_page: number;
      total_pages: number;
      total_count: number;
      per_page: number;
    };
  }> => {
    const response = await apiClient.get('/api/v1/investments', { params });
    return response.data;
  },

  /**
   * Obtener detalle de una inversión por ID
   * GET /api/v1/investments/{id}
   */
  investment: async (id: string) => {
    const response = await apiClient.get(`/api/v1/investments/${id}`);
    return response.data;
  },

  /**
   * GET /api/v1/investments/{id}/projection
   * Calcula la proyección de intereses para la inversión
   */
  investmentProjection: async (
    id: string,
    params?: {
      scenarios?: string;
      inflation_rate?: number;
    }
  ) => {
    const response = await apiClient.get(`/api/v1/investments/${id}/projection`, { params });
    return response.data;
  },

  investmentsSummary: async (): Promise<{
    total_invested_cents: number;
    active_count: number;
  }> => {
    const response = await apiClient.get('/api/v1/investments/summary');
    return response.data;
  },

  /**
   * Obtener métricas del portafolio de inversiones
   * GET /api/v1/investments/portfolio
   */
  investmentsPortfolio: async (params?: {
    as_of_date?: string; // YYYY-MM-DD
  }): Promise<{
    success: boolean;
    data: {
      as_of_date: string;
      summary: {
        total_contracts: number;
        active_contracts: number;
        total_principal_cents: number;
        total_accrued_interest_cents: number;
        total_paid_interest_cents: number;
        total_withheld_tax_cents: number;
        aum_cents: number;
        currency: string;
        average_rate_bps: number;
        average_term_months: number;
      };
      distribution_by_product: Array<{
        product_plan_id: string;
        count: number;
        principal_cents: number;
        percentage: number;
      }>;
      distribution_by_term: Array<{
        range: string;
        count: number;
        percentage: number;
      }>;
      upcoming_maturities: Array<{
        window: string;
        count: number;
        principal_cents: number;
      }>;
      performance: {
        weighted_average_rate_bps: number;
        total_yield_pct: number;
        total_principal_cents: number;
        total_interest_earned_cents: number;
      };
    };
  }> => {
    const response = await apiClient.get('/api/v1/investments/portfolio', { params });
    return response.data;
  },

  /**
   * Simular inversión
   * POST /api/v1/investments/simulate
   */
  simulateInvestment: async (data: {
    amount_cents: number;
    term_months: number;
    /**
     * Código del producto (ej. INV-STANDARD). Si se envía, el BE deriva la tasa
     * del cuadro del producto (misma fuente que el contrato real) y rate_bps se
     * ignora. Recomendado: evita el drift FE vs server (11.5% vs 11.75%).
     */
    product_code?: string;
    /** Fallback solo si NO se envía product_code. */
    rate_bps?: number;
    start_date: string;
  }): Promise<{
    success: boolean;
    data: {
      principal_cents: number;
      term_months: number;
      rate_bps: number;
      rate_percentage: number;
      start_date: string;
      end_date: string;
      gat_nominal_percentage: number;
      gat_real_percentage: number;
      schedule: Array<{
        period: number;
        start_date: string;
        end_date: string;
        days: number;
        deposit_cents: number;
        accumulated_cents: number;
        gross_interest_cents: number;
        tax_cents: number;
        net_interest_cents: number;
        rate_percentage: number;
      }>;
      totals: {
        deposit_cents: number;
        gross_interest_cents: number;
        tax_cents: number;
        net_interest_cents: number;
        final_amount_cents: number;
      };
    };
  }> => {
    const response = await apiClient.post('/api/v1/investments/simulate', data);
    return response.data;
  },

  /**
   * Crear inversión
   * POST /api/v1/investments
   */
  /**
   * Create investment
   * @param data - Investment data
   * @param idempotencyKey - Idempotency key for safe retries
   * @param stepUpToken - Step-Up MFA token (required for amounts >= 500,000 centavos)
   */
  createInvestment: async (
    data: {
      product_code?: string;
      product_version_id?: string;
      amount_cents: number;
      currency?: string;
      term_months: number;
      interest_payment_frequency: 'monthly' | 'quarterly' | 'at_maturity';
      reinvest_at_maturity: boolean;
      auto_renewal_term_months?: number;
      start_date?: string;
      beneficiaries?: Array<{
        party_id?: string;
        name: string;
        percentage: number;
        relationship: 'spouse' | 'child' | 'parent' | 'sibling' | 'other';
      }>;
      metadata?: Record<string, unknown>;
    },
    idempotencyKey: string,
    stepUpToken?: string
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      contract_number: string;
      product_version_id: string;
      product_name: string;
      status: 'draft' | 'active' | 'pending' | 'completed' | 'cancelled';
      currency: string;
      principal_cents: number;
      interest_rate_bps: number;
      interest_rate_pct: string;
      interest_payment_frequency: string;
      day_count_convention: string;
      term_months: number;
      start_date: string;
      maturity_date: string;
      reinvest_at_maturity: boolean;
      auto_renewal_term_months: number;
      wallet_account_id: string;
      beneficiaries: Array<{
        id: string;
        name: string;
        percentage: number;
        relationship: string;
      }>;
      signed_at?: string;
      signature_method?: string;
      created_at: string;
      updated_at: string;
    };
  }> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': idempotencyKey,
    };

    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }

    const response = await apiClient.post('/api/v1/investments', data, { headers });
    return response.data;
  },

  /**
   * Retiro anticipado de inversión (con penalidad automática)
   * POST /api/v1/investments/:id/withdraw
   * @param investmentId - ID de la inversión
   * @param data - { confirm: boolean }
   * @param stepUpToken - Token de Step-Up MFA (opcional)
   */
  withdrawInvestment: async (
    investmentId: string,
    data: { confirm: boolean },
    stepUpToken?: string
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      status: string;
      withdrawn_amount_cents: number;
      penalty_cents: number;
      net_amount_cents: number;
    };
  }> => {
    const headers: Record<string, string> = {};
    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }
    const response = await apiClient.post(`/api/v1/investments/${investmentId}/withdraw`, data, {
      headers,
    });
    return response.data;
  },

  // --- Credits ---
  credits: async (): Promise<{
    data: Array<{
      id: string;
      folio: string;
      created_at: string;
      value_cents: number;
      approval: 'approved' | 'pending' | 'rejected';
      status: 'active' | 'pending' | 'completed' | 'cancelled';
    }>;
    meta: { total: number };
  }> => {
    const response = await apiClient.get('/api/v1/credits');
    return response.data;
  },

  creditsSummary: async (): Promise<{
    total_credit_limit_cents: number;
    used_credit_cents: number;
    active_count: number;
  }> => {
    const response = await apiClient.get('/api/v1/credits/summary');
    return response.data;
  },

  // --- Savings ---

  /**
   * GET /api/v1/savings/portfolio
   * Métricas agregadas del portafolio de ahorro
   */
  savingsPortfolio: async (
    asOfDate?: string
  ): Promise<{
    success: boolean;
    data: {
      as_of_date: string;
      summary: {
        total_accounts: number;
        active_accounts: number;
        total_balance_cents: number;
        total_contributed_cents: number;
        total_withdrawn_cents: number;
        total_interest_earned_cents: number;
        average_balance_cents: number;
        average_rate_bps: number;
        currency: string;
      };
      contribution_metrics: {
        contributions_this_month: number;
        missed_contributions_this_month: number;
        contribution_compliance_rate: number;
        total_scheduled_cents: number;
      };
      goal_metrics: {
        accounts_with_goals: number;
        goals_reached: number;
        goals_in_progress: number;
        average_progress_pct: number;
      };
      retention_metrics: {
        opened_last_30_days: number;
        closed_last_30_days: number;
        net_growth: number;
      };
      generated_at: string;
    };
  }> => {
    const params = asOfDate ? { as_of_date: asOfDate } : {};
    const response = await apiClient.get('/api/v1/savings/portfolio', { params });
    return response.data;
  },

  /**
   * GET /api/v1/savings
   * Lista de cuentas de ahorro del usuario
   */
  savings: async (params?: {
    status?: string;
    sort?: 'balance' | 'created_at' | 'next_contribution';
    page?: number;
    per_page?: number;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      contract_number: string;
      product_version_id: string;
      product_name: string;
      display_name?: string | null;
      status:
        | 'draft'
        | 'pending_signature'
        | 'active'
        | 'suspended'
        | 'matured'
        | 'closed'
        | 'cancelled';
      current_balance_cents: number;
      current_balance: { amount: string; currency: string };
      contribution_amount_cents: number;
      contribution_frequency: string;
      next_contribution_date: string | null;
      goal_name: string | null;
      goal_amount_cents: number;
      goal_amount: { amount: string; currency: string };
      goal_progress_percentage: number;
      goal_achieved: boolean;
      total_contributed_cents: number;
      total_contributed: { amount: string; currency: string };
      total_interest_earned_cents: number;
      total_interest_earned: { amount: string; currency: string };
      total_withdrawn_cents: number;
      total_withdrawn: { amount: string; currency: string };
      contributions_count: number;
      missed_contributions_count: number;
      start_date: string;
      maturity_date: string | null;
      beneficiaries: Array<{
        id: string;
        name: string;
        percentage: number;
        relationship: string;
      }>;
      created_at: string;
      updated_at: string;
    }>;
    meta: {
      current_page: number;
      total_pages: number;
      total_count: number;
      per_page: number;
    };
  }> => {
    const response = await apiClient.get('/api/v1/savings', { params });
    return response.data;
  },

  /**
   * POST /api/v1/savings/simulate
   * Simula rendimientos de una caja de ahorro
   */
  savingsSimulate: async (params: {
    contribution_amount_cents: number;
    months: number;
    /**
     * Código del producto (ej. SAV-BASICA). Si se envía, el BE deriva la tasa
     * del cuadro del producto por monto (misma fuente que el contrato real) y
     * tiene precedencia sobre rate_bps. Sin esto el BE cae al default 8%.
     */
    product_code?: string;
    rate_bps?: number;
    initial_balance_cents?: number;
    start_date?: string;
  }): Promise<{
    success: boolean;
    data: {
      contribution_amount_cents: number;
      months: number;
      rate_bps: number;
      /** ISR rate applied (bps). Art. 21 LIF — varies by year. */
      isr_bps: number;
      /** GAT nominal/real (porcentaje) — homologa la confirmación con inversión. */
      gat_nominal_percentage: number;
      gat_real_percentage: number;
      initial_balance_cents: number;
      projected_balance_cents: number;
      total_contributions_cents: number;
      total_gross_interest_cents: number;
      total_isr_cents: number;
      total_net_interest_cents: number;
      monthly_breakdown: Array<{
        cuota: number;
        inicio: string;
        vencimiento: string;
        dias: number;
        deposito_cents: number;
        acumulado_cents: number;
        saldo_final_cents: number;
        rendimiento_bruto_cents: number;
        impuesto_cents: number;
        rendimiento_neto_cents: number;
        tasa_pct: number;
      }>;
      simulated_at: string;
    };
  }> => {
    const response = await apiClient.post('/api/v1/savings/simulate', params);
    return response.data;
  },

  /**
   * POST /api/v1/savings
   * Crear una nueva caja de ahorro
   * @param data - Datos de la caja de ahorro
   * @param idempotencyKey - Clave de idempotencia para retries seguros
   * @param stepUpToken - Token Step-Up MFA (requerido para montos >= 500,000 centavos)
   */
  createSavings: async (
    data: {
      product_code?: string;
      contribution_amount_cents: number;
      /** SAV-BASICA solo acepta frecuencia mensual */
      contribution_frequency: 'monthly';
      /** Plazo de compromiso obligatorio: 12 | 24 | 36 meses. Otro valor → 400/422 VAL-002 */
      commitment_months: 12 | 24 | 36;
      goal_name?: string;
      goal_amount_cents?: number;
      auto_debit_enabled?: boolean;
      beneficiaries?: Array<{
        party_id: string;
        percentage: number;
        relationship: 'spouse' | 'child' | 'parent' | 'sibling' | 'other';
      }>;
      metadata?: Record<string, unknown>;
    },
    idempotencyKey: string,
    stepUpToken?: string
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      contract_number: string;
      product_code: string;
      product_name: string;
      status: string;
      current_balance: { cents: number; currency: string };
      goal_name?: string;
      goal_amount_cents?: number;
      goal_progress_percentage?: number;
      contribution_amount_cents: number;
      contribution_frequency: string;
      interest_rate_bps: number;
      next_contribution_date?: string;
      auto_debit_enabled: boolean;
      start_date?: string;
      maturity_date?: string;
      /** Plazo de compromiso en meses (12 | 24 | 36) devuelto por el create */
      commitment_months?: number;
      /** Fecha de entrega (ISO date) = maturity_date; usada en la pantalla de éxito */
      delivery_date?: string;
      created_at: string;
      updated_at: string;
    };
  }> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': idempotencyKey,
    };

    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }

    const response = await apiClient.post('/api/v1/savings', data, { headers });
    return response.data;
  },

  /**
   * POST /api/v1/savings/{id}/contribute
   * Realiza un aporte a la cuenta de ahorro
   */
  savingsContribute: async (
    id: string,
    data: {
      amount_cents: number;
      payment_reference?: string;
      payment_source: 'internal_transfer' | 'spei' | 'card';
      contribution_date?: string;
      metadata?: Record<string, unknown>;
    },
    idempotencyKey: string,
    stepUpToken?: string
  ): Promise<{
    success: boolean;
    data: {
      contribution_id: string;
      contract_id: string;
      amount_cents: number;
      status: string;
      payment_reference?: string;
      contribution_date: string;
      days_late: number;
      new_balance_cents: number;
      total_contributed_cents: number;
      contributions_count: number;
      next_contribution_date: string;
      goal_progress_percentage?: number;
    };
  }> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': idempotencyKey,
    };

    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }

    const response = await apiClient.post(`/api/v1/savings/${id}/contribute`, data, { headers });
    return response.data;
  },

  /**
   * POST /api/v1/savings/{id}/withdraw
   * Solicita el retiro parcial o total de fondos de la cuenta de ahorro
   */
  savingsWithdraw: async (
    id: string,
    data: {
      amount_cents: number;
      reason?: string;
      confirm?: boolean;
    },
    idempotencyKey: string,
    stepUpToken?: string
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      contract_number: string;
      product_version_id: string;
      product_name: string;
      status: string;
      current_balance_cents: number;
      current_balance: { amount: string; currency: string };
      contribution_amount_cents: number;
      contribution_frequency: string;
      next_contribution_date: string;
      goal_name?: string;
      goal_amount_cents?: number;
      goal_amount?: { amount: string; currency: string };
      goal_progress_percentage?: number;
      goal_achieved?: boolean;
      total_contributed_cents: number;
      total_contributed: { amount: string; currency: string };
      total_interest_earned_cents: number;
      total_interest_earned: { amount: string; currency: string };
      total_withdrawn_cents: number;
      total_withdrawn: { amount: string; currency: string };
      contributions_count: number;
      missed_contributions_count: number;
      start_date: string;
      maturity_date: string;
      beneficiaries: Array<{
        id: string;
        name: string;
        percentage: number;
        relationship: string;
      }>;
      created_at: string;
      updated_at: string;
    };
  }> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': idempotencyKey,
    };

    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }

    const response = await apiClient.post(`/api/v1/savings/${id}/withdraw`, data, { headers });
    return response.data;
  },

  /**
   * POST /api/v1/savings/{id}/close
   * Cierra la cuenta de ahorro y transfiere el saldo restante
   */
  savingsClose: async (
    id: string,
    data: {
      transfer_destination?: string;
      reason?: string;
      confirm: boolean;
    },
    idempotencyKey: string,
    stepUpToken?: string
  ): Promise<{
    success: boolean;
    data: {
      contract_id: string;
      contract_number: string;
      status: string;
      closed_at: string;
      closed_by: string;
      reason: string | null;
      final_settlement: {
        principal_cents: number;
        interest_earned_cents: number;
        penalty_cents: number;
        total_payout_cents: number;
      };
      transfer_reference: string | null;
      account_summary: {
        opened_at: string;
        closed_at: string;
        duration_days: number;
        total_contributions: number;
        missed_contributions: number;
      };
    };
  }> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': idempotencyKey,
    };

    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }

    const response = await apiClient.post(`/api/v1/savings/${id}/close`, data, { headers });
    return response.data;
  },

  /**
   * GET /api/v1/savings/{id}
   * Obtiene los detalles de un contrato de ahorro
   */
  savingsDetail: async (id: string) => {
    const response = await apiClient.get(`/api/v1/savings/${id}`);
    return response.data;
  },

  /**
   * GET /api/v1/savings/{id}/projection
   * Calcula la proyección de intereses y crecimiento del ahorro
   */
  savingsProjection: async (
    id: string,
    params?: {
      months?: number;
      include_scenarios?: boolean;
    }
  ): Promise<{
    success: boolean;
    data: {
      initial_balance_cents: number;
      projected_balance_cents: number;
      total_contributions_cents: number;
      total_interest_cents: number;
      effective_rate_pct: number;
      monthly_breakdown: Array<{
        month: number;
        contribution_cents: number;
        interest_cents: number;
        balance_cents: number;
      }>;
      projection_date: string;
      goal_analysis: {
        goal_amount_cents: number;
        projected_balance_cents: number;
        goal_achieved_in_projection: boolean;
        shortfall_cents: number;
        excess_cents: number;
        progress_percentage: number;
      } | null;
    };
  }> => {
    const response = await apiClient.get(`/api/v1/savings/${id}/projection`, { params });
    return response.data;
  },

  /**
   * GET /api/v1/savings/{id}/amortization
   * Obtiene la tabla de amortización de una caja de ahorro
   */
  savingsAmortization: async (
    id: string,
    params?: { months?: number }
  ): Promise<{
    success: boolean;
    data: {
      contribution_amount_cents: number;
      months: number;
      rate_bps: number;
      isr_bps: number;
      initial_balance_cents: number;
      projected_balance_cents: number;
      total_contributions_cents: number;
      total_gross_interest_cents: number;
      total_isr_cents: number;
      total_net_interest_cents: number;
      monthly_breakdown: Array<{
        cuota: number;
        inicio: string;
        vencimiento: string;
        dias: number;
        deposito_cents: number;
        acumulado_cents: number;
        saldo_final_cents: number;
        rendimiento_bruto_cents: number;
        impuesto_cents: number;
        rendimiento_neto_cents: number;
        tasa_pct: number;
      }>;
      contract_id: string;
      contract_number: string;
      contribution_frequency: string;
    };
  }> => {
    const response = await apiClient.get(`/api/v1/savings/${id}/amortization`, { params });
    return response.data;
  },

  // --- Contacts ---
  contacts: async (params?: {
    type?: 'external' | 'tankef';
    search?: string;
  }): Promise<{
    data: Array<{
      id: string;
      type: 'external' | 'tankef';
      first_name: string;
      last_name: string;
      clabe?: string;
      bank_code?: string;
      bank_name?: string;
      alias?: string;
      email?: string;
      phone?: string;
      avatar_url?: string;
      created_at: string;
    }>;
    meta: { total: number };
  }> => {
    const response = await apiClient.get('/api/v1/contacts', { params });
    return response.data;
  },

  contactsSummary: async (): Promise<{
    total_count: number;
    external_count: number;
    tankef_count: number;
  }> => {
    const response = await apiClient.get('/api/v1/contacts/summary');
    return response.data;
  },

  createContact: async (data: {
    type: 'external' | 'tankef';
    first_name: string;
    last_name: string;
    clabe?: string;
    alias?: string;
    email?: string;
    phone?: string;
  }): Promise<{
    id: string;
    type: 'external' | 'tankef';
    first_name: string;
    last_name: string;
    clabe?: string;
    bank_code?: string;
    bank_name?: string;
    alias?: string;
    email?: string;
    phone?: string;
    created_at: string;
  }> => {
    const response = await apiClient.post('/api/v1/contacts', data);
    return response.data;
  },

  updateContact: async (
    id: string,
    data: {
      first_name?: string;
      last_name?: string;
      alias?: string;
      email?: string;
      phone?: string;
    }
  ): Promise<{
    id: string;
    type: 'external' | 'tankef';
    first_name: string;
    last_name: string;
    clabe?: string;
    bank_code?: string;
    bank_name?: string;
    alias?: string;
    email?: string;
    phone?: string;
    created_at: string;
  }> => {
    const response = await apiClient.patch(`/api/v1/contacts/${id}`, data);
    return response.data;
  },

  deleteContact: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/contacts/${id}`);
  },

  // ============================================================================
  // RECIPIENTS API (Unified Contacts + Transfer Recipients)
  // ============================================================================

  /**
   * GET /api/v1/recipients
   * Lista unificada de destinatarios (internos + SPEI)
   */
  recipients: async (params?: {
    favorite?: boolean;
    type?: 'internal' | 'external';
    search?: string;
  }): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      source: 'contact' | 'transfer_recipient';
      source_id: string;
      recipient_type: 'internal' | 'external';
      display_name: string;
      alias?: string;
      favorite: boolean;
      contact_party_id?: string;
      account_id?: string;
      phone_masked?: string;
      clabe_masked?: string;
      bank_code?: string;
      bank_name?: string;
      transfer_method: 'wallet' | 'spei';
      transfer_cost: 'free' | 'fee';
      transfer_speed: 'instant' | 'minutes';
      status: 'active' | 'inactive' | 'pending';
      verified: boolean;
      resolution_method?: 'phone' | 'email' | 'clabe';
      created_at: string;
    }>;
  }> => {
    const response = await apiClient.get('/api/v1/recipients', { params });
    return response.data;
  },

  /**
   * GET /api/v1/recipients/{id}
   * Obtener un destinatario por ID
   */
  recipient: async (
    id: string
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      source: 'contact' | 'transfer_recipient';
      source_id: string;
      recipient_type: 'internal' | 'external';
      display_name: string;
      alias?: string;
      favorite: boolean;
      contact_party_id?: string;
      account_id?: string;
      phone_masked?: string;
      clabe_masked?: string;
      bank_code?: string;
      bank_name?: string;
      transfer_method: 'wallet' | 'spei';
      transfer_cost: 'free' | 'fee';
      transfer_speed: 'instant' | 'minutes';
      status: 'active' | 'inactive' | 'pending';
      verified: boolean;
      resolution_method?: 'phone' | 'email' | 'clabe';
      created_at: string;
    };
  }> => {
    const response = await apiClient.get(`/api/v1/recipients/${id}`);
    return response.data;
  },

  /**
   * POST /api/v1/recipients
   * Crear destinatario (auto-detecta interno vs externo)
   */
  createRecipient: async (data: {
    identifier: string;
    identifier_type: 'clabe' | 'phone' | 'email';
    name?: string;
    alias?: string;
    favorite?: boolean;
    account_type?: 'checking' | 'savings';
    send_invitation?: boolean;
  }): Promise<{
    success: boolean;
    data: {
      id: string;
      source: 'contact' | 'transfer_recipient';
      source_id: string;
      recipient_type: 'internal' | 'external';
      display_name: string;
      alias?: string;
      favorite: boolean;
      contact_party_id?: string;
      account_id?: string;
      phone_masked?: string;
      clabe_masked?: string;
      bank_code?: string;
      bank_name?: string;
      transfer_method: 'wallet' | 'spei';
      transfer_cost: 'free' | 'fee';
      transfer_speed: 'instant' | 'minutes';
      status: 'active' | 'inactive' | 'pending';
      verified: boolean;
      resolution_method?: 'phone' | 'email' | 'clabe';
      created_at: string;
    };
  }> => {
    const response = await apiClient.post('/api/v1/recipients', data);
    return response.data;
  },

  /**
   * POST /api/v1/recipients/resolve
   * Previsualizar resolución sin crear
   */
  resolveRecipient: async (data: {
    identifier: string;
    identifier_type: 'clabe' | 'phone' | 'email';
  }): Promise<{
    success: boolean;
    data: {
      recipient_type: 'internal' | 'external';
      display_name?: string;
      resolution_method: 'phone' | 'email' | 'clabe';
      account_id?: string;
      bank_name?: string;
      clabe_masked?: string;
    };
  }> => {
    const response = await apiClient.post('/api/v1/recipients/resolve', data);
    return response.data;
  },

  /**
   * POST /api/v1/recipients/accept_invitation
   * Acepta una invitación de contacto creando el contacto inverso por party_id.
   */
  acceptContactInvitation: async (data: {
    party_id: string;
    alias?: string;
  }): Promise<{ success: boolean; data: Record<string, unknown> }> => {
    const response = await apiClient.post('/api/v1/recipients/accept_invitation', data);
    return response.data;
  },

  /**
   * PATCH /api/v1/recipients/{id}
   * Actualizar destinatario
   */
  updateRecipient: async (
    id: string,
    data: {
      alias?: string;
      favorite?: boolean;
      name?: string;
    }
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      source: 'contact' | 'transfer_recipient';
      source_id: string;
      recipient_type: 'internal' | 'external';
      display_name: string;
      alias?: string;
      favorite: boolean;
      contact_party_id?: string;
      account_id?: string;
      phone_masked?: string;
      clabe_masked?: string;
      bank_code?: string;
      bank_name?: string;
      transfer_method: 'wallet' | 'spei';
      transfer_cost: 'free' | 'fee';
      transfer_speed: 'instant' | 'minutes';
      status: 'active' | 'inactive' | 'pending';
      verified: boolean;
      resolution_method?: 'phone' | 'email' | 'clabe';
      created_at: string;
    };
  }> => {
    const response = await apiClient.patch(`/api/v1/recipients/${id}`, data);
    return response.data;
  },

  /**
   * DELETE /api/v1/recipients/{id}
   */
  deleteRecipient: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/recipients/${id}`);
  },

  // ============================================================================
  // MFA API (Multi-Factor Authentication - TOTP RFC 6238)
  // ============================================================================

  /**
   * GET /api/v1/mfa/status
   * Retorna estado actual de MFA
   */
  mfaStatus: async (): Promise<{
    success: boolean;
    data: {
      enabled: boolean;
      channel: 'totp' | 'sms' | 'email' | null;
      available_channels: Array<'totp' | 'sms' | 'email'>;
      required: boolean;
      must_setup: boolean;
      locked: boolean;
      locked_until: string | null;
      remaining_backup_codes: number | null;
      enabled_at: string | null;
      last_used_at: string | null;
      masked_phone: string | null;
      masked_email: string | null;
      enforcement?: {
        phase: string;
        required_for_login: boolean;
        days_until_required: number | null;
        grace_period_remaining: number | null;
        should_prompt_setup: boolean;
      };
    };
  }> => {
    const response = await apiClient.get('/api/v1/mfa/status');
    return response.data;
  },

  /**
   * POST /api/v1/mfa/setup
   * Genera secreto TOTP y QR code
   */
  mfaSetup: async (): Promise<{
    success: boolean;
    data: {
      secret: string;
      provisioning_uri: string;
      qr_code_svg: string;
      backup_codes: string[];
    };
  }> => {
    const response = await apiClient.post('/api/v1/mfa/setup', {});
    return response.data;
  },

  /**
   * POST /api/v1/mfa/enable
   * Habilita MFA después de verificar código OTP
   */
  mfaEnable: async (
    otp: string
  ): Promise<{
    success: boolean;
    data: {
      enabled: boolean;
      backup_codes: string[];
    };
  }> => {
    const response = await apiClient.post('/api/v1/mfa/enable', { code: otp });
    return response.data;
  },

  /**
   * DELETE /api/v1/mfa
   * Deshabilita MFA (requiere step-up token)
   */
  mfaDisable: async (
    stepUpToken: string
  ): Promise<{
    success: boolean;
    data: {
      enabled: boolean;
      disabled_at: string;
    };
  }> => {
    const response = await apiClient.delete('/api/v1/mfa', {
      data: { step_up_token: stepUpToken },
    });
    return response.data;
  },

  /**
   * POST /api/v1/mfa/verify
   * Verifica código OTP y retorna step-up token
   */
  /**
   * Verify MFA code and obtain step-up token
   * @param otp - 6-digit OTP code
   * @param purpose - 'login' (default) or 'operation_confirmation' for step-up MFA
   */
  /**
   * POST /api/v1/mfa/challenge
   * Envía un código OTP vía SMS, email, o WhatsApp
   */
  mfaChallenge: async (data: {
    channel: 'sms' | 'email' | 'whatsapp';
    reason?: 'signing' | 'login' | 'account_change';
  }): Promise<{
    success: boolean;
    data: {
      sent: boolean;
      masked_destination: string;
      challenge_sent: boolean;
    };
  }> => {
    const response = await apiClient.post('/api/v1/mfa/challenge', data);
    return response.data;
  },

  /**
   * POST /api/v1/mfa/verify
   * Verifica un código OTP (TOTP, SMS, o email) y retorna un step-up token
   */
  mfaVerify: async (
    otp: string,
    purpose?: 'login' | 'operation_confirmation'
  ): Promise<{
    success: boolean;
    data: {
      verified: boolean;
      step_up_token: string;
      expires_at: string;
    };
  }> => {
    const response = await apiClient.post('/api/v1/mfa/verify', {
      code: otp,
      ...(purpose && { purpose }),
    });
    return response.data;
  },

  /**
   * POST /api/v1/mfa/backup-codes/regenerate
   * Regenera códigos de respaldo (requiere step-up token)
   */
  mfaRegenerateBackupCodes: async (
    stepUpToken: string
  ): Promise<{
    success: boolean;
    data: {
      backup_codes: string[];
      generated_at: string;
    };
  }> => {
    const response = await apiClient.post('/api/v1/mfa/backup-codes/regenerate', {
      step_up_token: stepUpToken,
    });
    return response.data;
  },

  // ============================================================================
  // TRANSACTION LIMITS, SETTINGS & ACCOUNT INFO
  // ============================================================================

  transactionLimits: async () => {
    const response = await apiClient.get('/api/v1/transaction-limits');
    return response.data?.data ?? response.data;
  },

  transactionLimitsUsage: async () => {
    const response = await apiClient.get('/api/v1/transaction-limits/usage');
    return response.data?.data ?? response.data;
  },

  updateTransactionLimits: async (limits: Record<string, unknown>) => {
    const response = await apiClient.patch('/api/v1/settings/wallet', limits);
    return response.data;
  },

  accountClabe: async (accountId: string) => {
    try {
      const response = await apiClient.get(`/api/v1/accounts/${accountId}/clabe`);
      // API wraps payload: { success, data: { clabe, status, provider, ... } }
      return (response.data?.data ?? response.data) as {
        clabe: string;
        provider?: string;
        status: 'active' | 'pending' | 'failed';
        status_code?: string;
        status_description?: string;
        registered_at?: string;
        updated_at?: string;
      };
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { data?: unknown; [k: string]: unknown } };
      };
      // BE returns 422 when registration_status is "failed" or "clabe_failed".
      // Return the data payload so the hook stays in success state and the
      // component can render the failed UI instead of swallowing the error.
      if (axiosErr?.response?.status === 422) {
        return (axiosErr.response.data?.data ?? axiosErr.response.data) as {
          clabe: string;
          provider?: string;
          status: 'active' | 'pending' | 'failed';
          status_code?: string;
          status_description?: string;
          registered_at?: string;
          updated_at?: string;
        };
      }
      throw err;
    }
  },

  accountHolds: async (accountId: string, filters?: Record<string, string>) => {
    const response = await apiClient.get(`/api/v1/accounts/${accountId}/holds`, {
      params: filters,
    });
    const result = response.data?.data ?? response.data;
    return Array.isArray(result) ? result : [];
  },

  paymentOrderStatus: async (
    orderId: string
  ): Promise<{
    status: string;
    label?: string;
    updated_at?: string;
    message?: string;
    estimated_completion?: string;
    confidence?: string;
    is_business_hours?: boolean;
    next_business_day?: string;
    provider_status?: string;
  }> => {
    const response = await apiClient.get(`/api/v1/payment-orders/${orderId}/status`);
    return response.data?.data ?? response.data;
  },

  getAccessibilitySettings: async () => {
    const response = await apiClient.get('/api/v1/settings/accessibility');
    return response.data;
  },

  updateAccessibilitySettings: async (settings: Record<string, unknown>) => {
    const response = await apiClient.patch('/api/v1/settings/accessibility', settings);
    return response.data;
  },

  getProfileSettings: async () => {
    const response = await apiClient.get('/api/v1/settings/profile');
    return response.data;
  },

  updateProfileSettings: async (settings: Record<string, unknown>) => {
    const response = await apiClient.patch('/api/v1/settings/profile', settings);
    return response.data;
  },

  // --- Sesiones activas (Settings → Sesiones) ---
  // BE: GET /me/sessions devuelve [{ id, device_type, ip_address, user_agent,
  // last_active_at, created_at, current }] dentro del sobre { success, data }.
  // Mapeamos a camelCase para el FE.
  getActiveSessions: async (): Promise<
    Array<{
      id: string;
      deviceType: 'mobile' | 'tablet' | 'desktop';
      ipAddress: string | null;
      userAgent: string | null;
      lastActiveAt: string | null;
      createdAt: string;
      current: boolean;
    }>
  > => {
    const response = await apiClient.get('/api/v1/me/sessions');
    const rows =
      ((response.data?.data ?? response.data ?? []) as Array<Record<string, unknown>>) || [];
    return rows.map(r => ({
      id: String(r.id),
      deviceType: (r.device_type as 'mobile' | 'tablet' | 'desktop') ?? 'desktop',
      ipAddress: (r.ip_address as string) ?? null,
      userAgent: (r.user_agent as string) ?? null,
      lastActiveAt: (r.last_active_at as string) ?? null,
      createdAt: String(r.created_at ?? ''),
      current: Boolean(r.current),
    }));
  },

  /** Cierra una sesión específica. El BE rechaza la actual (CANNOT_REVOKE_CURRENT_SESSION). */
  revokeSession: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/me/sessions/${id}`);
  },

  /** Cierra todas las sesiones excepto la actual. */
  revokeAllSessions: async (): Promise<void> => {
    await apiClient.delete('/api/v1/me/sessions');
  },

  // --- Documentos legales del titular (Settings → Contratos) — RFC 0014 ---
  // BE: GET /me/legal-documents → { data: { documents: [...] } }. Solo
  // documentos inmutables ya archivados; los `processing` no tienen descarga.
  getLegalDocuments: async (): Promise<
    Array<{
      id: string;
      code: string;
      title: string;
      category: 'contract' | 'caratula' | 'institutional';
      folio: string | null;
      signedAt: string | null;
      version: string | null;
      contentType: string;
      status: 'available' | 'processing';
      generatedAt: string | null;
      sha256: string | null;
    }>
  > => {
    const response = await apiClient.get('/api/v1/me/legal-documents');
    const rows = ((response.data?.data?.documents ?? []) as Array<Record<string, unknown>>) || [];
    return rows.map(r => ({
      id: String(r.id),
      code: String(r.code ?? ''),
      title: String(r.title ?? ''),
      category: (r.category as 'contract' | 'caratula' | 'institutional') ?? 'institutional',
      folio: (r.folio as string) ?? null,
      signedAt: (r.signed_at as string) ?? null,
      version: (r.version as string) ?? null,
      contentType: String(r.content_type ?? 'application/pdf'),
      status: (r.status as 'available' | 'processing') ?? 'available',
      generatedAt: (r.generated_at as string) ?? null,
      sha256: (r.sha256 as string) ?? null,
    }));
  },

  /**
   * GET /me/legal-documents/:id/download → URL presignada de 300s.
   * La emisión queda auditada en el BE; abrirla de inmediato (expira).
   */
  downloadLegalDocument: async (
    id: string
  ): Promise<{ downloadUrl: string; expiresIn: number }> => {
    const response = await apiClient.get(`/api/v1/me/legal-documents/${id}/download`);
    const data = (response.data?.data ?? {}) as Record<string, unknown>;
    return {
      downloadUrl: String(data.download_url ?? ''),
      expiresIn: Number(data.expires_in ?? 300),
    };
  },

  getUIPreferences: async () => {
    const response = await apiClient.get('/api/v1/settings/ui-preferences');
    return response.data;
  },

  updateUIPreferences: async (preferences: Record<string, unknown>) => {
    const response = await apiClient.patch('/api/v1/settings/ui-preferences', preferences);
    return response.data;
  },

  // ============================================================================
  // USER & PROFILE
  // ============================================================================

  /**
   * GET /api/v1/me
   * Get current user profile
   */
  clientProfile: async () => {
    const response = await apiClient.get('/api/v1/me');
    return response.data;
  },

  // ============================================================================
  // ACCOUNTS & WALLET
  // ============================================================================

  /**
   * GET /api/v1/accounts/{id}/balance
   * Get balance for a specific account
   */
  accountBalance: async (accountId: string) => {
    const response = await apiClient.get(`/api/v1/accounts/${accountId}/balance`);
    return response.data?.data ?? response.data;
  },

  // ============================================================================
  // PRODUCTS
  // ============================================================================

  /**
   * GET /api/v1/products
   * Get product catalog
   */
  products: async (filters?: Record<string, unknown>) => {
    const response = await apiClient.get('/api/v1/products', { params: filters });
    return response.data;
  },

  /**
   * GET /api/v1/products/types
   * Get available product types
   */
  productTypes: async () => {
    const response = await apiClient.get('/api/v1/products/types');
    return response.data;
  },

  /**
   * GET /api/v1/products/eligible
   * Returns products the user is eligible for, plus ineligible ones with denial reasons
   */
  productsEligible: async (params?: { category?: string; include_ineligible?: boolean }) => {
    const response = await apiClient.get('/api/v1/products/eligible', { params });
    return response.data as {
      data: {
        eligible: Array<{
          product_plan_id: string;
          product_code: string;
          product_name: string;
          category: string;
          eligible: true;
          open_for_sale: boolean;
          capacity_remaining: number | null;
        }>;
        ineligible: Array<{
          product_plan_id: string;
          product_code: string;
          product_name: string;
          category: string;
          eligible: false;
          denial_reasons: Array<{ criterion_type: string; error_code: string; message: string }>;
          lifecycle_status: string;
        }>;
        summary: {
          total_evaluated: number;
          eligible_count: number;
          ineligible_count: number;
          categories: string[];
        };
      };
    };
  },

  // ============================================================================
  // INVESTMENTS (Additional endpoints)
  // ============================================================================

  /**
   * GET /api/v1/investments/{id}
   * Get investment details
   */
  investmentDetail: async (investmentId: string) => {
    const response = await apiClient.get(`/api/v1/investments/${investmentId}`);
    return response.data;
  },

  /**
   * POST /api/v1/investments/{id}/sign
   * Sign investment contract
   */
  investmentSign: async (investmentId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(`/api/v1/investments/${investmentId}/sign`, data);
    return response.data;
  },

  /**
   * POST /api/v1/investments/{id}/withdraw
   * Initiate investment withdrawal
   */
  investmentWithdraw: async (investmentId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(`/api/v1/investments/${investmentId}/withdraw`, data);
    return response.data;
  },

  /**
   * POST /api/v1/investments/{id}/confirm-withdrawal
   * Confirm investment withdrawal
   */
  confirmInvestmentWithdraw: async (investmentId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(
      `/api/v1/investments/${investmentId}/confirm-withdrawal`,
      data
    );
    return response.data;
  },

  /**
   * DELETE /api/v1/investments/{id}/withdraw
   * Cancel investment withdrawal
   */
  cancelInvestmentWithdraw: async (investmentId: string) => {
    await apiClient.delete(`/api/v1/investments/${investmentId}/withdraw`);
  },

  /**
   * GET /api/v1/investments/{id}/statement
   * Get investment statement
   */
  investmentStatement: async (investmentId: string) => {
    const response = await apiClient.get(`/api/v1/investments/${investmentId}/statement`);
    return response.data;
  },

  /**
   * PATCH /api/v1/investments/{id}/renewal
   * Update investment renewal settings
   */
  updateInvestmentRenewal: async (investmentId: string, data: Record<string, unknown>) => {
    const response = await apiClient.patch(`/api/v1/investments/${investmentId}/renewal`, data);
    return response.data;
  },

  /**
   * POST /api/v1/investments/{id}/beneficiaries
   * Add investment beneficiary
   */
  addInvestmentBeneficiary: async (investmentId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(
      `/api/v1/investments/${investmentId}/beneficiaries`,
      data
    );
    return response.data;
  },

  /**
   * DELETE /api/v1/investments/{id}/beneficiaries/{beneficiaryId}
   * Remove investment beneficiary
   */
  removeInvestmentBeneficiary: async (investmentId: string, beneficiaryId: string) => {
    await apiClient.delete(`/api/v1/investments/${investmentId}/beneficiaries/${beneficiaryId}`);
  },

  // ============================================================================
  // SAVINGS (Additional endpoints)
  // ============================================================================

  /**
   * GET /api/v1/savings
   * Get list of user's savings boxes
   */
  savingsBoxes: async () => {
    const response = await apiClient.get('/api/v1/savings');
    return response.data;
  },

  /**
   * GET /api/v1/savings/portfolio
   * Get savings portfolio summary
   */
  savingsBoxSummary: async () => {
    const response = await apiClient.get('/api/v1/savings/portfolio');
    return response.data;
  },

  /**
   * POST /api/v1/savings/{id}/sign
   * Sign savings contract
   */
  savingsSign: async (savingsId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(`/api/v1/savings/${savingsId}/sign`, data);
    return response.data;
  },

  /**
   * GET /api/v1/savings/{id}/statement
   * Get savings statement
   */
  savingsStatement: async (savingsId: string) => {
    const response = await apiClient.get(`/api/v1/savings/${savingsId}/statement`);
    return response.data;
  },

  /**
   * PATCH /api/v1/savings/{id}/goal
   * Update savings goal
   */
  updateSavingsGoal: async (savingsId: string, data: Record<string, unknown>) => {
    const response = await apiClient.patch(`/api/v1/savings/${savingsId}/goal`, data);
    return response.data;
  },

  /**
   * PATCH /api/v1/savings/{id}/auto-debit
   * Update savings auto-debit status
   */
  updateSavingsAutoDebit: async (savingsId: string, data: Record<string, unknown>) => {
    const response = await apiClient.patch(`/api/v1/savings/${savingsId}/auto-debit`, data);
    return response.data;
  },

  /**
   * POST /api/v1/savings/{id}/suspend
   * Suspend savings box
   */
  savingsSuspend: async (savingsId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(`/api/v1/savings/${savingsId}/suspend`, data);
    return response.data;
  },

  /**
   * POST /api/v1/savings/{id}/reactivate
   * Reactivate savings box
   */
  savingsReactivate: async (savingsId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(`/api/v1/savings/${savingsId}/reactivate`, data);
    return response.data;
  },

  // ============================================================================
  // CREDITS (Additional endpoints)
  // ============================================================================

  /**
   * GET /api/v1/credits/{id}
   * Get credit details
   */
  credit: async (creditId: string) => {
    const response = await apiClient.get(`/api/v1/credits/${creditId}`);
    return response.data;
  },

  /**
   * GET /api/v1/credits/portfolio
   * Get credits portfolio summary
   */
  creditsPortfolio: async () => {
    const response = await apiClient.get('/api/v1/credits/portfolio');
    return response.data;
  },

  /**
   * GET /api/v1/credits/{id}/schedule
   * Get credit payment schedule
   */
  creditSchedule: async (creditId: string) => {
    const response = await apiClient.get(`/api/v1/credits/${creditId}/schedule`);
    return response.data;
  },

  /**
   * GET /api/v1/credits/{id}/statement
   * Get credit statement
   */
  creditStatement: async (creditId: string) => {
    const response = await apiClient.get(`/api/v1/credits/${creditId}/statement`);
    return response.data;
  },

  /**
   * GET /api/v1/credits/{id}/projection
   * Get credit projection
   */
  creditProjection: async (creditId: string) => {
    const response = await apiClient.get(`/api/v1/credits/${creditId}/projection`);
    return response.data;
  },

  /**
   * POST /api/v1/credits/{id}/pay
   * Make credit payment
   */
  payCredit: async (creditId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(`/api/v1/credits/${creditId}/pay`, data);
    return response.data;
  },

  /**
   * POST /api/v1/credits/{id}/early-payoff
   * Get early payoff quote for credit
   */
  creditEarlyPayoff: async (creditId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(`/api/v1/credits/${creditId}/early-payoff`, data);
    return response.data;
  },

  // ============================================================================
  // KYC ENDPOINTS
  // ============================================================================

  /**
   * POST /api/v1/kyc/validate-curp
   * Validate CURP format and check against government records
   */
  validateCurp: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/api/v1/kyc/validate-curp', data);
    return response.data;
  },

  // ============================================================================
  // ONBOARDING & KYC (Additional methods)
  // ============================================================================

  /**
   * GET /api/v1/onboarding/kyc-sdk-config
   * Get KYC SDK configuration
   */
  getOnboardingKycSdkConfig: async () => {
    const response = await onboardingClient.get(buildOnboardingUrl('kyc/sdk-config'), {
      headers: getOnboardingAuthHeaders(),
    });
    return response.data.data;
  },

  /**
   * POST /api/v1/onboarding/sessions/resume/request
   */
  onboardingResumeRequest: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/api/v1/onboarding/sessions/resume/request', data);
    return response.data;
  },

  /**
   * POST /api/v1/onboarding/sessions/resume/confirm
   */
  onboardingResumeConfirm: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/api/v1/onboarding/sessions/resume/confirm', data);
    return response.data;
  },

  /**
   * POST /api/v1/onboarding/sessions/:session_id/kyc/check-blacklist
   * Check blacklist status within an onboarding session
   */
  checkBlacklistKyc: async (sessionId: string, data: Record<string, unknown>) => {
    // Endpoint de onboarding (pre-auth): debe usar onboardingClient + el
    // onboarding token, NO apiClient. Con apiClient: (a) no se manda el
    // Authorization correcto, (b) el 401 dispara el interceptor que hace
    // window.location = '/login' y "saca" al usuario del flujo de onboarding.
    const response = await onboardingClient.post(
      `/api/v1/onboarding/sessions/${sessionId}/kyc/check-blacklist`,
      data,
      { headers: getOnboardingAuthHeaders() }
    );
    return response.data;
  },

  // ============================================================================
  // TANDAS (Rotating Savings)
  // ============================================================================

  // ============================================================================
  // Tandas — endpoints legacy `/tandas/*` removidos. La superficie canónica
  // es `/relays/*` (clientApi.relayList, relayCreate, relayJoin, etc).
  // Solo permanecen los endpoints que NO tienen equivalente en /relays:
  // simulate, types, active, my-status, accept-agreement, set-payment-accounts,
  // business-rules. Más `createRotatingSavings` que sigue usado por el
  // dominio `rotating-savings/` (producto legacy con su propio UI separado).
  // ============================================================================

  /**
   * POST /api/v1/tandas
   * Crea una tanda para el dominio legacy `rotating-savings/`. El dominio
   * moderno `tandas/` usa `clientApi.relayCreate` (POST /api/v1/relays).
   */
  createRotatingSavings: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/api/v1/tandas', data);
    return response.data;
  },

  /**
   * GET /api/v1/tandas/types
   * List available tanda types / product plans
   */
  tandaTypes: async () => {
    const response = await apiClient.get('/api/v1/tandas/types');
    return response.data as {
      data: Array<{
        id: string;
        code: string;
        name: string;
        rotation_type: 'sequential' | 'lottery' | 'auction';
        min_slot_count: number;
        max_slot_count: number;
        min_contribution_amount_cents: number;
        max_contribution_amount_cents: number;
        allowed_frequencies: Array<'weekly' | 'biweekly' | 'monthly'>;
        description?: string;
      }>;
    };
  },

  // ============================================================================
  // Tandas — Relays API (canonical surface per docs/plans/TANDAS_RELAY_SPEC.md)
  // ============================================================================

  /**
   * GET /api/v1/tandas/simulate
   * Calcula bonos, term_label, effort_rate y total_interest del simulator.
   * No persiste — puro cálculo, idempotente. Sólo requiere bearer auth.
   */
  tandaSimulate: async (params: {
    amount_cents: number;
    participants: number;
    frequency?: 'monthly' | 'mensuales' | 'biweekly' | 'quincenales';
  }): Promise<{
    success: boolean;
    data: {
      pot_amount_cents: number;
      participants: number;
      total_persons: number;
      payers: number;
      amount_per_participant_cents: number;
      total_flow_cents: number;
      frequency: string;
      frequency_days: number;
      total_days: number;
      term_label: string;
      effort_rate: number;
      total_interest_cents: number;
      admin_bonus_cents: number;
      participant_bonus_cents: number;
    };
  }> => {
    const response = await apiClient.get('/api/v1/tandas/simulate', { params });
    return response.data;
  },

  /**
   * GET /api/v1/tandas/active
   * Indica si el usuario autenticado tiene tandas (`has_active_tandas`) y,
   * de tenerlas, las devuelve separadas por rol (`as_admin` /
   * `as_participant`). En el caso sin tandas el backend omite las listas;
   * acá se normalizan a `[]` para que el consumidor no tenga que checarlas.
   * "Activa" aquí = cualquier tanda del usuario sin importar su `status`.
   */
  tandasActive: async (): Promise<ActiveTandasResponse> => {
    const response = await apiClient.get('/api/v1/tandas/active');
    const data = unwrapRelayEnvelope<Partial<ActiveTandasResponse>>(response.data);
    return {
      has_active_tandas: data?.has_active_tandas ?? false,
      as_admin: data?.as_admin ?? [],
      as_participant: data?.as_participant ?? [],
    };
  },

  /**
   * GET /api/v1/relays
   * Lista tandas del usuario (split en admin/participant).
   */
  relayList: async (): Promise<{
    relays_as_admin: RelaySummary[];
    relays_as_participant: RelaySummary[];
  }> => {
    const response = await apiClient.get('/api/v1/relays');
    return unwrapRelayEnvelope(response.data);
  },

  /**
   * GET /api/v1/relays/active
   * Tandas activas (mismo shape que list).
   */
  relayListActive: async (): Promise<{
    relays_as_admin: RelaySummary[];
    relays_as_participant: RelaySummary[];
  }> => {
    const response = await apiClient.get('/api/v1/relays/active');
    return unwrapRelayEnvelope(response.data);
  },

  /**
   * GET /api/v1/relays/finished
   * Tandas completadas.
   */
  relayListFinished: async (): Promise<{
    relays_as_admin: RelaySummary[];
    relays_as_participant: RelaySummary[];
  }> => {
    const response = await apiClient.get('/api/v1/relays/finished');
    return unwrapRelayEnvelope(response.data);
  },

  /**
   * GET /api/v1/relays/:id
   * Detalle de una tanda con participants + payment_periods.
   */
  relayDetail: async (id: string): Promise<{ data: RelayDetail }> => {
    const response = await apiClient.get(`/api/v1/relays/${id}`);
    return { data: unwrapRelayResource<RelayDetail>(response.data) };
  },

  /**
   * POST /api/v1/relays
   * Crear tanda (admin) — requiere MFA stepUp.
   */
  relayCreate: async (
    body: CreateRelayInput,
    stepUpToken?: string
  ): Promise<{ relay: RelayDetail; message?: string }> => {
    const headers: Record<string, string> = {};
    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }
    const response = await apiClient.post('/api/v1/relays', { relay: body }, { headers });
    return {
      relay: unwrapRelayResource<RelayDetail>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * GET /api/v1/relays/by-token/:token
   * Preview público de invitación — sin auth, rate-limited en backend.
   * El interceptor de apiClient añade Authorization si hay token de sesión;
   * eso NO rompe el endpoint (backend lo acepta opcional).
   */
  relayInvitationByToken: async (token: string): Promise<{ data: RelayInvitationPreview }> => {
    const response = await apiClient.get(`/api/v1/relays/by-token/${encodeURIComponent(token)}`);
    return { data: unwrapRelayResource<RelayInvitationPreview>(response.data) };
  },

  /**
   * POST /api/v1/relays/invitations/:token/accept
   *
   * Acepta una invitación a una tanda usando el token público. Reemplaza
   * al deprecated `POST /relays/:id/join` que pedía `id` en path + body
   * `{ token }` — el token ahora va directo en el path y no hay body.
   *
   * El BE devuelve `{ success, data: { tanda_group_id, member_id, role,
   * message } }` (plano, sin envoltorio `relay`). Por eso usamos
   * `unwrapRelayEnvelope` (pela solo `{ success, data }`) y NO
   * `unwrapRelayResource` (que des-anidaría `data.relay`).
   */
  relayAcceptInvitation: async (
    token: string
  ): Promise<{ data: RelayAcceptInvitationResponse; message?: string }> => {
    const response = await apiClient.post(
      `/api/v1/relays/invitations/${encodeURIComponent(token)}/accept`,
      {}
    );
    return {
      data: unwrapRelayEnvelope<RelayAcceptInvitationResponse>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * GET /api/v1/relays/:id/invitations
   * Lista invitaciones (admin-level) de un relay. Solo el admin del relay
   * puede leerla (BE policy `TandaRelayInvitationPolicy#index?`).
   */
  relayListInvitations: async (id: string): Promise<{ data: RelayInvitation[] }> => {
    const response = await apiClient.get(`/api/v1/relays/${encodeURIComponent(id)}/invitations`);
    // El BE actualmente devuelve `{ success, data: [...] }` (array directo).
    // Defensivo: aceptar también `{ data: { invitations: [...] } }` por si
    // el shape cambia en el futuro.
    const unwrapped = unwrapRelayEnvelope<RelayInvitation[] | { invitations: RelayInvitation[] }>(
      response.data
    );
    const list = Array.isArray(unwrapped) ? unwrapped : (unwrapped?.invitations ?? []);
    return { data: list };
  },

  /**
   * POST /api/v1/relays/:id/invitations
   * Crea una invitación admin. Requiere al menos `invitee_email` o
   * `invitee_phone`. El BE espera el body nested bajo `invitation`.
   */
  relayCreateInvitation: async (
    id: string,
    body: CreateRelayInvitationInput
  ): Promise<{ data: CreateRelayInvitationResponse; message?: string }> => {
    const response = await apiClient.post(`/api/v1/relays/${encodeURIComponent(id)}/invitations`, {
      invitation: body,
    });
    const unwrapped = unwrapRelayEnvelope<{
      invitation: CreateRelayInvitationResponse;
      message?: string;
    }>(response.data);
    return {
      data: unwrapped.invitation,
      message: unwrapped.message ?? response.data?.message,
    };
  },

  /**
   * DELETE /api/v1/relays/:id/invitations/:invitation_id
   * Revoca una invitación pendiente. `reason` opcional como query param.
   */
  relayRevokeInvitation: async (
    id: string,
    invitationId: string,
    reason?: string
  ): Promise<{ data: { invitation_id: string; revoked: boolean } }> => {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await apiClient.delete(
      `/api/v1/relays/${encodeURIComponent(id)}/invitations/${encodeURIComponent(invitationId)}${query}`
    );
    return {
      data: unwrapRelayEnvelope<{ invitation_id: string; revoked: boolean }>(response.data),
    };
  },

  /**
   * GET /api/v1/relays/:id/validate
   * Verifica que la tanda esté completa antes de activar.
   */
  relayValidate: async (
    id: string
  ): Promise<{
    valid: boolean;
    total_members: number;
    current_participants: number;
    missing: number;
  }> => {
    const response = await apiClient.get(`/api/v1/relays/${id}/validate`);
    return unwrapRelayEnvelope(response.data);
  },

  /**
   * PUT /api/v1/relays/:id/activate
   * Activar la tanda — pasa al estado active. Requiere MFA stepUp.
   */
  relayActivate: async (
    id: string,
    body: { start_at?: string },
    stepUpToken?: string
  ): Promise<{ data: RelayDetail; message?: string }> => {
    const headers: Record<string, string> = {};
    if (stepUpToken) {
      headers['X-Step-Up-Token'] = stepUpToken;
    }
    const response = await apiClient.put(`/api/v1/relays/${id}/activate`, body, { headers });
    return {
      data: unwrapRelayResource<RelayDetail>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * PATCH /api/v1/relays/:id/participants/reorder
   * Reordena participantes (admin). `participant_ids` es el orden 1..N.
   */
  relayReorderParticipants: async (
    id: string,
    participant_ids: string[]
  ): Promise<{ data: RelayDetail; message?: string }> => {
    const response = await apiClient.patch(`/api/v1/relays/${id}/participants/reorder`, {
      participant_ids,
    });
    return {
      data: unwrapRelayResource<RelayDetail>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * DELETE /api/v1/relays/:id/participants/:participant_id
   * Remueve participante (admin) con motivo.
   */
  relayRemoveParticipant: async (
    id: string,
    participantId: string,
    body: { reason: string }
  ): Promise<{ message?: string }> => {
    const response = await apiClient.delete(`/api/v1/relays/${id}/participants/${participantId}`, {
      data: body,
    });
    return { message: response.data?.message };
  },

  /**
   * PATCH /api/v1/relays/:id
   * Update granular de un draft. Envía solo los campos que cambian.
   */
  relayUpdate: async (
    id: string,
    body: Partial<CreateRelayInput>
  ): Promise<{ data: RelayDetail; message?: string }> => {
    const response = await apiClient.patch(`/api/v1/relays/${id}`, { relay: body });
    return {
      data: unwrapRelayResource<RelayDetail>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * PATCH /api/v1/relays/:id/amount_and_frequency
   * Atajo para el caso más común (monto + frecuencia).
   */
  relayPatchAmountAndFrequency: async (
    id: string,
    body: { amount: string; payment_frequency: RelayPaymentFrequency }
  ): Promise<{ data: RelayDetail; message?: string }> => {
    const response = await apiClient.patch(`/api/v1/relays/${id}/amount_and_frequency`, {
      relay: body,
    });
    return {
      data: unwrapRelayResource<RelayDetail>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * PATCH /api/v1/relays/:id/start_date
   */
  relayPatchStartDate: async (
    id: string,
    start_at: string
  ): Promise<{ data: RelayDetail; message?: string }> => {
    const response = await apiClient.patch(`/api/v1/relays/${id}/start_date`, { start_at });
    return {
      data: unwrapRelayResource<RelayDetail>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * PATCH /api/v1/relays/:id/participant_count
   */
  relayPatchParticipantCount: async (
    id: string,
    person: number
  ): Promise<{ data: RelayDetail; message?: string }> => {
    const response = await apiClient.patch(`/api/v1/relays/${id}/participant_count`, {
      person,
    });
    return {
      data: unwrapRelayResource<RelayDetail>(response.data),
      message: response.data?.message,
    };
  },

  /**
   * DELETE /api/v1/relays/:id
   * Cancela un draft (no aplica a tandas activas). Requiere `reason`.
   */
  relayCancel: async (id: string, reason: string): Promise<{ message?: string }> => {
    const response = await apiClient.delete(`/api/v1/relays/${id}`, {
      data: { reason },
    });
    return { message: response.data?.message };
  },

  /**
   * GET /api/v1/relays/:id/payments?period=N
   * Lista pagos del período (default = todos los períodos si no se especifica).
   */
  relayPaymentsList: async (
    id: string,
    params?: { period?: number }
  ): Promise<{ data: RelayPayment[] }> => {
    const response = await apiClient.get(`/api/v1/relays/${id}/payments`, { params });
    // El backend puede envolver y/o anidar la lista bajo `payments`.
    const env = unwrapRelayEnvelope<unknown>(response.data);
    const list = Array.isArray(env) ? env : ((env as { payments?: unknown[] })?.payments ?? []);
    // Normalización de shape: el BE nuevo emite los pagos con
    // `round_number / member_id / status / due_date / has_receipt`, pero el
    // FE (RelayPayment) usa `period / participant_id / state / scheduled_at /
    // payment_receipt_url`. Sin esto el filtro por período no matchea y la
    // tabla sale vacía. Preferimos el campo FE si ya viene, fallback al nuevo.
    const normalized = (list as Record<string, unknown>[]).map(p => ({
      ...p,
      period: (p.period ?? p.round_number) as number,
      participant_id: (p.participant_id ?? p.member_id) as string,
      participant_name: (p.participant_name ?? p.member_name) as string | undefined,
      state: (p.state ?? p.status) as RelayPaymentState,
      scheduled_at: (p.scheduled_at ?? p.due_date) as string,
    }));
    return { data: normalized as RelayPayment[] };
  },

  /**
   * GET /api/v1/relays/:id/payments/:paymentId
   */
  relayPaymentDetail: async (id: string, paymentId: string): Promise<{ data: RelayPayment }> => {
    const response = await apiClient.get(`/api/v1/relays/${id}/payments/${paymentId}`);
    const env = unwrapRelayEnvelope<unknown>(response.data);
    const payment =
      env !== null && typeof env === 'object' && 'payment' in env
        ? (env as { payment: RelayPayment }).payment
        : (env as RelayPayment);
    return { data: payment };
  },

  /**
   * PATCH /api/v1/relays/:id/payments/:paymentId
   * Sube comprobante (multipart). Axios detecta FormData automáticamente y
   * setea el header Content-Type con boundary correcto — NO sobrescribirlo.
   *
   * BE response: `{ success, data: { payment_id, message } }`. NO devuelve
   * el objeto RelayPayment completo — el caller debe invalidar la query
   * para refrescar el detalle del pago si lo necesita.
   */
  relayPaymentUploadReceipt: async (
    id: string,
    paymentId: string,
    data: { file: File; reference?: string }
  ): Promise<{ data: { payment_id: string; message?: string } }> => {
    const formData = new FormData();
    formData.append('payment_receipt', data.file);
    if (data.reference) {
      formData.append('payment_reference', data.reference);
    }
    // Usamos `directUploadClient` (sin Content-Type forzado) en vez de
    // `apiClient`: con el default `application/json` axios no ponía el
    // boundary del multipart y el backend respondía 422. Auth manual.
    const token = getStoredToken();
    const response = await directUploadClient.patch(
      `/api/v1/relays/${id}/payments/${paymentId}`,
      formData,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return {
      data: unwrapRelayEnvelope<{ payment_id: string; message?: string }>(response.data),
    };
  },

  /**
   * POST /api/v1/relays/:id/payments/:paymentId/charge
   * Paga la aportación cargándola al saldo del wallet del usuario (sin body —
   * el monto es el del slot). 200 → aportación `approved`. Si falla, el BE
   * responde 422 con `error.code` (INSUFFICIENT_BALANCE, FEATURE_DISABLED,
   * NO_ESCROW, NOT_CONTRIBUTOR, PAYMENT_NOT_PENDING, NO_SOURCE_ACCOUNT) que
   * el caller usa para decidir el fallback a comprobante.
   */
  relayPaymentCharge: async (
    id: string,
    paymentId: string
  ): Promise<{
    data: { payment_id: string; status: string; charged_amount_cents: number };
  }> => {
    const response = await apiClient.post(`/api/v1/relays/${id}/payments/${paymentId}/charge`, {});
    return {
      data: unwrapRelayEnvelope<{
        payment_id: string;
        status: string;
        charged_amount_cents: number;
      }>(response.data),
    };
  },

  /**
   * POST /api/v1/relays/:id/payments/:period/execute
   * Admin ejecuta la distribución de un período. Sin body.
   */
  relayPaymentExecute: async (
    id: string,
    period: number
  ): Promise<{ data: RelayPayment[]; message?: string }> => {
    const response = await apiClient.post(`/api/v1/relays/${id}/payments/${period}/execute`, {});
    const env = unwrapRelayEnvelope<unknown>(response.data);
    const list = Array.isArray(env)
      ? env
      : ((env as { payments?: RelayPayment[] })?.payments ?? []);
    return { data: list as RelayPayment[], message: response.data?.message };
  },

  /**
   * GET /api/v1/relays/by-folio/:folio
   * Búsqueda REST por folio público (formato `TAN-YY-NNNNNN`). Reemplaza
   * el endpoint legacy `POST /relays/search` que llevaba el folio en body.
   * Devuelve la tanda con su token (para construir el link de unirse).
   * Doc: `TANDA_API_REFERENCE.md` § 1 (Discovery).
   */
  relaySearchByFolio: async (
    folio: string
  ): Promise<{ data: RelayInvitationPreview & { id: string; token?: string } }> => {
    const response = await apiClient.get(`/api/v1/relays/by-folio/${encodeURIComponent(folio)}`);
    return {
      data: unwrapRelayResource<RelayInvitationPreview & { id: string; token?: string }>(
        response.data
      ),
    };
  },

  // ============================================================================
  // RELAYS — Admin payments approve/reject (F2.1 — TANDA_API_REFERENCE.md § 7)
  // ============================================================================

  /**
   * PATCH /api/v1/admin/relays/:id/payments/:paymentId/approve
   * Admin aprueba una aportación. Dispara JournalEntry contable.
   */
  relayPaymentApprove: async (
    id: string,
    paymentId: string
  ): Promise<{
    data: RelayPayment & { approved_at?: string; journal_entry_id?: string };
    message?: string;
  }> => {
    const response = await apiClient.patch(
      `/api/v1/admin/relays/${id}/payments/${paymentId}/approve`,
      {}
    );
    const env = unwrapRelayEnvelope<unknown>(response.data);
    return {
      data: env as RelayPayment & { approved_at?: string; journal_entry_id?: string },
      message: response.data?.message,
    };
  },

  /**
   * PATCH /api/v1/admin/relays/:id/payments/:paymentId/reject
   * Admin rechaza una aportación con motivo (auditable).
   */
  relayPaymentReject: async (
    id: string,
    paymentId: string,
    reason: string
  ): Promise<{
    data: RelayPayment & { rejected_at?: string; rejection_reason?: string };
    message?: string;
  }> => {
    const response = await apiClient.patch(
      `/api/v1/admin/relays/${id}/payments/${paymentId}/reject`,
      { reason }
    );
    const env = unwrapRelayEnvelope<unknown>(response.data);
    return {
      data: env as RelayPayment & { rejected_at?: string; rejection_reason?: string },
      message: response.data?.message,
    };
  },

  // ============================================================================
  // RELAYS — Decline invitation (F2.2 — TANDA_API_REFERENCE.md § 4)
  // ============================================================================

  /**
   * POST /api/v1/relays/invitations/:token/decline
   * Invitee rechaza voluntariamente una invitación dirigida (vs. silencio).
   */
  relayDeclineInvitation: async (
    token: string,
    reason?: string
  ): Promise<{
    data: {
      message?: string;
      invitation_id: string;
      status: 'declined';
      tanda_group_id: string;
    };
  }> => {
    const response = await apiClient.post(
      `/api/v1/relays/invitations/${encodeURIComponent(token)}/decline`,
      reason ? { reason } : {}
    );
    return {
      data: unwrapRelayEnvelope<{
        message?: string;
        invitation_id: string;
        status: 'declined';
        tanda_group_id: string;
      }>(response.data),
    };
  },

  // ============================================================================
  // RELAYS — Turn requests (F2.3 — TANDA_API_REFERENCE.md § 6)
  // ============================================================================

  /**
   * GET /api/v1/relays/:id/turn-requests?status=pending
   * Lista peticiones de cambio de turno. Admin ve todas; miembro ve solo las
   * suyas (BE filtra por permisos).
   */
  relayTurnRequestsList: async (
    id: string,
    status?: 'pending' | 'approved' | 'rejected'
  ): Promise<{ data: { turn_requests: TurnRequest[] } }> => {
    const response = await apiClient.get(`/api/v1/relays/${id}/turn-requests`, {
      params: status ? { status } : undefined,
    });
    return {
      data: unwrapRelayEnvelope<{ turn_requests: TurnRequest[] }>(response.data),
    };
  },

  /**
   * POST /api/v1/relays/:id/turn-requests
   * Miembro solicita cambiar a una posición concreta (con motivo).
   */
  relayTurnRequestCreate: async (
    id: string,
    body: { requested_position: number; reason: string }
  ): Promise<{
    data: {
      turn_request_id: string;
      requested_position: number;
      status: 'pending';
    };
  }> => {
    const response = await apiClient.post(`/api/v1/relays/${id}/turn-requests`, body);
    return { data: unwrapRelayEnvelope(response.data) };
  },

  /**
   * POST /api/v1/relays/:id/turn-requests/:trid/decide
   * Admin aprueba/rechaza. Si aprueba, BE aplica swap automáticamente.
   *
   * BE espera `decision: 'approved' | 'rejected'` (gerundios). La API expone
   * `'approve' | 'reject'` (infinitivos) por UX y traduce internamente.
   */
  relayTurnRequestDecide: async (
    id: string,
    trid: string,
    body: { decision: 'approve' | 'reject'; note?: string }
  ): Promise<{
    data: {
      turn_request_id: string;
      status: 'approved' | 'rejected';
    };
  }> => {
    const wireBody = {
      decision: body.decision === 'approve' ? 'approved' : 'rejected',
      note: body.note,
    };
    const response = await apiClient.post(
      `/api/v1/relays/${id}/turn-requests/${trid}/decide`,
      wireBody
    );
    return { data: unwrapRelayEnvelope(response.data) };
  },

  // ============================================================================
  // RELAYS — Member admin actions (F2.4 — TANDA_API_REFERENCE.md § 5)
  // ============================================================================

  /**
   * POST /api/v1/relays/:id/members/:mid/assign-turn
   * Admin asigna directamente una posición a un miembro. Si la posición
   * destino ya estaba ocupada, BE hace swap automático.
   */
  relayMemberAssignTurn: async (
    id: string,
    memberId: string,
    body: { position: number; reason: string }
  ): Promise<{
    data: {
      message?: string;
      relay_id: string;
      member_id: string;
      previous_position: number;
      new_position: number;
      displaced_member_id: string | null;
      displaced_member_new_position: number | null;
    };
  }> => {
    const response = await apiClient.post(
      `/api/v1/relays/${id}/members/${memberId}/assign-turn`,
      body
    );
    return { data: unwrapRelayEnvelope(response.data) };
  },

  /**
   * DELETE /api/v1/relays/:id/members/:mid (expel)
   * Admin expulsa miembro de tanda activa por incumplimiento. Razón
   * obligatoria (auditable). Diferente del legacy `/participants/:id`
   * (soft delete en draft); este sirve en estado active.
   */
  relayMemberExpel: async (
    id: string,
    memberId: string,
    reason: string
  ): Promise<{
    data: {
      message?: string;
      relay_id: string;
      member_id: string;
      status: 'expelled';
      previous_status: string;
      previous_position: number;
    };
  }> => {
    const response = await apiClient.delete(`/api/v1/relays/${id}/members/${memberId}`, {
      data: { reason },
    });
    return { data: unwrapRelayEnvelope(response.data) };
  },

  // ============================================================================
  // RELAYS — Recalculate (F2.5 — TANDA_API_REFERENCE.md § 2)
  // ============================================================================

  /**
   * POST /api/v1/relays/:id/recalculate
   * Admin recalcula fechas/montos cuando la tanda no se llenó. Dos modos:
   *  - `adjust_amount`: mantener pot total, aporte sube.
   *  - `adjust_persons`: mantener aporte, reducir target.
   *
   * BE espera `mode: 'keep_total_pot' | 'keep_contribution'`. La API expone
   * los modos FE-friendly y traduce internamente:
   *   adjust_amount  → keep_total_pot
   *   adjust_persons → keep_contribution
   */
  relayRecalculate: async (
    id: string,
    body: { mode: RelayRecalculateMode; new_start_date?: string }
  ): Promise<{
    data: {
      new_total_members: number;
      new_contribution_amount_cents: number;
      new_pot_amount_cents: number;
      mode: 'keep_total_pot' | 'keep_contribution';
      message?: string;
    };
  }> => {
    const wireBody = {
      mode: body.mode === 'adjust_amount' ? 'keep_total_pot' : 'keep_contribution',
      ...(body.new_start_date ? { new_start_date: body.new_start_date } : {}),
    };
    const response = await apiClient.post(`/api/v1/relays/${id}/recalculate`, wireBody);
    return { data: unwrapRelayEnvelope(response.data) };
  },

  // ============================================================================
  // TANDAS — Globals (F2.6 — TANDA_API_REFERENCE.md § 8)
  // ============================================================================

  /**
   * GET /api/v1/tanda/business-rules
   * Reglas de negocio vigentes (FE las usa para validar formularios con
   * valores frescos en vez de hardcodear).
   */
  tandaBusinessRules: async (): Promise<{
    data: { rules: TandaBusinessRules; snapshotted_at: string };
  }> => {
    const response = await apiClient.get('/api/v1/tanda/business-rules');
    return { data: unwrapRelayEnvelope(response.data) };
  },

  /**
   * GET /api/v1/tandas/my-status
   * Status GLOBAL del usuario (cuántas como admin/participant, caps,
   * pending invitations, pending turn requests).
   *
   * BE expone esta ruta como COLLECTION (sin :id). Antes era per-tanda
   * (member route) — refactor 2026-05-28 PR pay-service#354 la convirtió
   * en global porque el FE necesitaba el summary del current_user, no
   * por tanda específica. Para status per-tanda usa GET /relays/:id.
   *
   * El parámetro tandaId quedó deprecated en 2026-05-29: cualquier valor
   * se ignora silenciosamente (la URL siempre es la global). Eliminar
   * de los callers en una iteración futura.
   */
  tandaMyStatus: async (_tandaId?: string): Promise<{ data: TandaMyStatus }> => {
    const response = await apiClient.get('/api/v1/tandas/my-status');
    return { data: unwrapRelayEnvelope(response.data) };
  },

  /**
   * POST /api/v1/tandas/:id/accept-agreement
   * Aceptar contrato como paso explícito antes de aportar.
   *
   * BE espera tanda_id en URL (member route), no en body. La API expone
   * `tanda_id` en el input por compat y lo mueve al path internamente.
   */
  tandaAcceptAgreement: async (body: {
    tanda_id: string;
    agreement_version: string;
  }): Promise<{
    data: { tanda_id: string; agreement_accepted_at: string; agreement_version: string };
  }> => {
    const response = await apiClient.post(
      `/api/v1/tandas/${encodeURIComponent(body.tanda_id)}/accept-agreement`,
      { agreement_version: body.agreement_version }
    );
    return { data: unwrapRelayEnvelope(response.data) };
  },

  /**
   * POST /api/v1/tandas/:id/set-payment-accounts
   * Configurar cuentas source (de donde sale aporte) y destination (donde
   * recibe pot) antes de activar.
   *
   * BE espera tanda_id en URL (member route), no en body. La API expone
   * `tanda_id` en el input por compat y lo mueve al path internamente.
   */
  tandaSetPaymentAccounts: async (body: {
    tanda_id: string;
    source_account_id: string;
    destination_account_id: string;
  }): Promise<{
    data: {
      tanda_id: string;
      source_account_id: string;
      destination_account_id: string;
      ready_to_start: boolean;
    };
  }> => {
    const response = await apiClient.post(
      `/api/v1/tandas/${encodeURIComponent(body.tanda_id)}/set-payment-accounts`,
      {
        source_account_id: body.source_account_id,
        destination_account_id: body.destination_account_id,
      }
    );
    return { data: unwrapRelayEnvelope(response.data) };
  },

  // ============================================================================
  // RELAYS — Join genérico por folio (F2.7 — TANDA_API_REFERENCE.md § 3)
  // ============================================================================

  /**
   * POST /api/v1/relays/:id/join
   * Sistema GENÉRICO: unirse con o sin token público (folio compartido).
   * Diferente del `accept` (DIRIGIDO por email) — ambos coexisten según doc.
   */
  relayJoinByFolio: async (
    id: string,
    token?: string
  ): Promise<{
    data: {
      message?: string;
      user_relay: {
        relay_id: string;
        user_id: string;
        role: string;
        position: number;
      };
    };
  }> => {
    const response = await apiClient.post(`/api/v1/relays/${id}/join`, token ? { token } : {});
    return { data: unwrapRelayEnvelope(response.data) };
  },

  // ============================================================================
  // Smart Email & Phone Validation (Onboarding)
  // ============================================================================

  /**
   * Submit email for validation in onboarding session
   * POST /api/v1/onboarding/sessions/{session_id}/email/submit
   *
   * @param sessionId - Onboarding session ID
   * @param data - Email to validate
   * @todo Verify endpoint path and response format with backend
   */
  submitEmail: async (
    sessionId: string,
    data: {
      email: string;
    }
  ): Promise<{
    next_step?: { block: string; step: string };
    verification_sent: boolean;
    expires_at?: string;
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl(`sessions/${sessionId}/email/submit`),
      data
    );
    return response.data;
  },

  /**
   * Verify email OTP in onboarding session
   * POST /api/v1/onboarding/sessions/{session_id}/email/verify
   *
   * @param sessionId - Onboarding session ID
   * @param data - OTP code
   * @todo Verify endpoint path and response format with backend
   */
  verifyEmailOtp: async (
    sessionId: string,
    data: {
      code: string;
    }
  ): Promise<{
    verified: boolean;
    next_step?: { block: string; step: string };
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl(`sessions/${sessionId}/email/verify`),
      data
    );
    return response.data;
  },

  /**
   * Submit phone for validation in onboarding session
   * POST /api/v1/onboarding/sessions/{session_id}/phone/submit
   *
   * @param sessionId - Onboarding session ID
   * @param data - Phone number and channel
   * @todo Verify endpoint path and response format with backend
   */
  submitPhone: async (
    sessionId: string,
    data: {
      phone: string;
      channel: 'sms' | 'whatsapp';
    }
  ): Promise<{
    next_step?: { block: string; step: string };
    verification_sent: boolean;
    masked_phone?: string;
    expires_at?: string;
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl(`sessions/${sessionId}/phone/submit`),
      data
    );
    return response.data;
  },

  /**
   * Verify phone OTP in onboarding session
   * POST /api/v1/onboarding/sessions/{session_id}/phone/verify
   *
   * @param sessionId - Onboarding session ID
   * @param data - OTP code
   * @todo Verify endpoint path and response format with backend
   */
  verifyPhoneOtp: async (
    sessionId: string,
    data: {
      code: string;
    }
  ): Promise<{
    verified: boolean;
    next_step?: { block: string; step: string };
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl(`sessions/${sessionId}/phone/verify`),
      data
    );
    return response.data;
  },

  /**
   * Resend phone OTP in onboarding session
   * POST /api/v1/onboarding/sessions/{session_id}/phone/resend
   *
   * @param sessionId - Onboarding session ID
   * @param data - Channel to resend to
   * @todo Verify endpoint path and response format with backend
   */
  resendPhone: async (
    sessionId: string,
    data: {
      channel: 'sms' | 'whatsapp';
    }
  ): Promise<{
    verification_sent: boolean;
    masked_phone?: string;
    expires_at?: string;
  }> => {
    const response = await onboardingClient.post(
      buildOnboardingUrl(`sessions/${sessionId}/phone/resend`),
      data
    );
    return response.data;
  },

  // ============================================================================
  // Account Management
  // ============================================================================

  /**
   * Update user password
   * PATCH /api/v1/me/password
   *
   * @param data - Current and new password
   */
  updatePassword: async (data: {
    current_password: string;
    new_password: string;
    new_password_confirmation: string;
  }): Promise<{
    message: string;
  }> => {
    const response = await apiClient.patch('/api/v1/me/password', data);
    return response.data;
  },
};

// ============================================================================
// BACKOFFICE API (Panel admin - admin.tankef.com)
// ============================================================================

function parseNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  return 0;
}

function deriveDiscrepancyType(item: Record<string, unknown>): string {
  const sourceRecordId = item.source_record_id ?? item.sourceRecordId;
  const targetRecordId = item.target_record_id ?? item.targetRecordId;
  const difference = Math.abs(parseNumber(item.difference_cents ?? item.differenceCents));

  if (sourceRecordId && !targetRecordId) return 'missing_external';
  if (targetRecordId && !sourceRecordId) return 'missing_internal';
  if (difference > 0) return 'amount_mismatch';
  return 'other';
}

function deriveDiscrepancySeverity(
  item: Record<string, unknown>
): 'low' | 'medium' | 'high' | 'critical' {
  const status = String(item.status ?? item.match_status ?? '').toLowerCase();
  const difference = Math.abs(parseNumber(item.difference_cents ?? item.differenceCents));

  if (status === 'matched' || status === 'ignored') return 'low';
  if (difference > 100_000) return 'critical';
  if (difference > 10_000) return 'high';
  if (difference > 1_000) return 'medium';
  return status === 'unmatched' ? 'high' : 'low';
}

function normalizeDiscrepancy(item: Record<string, unknown>) {
  const differenceCents = Math.abs(parseNumber(item.difference_cents ?? item.differenceCents));
  const amountCents = parseNumber(item.amount_cents ?? item.amountCents) || differenceCents;
  const type = String(item.discrepancy_type ?? item.discrepancyType ?? deriveDiscrepancyType(item));
  const severity = deriveDiscrepancySeverity(item);
  const description =
    String(item.description ?? item.notes ?? item.title ?? '').trim() ||
    `${type.replaceAll('_', ' ')} (${severity})`;

  return {
    id: String(item.id),
    type,
    severity,
    description,
    amount_cents: amountCents,
    difference_cents: differenceCents,
    adjustment_type: type,
    created_at: typeof item.created_at === 'string' ? item.created_at : '',
    status: String(item.status ?? item.match_status ?? ''),
  };
}

/**
 * KPIs administrativos por producto (auditoría backoffice jun-2026).
 * Fuente: GET /api/v1/admin/{investments,savings,credits}/kpis (gated admin/staff).
 * Montos en centavos (bigint en BE) — el FE los formatea con Money.fromCents.
 */
export interface ProductKpis {
  investment: {
    totalCount: number;
    activeCount: number;
    maturedCount: number;
    aumCents: number;
    totalPrincipalCents: number;
    byStatus: Record<string, number>;
  };
  savings: {
    totalCount: number;
    activeCount: number;
    aumCents: number;
    totalBalanceCents: number;
    byStatus: Record<string, number>;
  };
  credit: {
    totalCount: number;
    activeCount: number;
    delinquentCount: number;
    totalPrincipalCents: number;
    outstandingPrincipalCents: number;
    byStatus: Record<string, number>;
  };
}

/**
 * Cuenta (wallet/ledger) en la vista admin con CLABE + balances (jun-2026).
 * Fuente: GET /api/v1/admin/accounts (serialize_account). Montos en centavos.
 */
export interface AdminAccountRow {
  id: string;
  accountNumber: string;
  kind: string;
  status: string;
  currencyCode: string;
  partyId: string;
  clabe?: string;
  ledgerBalanceCents: number;
  availableBalanceCents: number;
  holdBalanceCents: number;
}

/**
 * `meta` de la bandeja de alertas.
 *
 * El backend calcula un resumen sobre TODA la cola —no sobre la página— y lo
 * anida en `meta.summary`. El cliente lo descartaba, así que el oficial no
 * tenía forma de ver cuántas alertas vencidas había sin paginar a mano.
 *
 * Nota: la paginación viaja como `page`, no `current_page`.
 */
export interface AlertsMeta extends PaginationMeta {
  page: number;
  summary?: {
    total_open: number;
    critical: number;
    overdue: number;
    by_type: Record<string, number>;
    resolved_today: number;
  };
}

export const backofficeApi = {
  // --- Auth (OAuth 2.0 Password Grant for backoffice) ---
  //
  // tankef-web-app is a confidential OAuth client (requires client_secret).
  // A secret can never reach browser JS safely (verified: Next.js only
  // inlines NEXT_PUBLIC_* into the client bundle, and this package's
  // readPublicEnv does a dynamic process.env[key] lookup webpack can't
  // statically resolve for the client bundle either way). So this calls a
  // same-origin Next.js Route Handler (apps/backoffice/src/app/api/auth)
  // that holds the secret server-side and proxies to /oauth/token — the
  // browser never sees it. Plain `axios.post` (not the oauthClient/apiClient
  // instances, which have baseURL set to the external API) so the relative
  // URL resolves against this app's own origin, and axios.isAxiosError still
  // works for the existing isLoginMFARequired/getBackendErrorMessage checks.
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await axios.post<LoginResponse>('/api/auth/token', {
      username: email,
      password: password,
    });

    const { access_token, refresh_token, expires_in } = response.data;
    // Igual que la web app (clientApi.login): los tokens viven en localStorage
    // vía setStoredTokens. El store NO los persiste (ver useBackofficeAuthStore),
    // así evitamos duplicar/desincronizar el token entre dos lugares.
    setStoredTokens(access_token, refresh_token);
    try {
      const user = await backofficeApi.me();
      return {
        token: access_token,
        refresh_token,
        expires_at: new Date(Date.now() + expires_in * 1000).toISOString(),
        user: user.toJSON(),
      };
    } catch (error) {
      clearStoredTokens();
      throw error;
    }
  },

  logout: async (): Promise<void> => {
    try {
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        // Same-origin proxy — see login's comment above for why.
        await axios.post('/api/auth/revoke', { refresh_token: refreshToken });
      }
    } catch (error) {
      // Log revoke error but don't fail logout — clear tokens anyway
      logger.error('[backofficeApi.logout] Token revocation failed:', error);
    } finally {
      // Igual que web: limpia los tokens de localStorage. El store se entera al
      // rehidratar (getStoredToken/getStoredRefreshToken vacíos → resetea sesión).
      clearStoredTokens();
    }
  },

  // --- Me (Current User) ---
  me: async (): Promise<User> => {
    const response = await apiClient.get('/api/v1/admin/me');
    // El backend responde con el sobre { success, data: {...} } (render_success).
    // Sin desenvolver, User.fromApi leía role/email/permissions del nivel
    // superior → undefined → can() daba false en todo → el backoffice solo
    // mostraba el Dashboard. Desenvolvemos .data (con fallback por si algún
    // entorno responde plano), igual que los endpoints de lista vecinos.
    return User.fromApi(response.data.data ?? response.data);
  },

  // --- Dashboard ---
  dashboard: async (): Promise<DashboardMetrics> => {
    const response = await apiClient.get('/api/v1/admin/dashboard');
    // El BE envuelve en { success, data: {...} } (render_success); desenvolvemos
    // .data como los endpoints vecinos (me/productKpis). Sin esto los KPIs salían
    // undefined porque se leían del nivel del sobre.
    const body = response.data as { data?: Record<string, unknown> } & Record<string, unknown>;
    const data = (body.data ?? body) as Record<string, unknown>;
    return {
      totalParties: data.total_parties as number,
      activeParties: data.active_parties as number,
      todayVolume: Money.fromCents(data.today_volume_cents as number),
      todayTransfers: data.today_transfers as number,
      pendingAlerts: data.pending_alerts as number,
      activeNotices: data.active_notices as number,
      monthlyGrowth: data.monthly_growth as number,
    };
  },

  // --- Product KPIs (Inversión / Ahorro / Crédito) ---
  productKpis: async (): Promise<ProductKpis> => {
    const unwrap = (r: { data: unknown }): Record<string, unknown> => {
      const body = r.data as { data?: unknown };
      return (body?.data ?? r.data) as Record<string, unknown>;
    };
    const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
    const byStatus = (v: unknown): Record<string, number> =>
      v && typeof v === 'object' ? (v as Record<string, number>) : {};

    const [inv, sav, cred] = await Promise.all([
      apiClient.get('/api/v1/admin/investments/kpis'),
      apiClient.get('/api/v1/admin/savings/kpis'),
      apiClient.get('/api/v1/admin/credits/kpis'),
    ]);
    const i = unwrap(inv);
    const s = unwrap(sav);
    const c = unwrap(cred);
    return {
      investment: {
        totalCount: num(i.total_count),
        activeCount: num(i.active_count),
        maturedCount: num(i.matured_count),
        aumCents: num(i.aum_cents),
        totalPrincipalCents: num(i.total_principal_cents),
        byStatus: byStatus(i.by_status),
      },
      savings: {
        totalCount: num(s.total_count),
        activeCount: num(s.active_count),
        aumCents: num(s.aum_cents),
        totalBalanceCents: num(s.total_balance_cents),
        byStatus: byStatus(s.by_status),
      },
      credit: {
        totalCount: num(c.total_count),
        activeCount: num(c.active_count),
        delinquentCount: num(c.delinquent_count),
        totalPrincipalCents: num(c.total_principal_cents),
        outstandingPrincipalCents: num(c.outstanding_principal_cents),
        byStatus: byStatus(c.by_status),
      },
    };
  },

  // --- Parties ---
  parties: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    kyc_level?: number;
  }): Promise<{ parties: Party[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/admin/parties',
      { params }
    );
    return {
      parties: response.data.data.map(Party.fromApi),
      meta: response.data.meta,
    };
  },

  party: async (id: string): Promise<Party> => {
    const response = await apiClient.get(`/api/v1/admin/parties/${id}`);
    return Party.fromApi(response.data.data ?? response.data);
  },

  /**
   * Empresas (personas morales). Endpoint distinto de `parties`, que sirve
   * personas físicas: una empresa no tiene nombre ni CURP, tiene razón social,
   * RFC y un expediente KYB con su propio ciclo de revisión.
   *
   * El backend expone esto desde la hoja BO-PM 02 y hasta ahora no tenía
   * consumidor: la cola de empresas esperando revisión no se veía en ningún
   * lado.
   */
  companies: async (
    params?: AdminCompanyFilters
  ): Promise<{ companies: AdminCompany[]; meta?: PaginationMeta }> => {
    const response = await apiClient.get<{ data: AdminCompany[]; meta?: PaginationMeta }>(
      '/api/v1/admin/companies',
      { params }
    );
    return {
      companies: response.data.data ?? [],
      meta: response.data.meta,
    };
  },

  company: async (id: string): Promise<AdminCompanyDetail> => {
    const response = await apiClient.get(`/api/v1/admin/companies/${id}`);
    return (response.data.data ?? response.data) as AdminCompanyDetail;
  },

  /**
   * Datos fiscales — hoja BO-PM 02.
   *
   * Va por su propia ruta y su propio permiso (`edit_fiscal`), no por el
   * `update` general: cambiar el RFC o la escritura de una empresa ya dada de
   * alta no es lo mismo que corregirle el teléfono.
   */
  updateCompanyFiscal: async (
    id: string,
    data: Partial<{
      legal_name: string;
      rfc: string;
      tax_regime_code: string;
      incorporation_date: string;
      deed_number: string;
      notary_number: string;
      notary_state: string;
      fiscal_address: Record<string, string>;
    }>
  ): Promise<AdminCompanyDetail> => {
    const response = await apiClient.patch(`/api/v1/admin/companies/${id}/fiscal`, data);
    return (response.data.data ?? response.data) as AdminCompanyDetail;
  },

  companyDocuments: async (id: string): Promise<AdminCompanyDocument[]> => {
    const response = await apiClient.get(`/api/v1/admin/companies/${id}/documents`);
    // El backend envuelve la lista en `data.documents`, no en `data`.
    return (response.data?.data?.documents ?? []) as AdminCompanyDocument[];
  },

  /**
   * Carga manual de un documento del expediente.
   *
   * Multipart, no JSON: el backend recibe `file` y lo adjunta a la sesión de
   * onboarding de la empresa. Si la empresa no tiene sesión responde
   * `PM_NO_ONBOARDING_SESSION` — no hay dónde colgarlo.
   */
  uploadCompanyDocument: async (
    id: string,
    documentType: string,
    file: File
  ): Promise<AdminCompanyDocument> => {
    const form = new FormData();
    form.append('document_type', documentType);
    form.append('file', file);
    const response = await apiClient.post(`/api/v1/admin/companies/${id}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return (response.data.data ?? response.data) as AdminCompanyDocument;
  },

  companyOwners: async (id: string): Promise<AdminBeneficialOwner[]> => {
    const response = await apiClient.get(`/api/v1/admin/companies/${id}/owners`);
    const body = response.data.data ?? response.data;
    return (body?.beneficial_owners ?? body ?? []) as AdminBeneficialOwner[];
  },

  /**
   * Reemplaza el set completo de beneficiarios controladores.
   *
   * El recurso es singular a propósito: la declaración de UBO es un documento
   * único de la empresa, no una colección de items direccionables. Mandar la
   * lista parcial borra a los que falten.
   */
  updateCompanyOwners: async (
    id: string,
    owners: AdminBeneficialOwner[]
  ): Promise<AdminBeneficialOwner[]> => {
    const response = await apiClient.put(`/api/v1/admin/companies/${id}/owners`, {
      beneficial_owners: owners,
    });
    const body = response.data.data ?? response.data;
    return (body?.beneficial_owners ?? owners) as AdminBeneficialOwner[];
  },

  /**
   * Ciclo de vida del expediente — hoja BO-PM 03.
   *
   * Una sola función y no diez: las diez rutas comparten forma (POST al
   * miembro, cuerpo opcional, misma traducción de error) y el permiso lo
   * decide el backend. Diez wrappers idénticos sólo dan diez lugares donde
   * olvidar el `data.data`.
   *
   * El nombre de la acción NO es el segmento de la URL: el backend usa
   * kebab-case en la ruta (`submit-for-review`) y snake_case en
   * `granted_actions` (`submit_for_review`). Ese mapeo vive aquí para que la
   * pantalla pueda pasar directo lo que el backend le anunció.
   */
  companyLifecycleAction: async (
    id: string,
    action: CompanyLifecycleAction,
    payload: CompanyLifecyclePayload = {}
  ): Promise<Record<string, unknown>> => {
    const paths: Record<CompanyLifecycleAction, string> = {
      submit_for_review: 'submit-for-review',
      assign_review: 'assign-review',
      recommend_approval: 'recommend-approval',
      approve: 'approve',
      reject: 'reject',
      suspend: 'suspend',
      unsuspend: 'unsuspend',
      deactivate: 'deactivate',
      reactivate: 'reactivate',
      run_screening: 'run-screening',
    };

    const response = await apiClient.post(
      `/api/v1/admin/companies/${id}/${paths[action]}`,
      payload
    );
    return (response.data.data ?? response.data) as Record<string, unknown>;
  },

  companyInvitations: async (
    id: string,
    includeResolved = false
  ): Promise<AdminCompanyInvitation[]> => {
    const response = await apiClient.get(`/api/v1/admin/companies/${id}/invitations`, {
      params: includeResolved ? { include_resolved: true } : undefined,
    });
    const body = response.data.data ?? response.data;
    return (body?.invitations ?? []) as AdminCompanyInvitation[];
  },

  /**
   * Invita a un usuario a la cuenta de la empresa.
   *
   * Exige que la empresa ya tenga cuenta provisionada: sin ella el backend
   * responde `PM_NO_ACCOUNT` y no hay a qué invitar. El token en claro nunca
   * viaja en la respuesta — el correo lo manda el backend.
   */
  inviteCompanyUser: async (
    id: string,
    data: { email: string; role: CompanyInvitationRole }
  ): Promise<Record<string, unknown>> => {
    const response = await apiClient.post(`/api/v1/admin/companies/${id}/invitations`, data);
    return (response.data.data ?? response.data) as Record<string, unknown>;
  },

  revokeCompanyInvitation: async (
    id: string,
    invitationId: string,
    reason?: string
  ): Promise<Record<string, unknown>> => {
    const response = await apiClient.delete(
      `/api/v1/admin/companies/${id}/invitations/${invitationId}`,
      { data: reason ? { reason } : undefined }
    );
    return (response.data.data ?? response.data) as Record<string, unknown>;
  },

  updateParty: async (
    id: string,
    data: Partial<{
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      kyc_level: number;
    }>
  ): Promise<Party> => {
    const response = await apiClient.patch(`/api/v1/admin/parties/${id}`, data);
    return Party.fromApi(response.data.data ?? response.data);
  },

  blockParty: async (id: string, reason: string): Promise<Party> => {
    const response = await apiClient.post(`/api/v1/admin/parties/${id}/block`, { reason });
    return Party.fromApi(response.data.data ?? response.data);
  },

  unblockParty: async (id: string): Promise<Party> => {
    const response = await apiClient.post(`/api/v1/admin/parties/${id}/unblock`, {});
    return Party.fromApi(response.data.data ?? response.data);
  },

  // --- Accounts (Admin view) — CLABE + balances ---
  adminAccounts: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    kind?: string;
    party_id?: string;
    currency?: string;
  }): Promise<{ accounts: AdminAccountRow[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/admin/accounts',
      { params }
    );
    const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);
    return {
      accounts: response.data.data.map(a => {
        const balances = (a.balances ?? {}) as Record<string, unknown>;
        return {
          id: String(a.id ?? ''),
          accountNumber: String(a.account_number ?? ''),
          kind: String(a.kind ?? ''),
          status: String(a.status ?? ''),
          currencyCode: String(a.currency_code ?? ''),
          partyId: String(a.party_id ?? ''),
          clabe: (a.clabe ?? undefined) as string | undefined,
          ledgerBalanceCents: num(balances.ledger_cents),
          availableBalanceCents: num(balances.available_cents),
          holdBalanceCents: num(balances.hold_cents),
        };
      }),
      meta: response.data.meta,
    };
  },

  // --- Transfers (Admin view) ---
  transfers: async (params?: {
    page?: number;
    per_page?: number;
    party_id?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{ transfers: Transfer[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/admin/transfers',
      { params }
    );
    return {
      transfers: response.data.data.map(Transfer.fromApi),
      meta: response.data.meta,
    };
  },

  approveTransfer: async (id: string): Promise<Transfer> => {
    const response = await apiClient.post(`/api/v1/admin/transfers/${id}/approve`, {});
    return Transfer.fromApi(response.data.data ?? response.data);
  },

  rejectTransfer: async (id: string, reason: string): Promise<Transfer> => {
    const response = await apiClient.post(`/api/v1/admin/transfers/${id}/reject`, { reason });
    return Transfer.fromApi(response.data.data ?? response.data);
  },

  // --- Alerts ---
  alerts: async (params?: {
    page?: number;
    per_page?: number;
    status?: AlertStatus;
    severity?: string;
    type?: string;
    party_id?: string;
    /** Solo alertas cuya fecha compromiso ya venció. */
    overdue?: boolean;
  }): Promise<{ alerts: Alert[]; meta: AlertsMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/admin/compliance-alerts',
      { params }
    );
    return {
      alerts: response.data.data.map(Alert.fromApi),
      meta: response.data.meta as AlertsMeta,
    };
  },

  alert: async (id: string): Promise<Alert> => {
    const response = await apiClient.get(`/api/v1/admin/compliance-alerts/${id}`);
    return Alert.fromApi(response.data.data ?? response.data);
  },

  updateAlertStatus: async (id: string, status: AlertStatus, notes?: string): Promise<Alert> => {
    const response = await apiClient.patch(`/api/v1/admin/compliance-alerts/${id}`, {
      status,
      resolution_notes: notes,
    });
    return Alert.fromApi(response.data.data ?? response.data);
  },

  assignAlert: async (id: string, userId: string): Promise<Alert> => {
    const response = await apiClient.post(`/api/v1/admin/compliance-alerts/${id}/assign`, {
      user_id: userId,
    });
    return Alert.fromApi(response.data.data ?? response.data);
  },

  /**
   * El backend exige `reason`, no `notes`. Enviando `notes` respondía siempre
   * 422 "Se requiere razón de escalamiento": la acción nunca pudo funcionar.
   */
  escalateAlert: async (id: string, reason: string, level?: string): Promise<Alert> => {
    const response = await apiClient.post(`/api/v1/admin/compliance-alerts/${id}/escalate`, {
      reason,
      ...(level ? { level } : {}),
    });
    return Alert.fromApi(response.data.data ?? response.data);
  },

  /**
   * Cierra la alerta. `resolution_type` debe ser uno de los que acepta el
   * backend (ver RESOLUTION_TYPES en IdentityComplianceAlert); cualquier otro
   * se normaliza silenciosamente a `no_action_required`, así que conviene
   * mandar siempre uno válido y no dejar que el servidor adivine.
   */
  resolveAlert: async (
    id: string,
    resolutionNotes: string,
    resolutionType: string
  ): Promise<Alert> => {
    const response = await apiClient.post(`/api/v1/admin/compliance-alerts/${id}/resolve`, {
      resolution_notes: resolutionNotes,
      resolution_type: resolutionType,
    });
    return Alert.fromApi(response.data.data ?? response.data);
  },

  /**
   * Reporta la alerta a la UIF. Requiere cumplimiento senior en el backend
   * (permiso `bo_compliance.approve_uif_report`): un 403 aquí significa que el
   * perfil no puede firmar el reporte, no que la alerta esté mal.
   */
  reportAlertToUif: async (id: string, notes: string, reportType: string): Promise<Alert> => {
    const response = await apiClient.post(`/api/v1/admin/compliance-alerts/${id}/report-to-uif`, {
      notes,
      report_type: reportType,
    });
    return Alert.fromApi(response.data.data ?? response.data);
  },

  // --- Notices ---
  notices: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    type?: string;
  }): Promise<{ notices: SystemNotice[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/admin/notices',
      { params }
    );
    return {
      notices: response.data.data.map(SystemNotice.fromApi),
      meta: response.data.meta,
    };
  },

  createNotice: async (data: {
    type: string;
    severity: string;
    target: string;
    title: string;
    message: string;
    dismissible: boolean;
    starts_at: string;
    ends_at?: string;
    maintenance_starts_at?: string;
    maintenance_ends_at?: string;
    link?: string;
    link_text?: string;
  }): Promise<SystemNotice> => {
    const response = await apiClient.post('/api/v1/admin/notices', data);
    return SystemNotice.fromApi(response.data.data ?? response.data);
  },

  updateNotice: async (
    id: string,
    data: Partial<{
      title: string;
      message: string;
      severity: string;
      starts_at: string;
      ends_at: string;
    }>
  ): Promise<SystemNotice> => {
    const response = await apiClient.patch(`/api/v1/admin/notices/${id}`, data);
    return SystemNotice.fromApi(response.data.data ?? response.data);
  },

  activateNotice: async (id: string): Promise<SystemNotice> => {
    const response = await apiClient.post(`/api/v1/admin/notices/${id}/activate`, {});
    return SystemNotice.fromApi(response.data.data ?? response.data);
  },

  deactivateNotice: async (id: string): Promise<SystemNotice> => {
    const response = await apiClient.post(`/api/v1/admin/notices/${id}/deactivate`, {});
    return SystemNotice.fromApi(response.data.data ?? response.data);
  },

  deleteNotice: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/notices/${id}`);
  },

  // --- Audit Log ---
  auditEvents: async (params?: {
    page?: number;
    per_page?: number;
    category?: AuditCategory;
    /** Ver `useAuditEvents`: `action` es nombre reservado de Rails. */
    audit_action?: string;
    actor_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{ events: AuditEvent[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/admin/audit-logs',
      { params }
    );
    return {
      events: response.data.data.map(AuditEvent.fromApi),
      meta: response.data.meta,
    };
  },

  // --- Users (Admin management) ---
  users: async (params?: {
    page?: number;
    per_page?: number;
    role?: string;
    search?: string;
  }): Promise<{ users: User[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(
      '/api/v1/admin/users',
      { params }
    );
    return {
      users: response.data.data.map(User.fromApi),
      meta: response.data.meta,
    };
  },

  createUser: async (data: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    password: string;
  }): Promise<User> => {
    const response = await apiClient.post('/api/v1/admin/users', data);
    return User.fromApi(response.data.data ?? response.data);
  },

  updateUser: async (
    id: string,
    data: Partial<{
      first_name: string;
      last_name: string;
      role: string;
      is_active: boolean;
    }>
  ): Promise<User> => {
    const response = await apiClient.patch(`/api/v1/admin/users/${id}`, data);
    return User.fromApi(response.data.data ?? response.data);
  },

  deactivateUser: async (id: string, reason = 'Desactivado desde el backoffice'): Promise<User> => {
    // El BE no expone /deactivate; la baja de un usuario es /suspend (exige
    // motivo). La reactivación es /activate. Firma compatible: reason opcional.
    const response = await apiClient.post(`/api/v1/admin/users/${id}/suspend`, { reason });
    return User.fromApi(response.data.data ?? response.data);
  },

  // --- Auth MFA ---
  //
  // /api/v1/admin/auth/mfa never existed on the backend (dead endpoint — this
  // would 404). The real MFA-continuation path is the same /oauth/token
  // password grant, resubmitted with mfa_token + mfa_code instead of
  // username/password (see BE Oauth::TokensController#handle_password_grant
  // -> restore_mfa_challenge_context). Same same-origin proxy as login(),
  // same reason (client_secret must stay server-side).
  loginWithMFA: async (mfaToken: string, mfaCode: string): Promise<AuthResponse> => {
    const response = await axios.post<LoginResponse>('/api/auth/token', {
      mfa_token: mfaToken,
      mfa_code: mfaCode,
    });

    const { access_token, refresh_token, expires_in } = response.data;
    setStoredTokens(access_token, refresh_token);
    try {
      const user = await backofficeApi.me();
      return {
        token: access_token,
        refresh_token,
        expires_at: new Date(Date.now() + expires_in * 1000).toISOString(),
        user: user.toJSON(),
      };
    } catch (error) {
      clearStoredTokens();
      throw error;
    }
  },

  // --- Feature Flags ---
  featureFlags: async (): Promise<Record<string, boolean>> => {
    const response = await apiClient.get('/api/v1/admin/feature_flags');
    return response.data;
  },

  updateFeatureFlag: async (key: string, enabled: boolean): Promise<void> => {
    await apiClient.patch(`/api/v1/admin/feature_flags/${key}`, { enabled });
  },

  // --- Onboarding (Backoffice Admin) ---
  adminOnboardingSessions: async (filters?: Record<string, unknown>) => {
    // BE: namespace plano con guion (/admin/onboarding-sessions), no /onboarding/sessions.
    const response = await apiClient.get('/api/v1/admin/onboarding-sessions', {
      params: filters,
    });
    return response.data;
  },

  adminOnboardingAction: async (
    sessionId: string,
    action: string,
    data: Record<string, unknown>
  ) => {
    // action ∈ {unlock, block, approve-kyc, reject-kyc, approve-pep, reject-pep,
    // clear-risk, reject-risk, approve-data-review, ...} → rutas member del BE
    // bajo /admin/onboarding-sessions/:id/:action (guion, no /onboarding/sessions).
    const response = await apiClient.post(
      `/api/v1/admin/onboarding-sessions/${sessionId}/${action}`,
      data
    );
    return response.data;
  },

  adminSendClaimInvite: async (sessionId: string, stepUpToken?: string) => {
    // BE: POST /api/v1/admin/onboarding-sessions/:id/send-claim-invite (RFC
    // 0020). Guarded by step-up MFA on the backend — first call with no
    // token gets a 403 (see useAdminStepUp), retry with the resolved token.
    const response = await apiClient.post(
      `/api/v1/admin/onboarding-sessions/${sessionId}/send-claim-invite`,
      {},
      stepUpToken ? { headers: { 'X-Step-Up-Token': stepUpToken } } : undefined
    );
    return response.data as { sent_at: string; remaining_resends: number };
  },

  // --- Reconciliation ---
  reconciliationDashboard: async () => {
    const response = await apiClient.get('/api/v1/admin/reconciliation/dashboard');
    return response.data;
  },

  reconciliationReports: async (filters?: Record<string, unknown>) => {
    const response = await apiClient.get('/api/v1/admin/reconciliation/reports', {
      params: filters,
    });
    return response.data;
  },

  reconciliationReport: async (reportId: string) => {
    const response = await apiClient.get(`/api/v1/admin/reconciliation/reports/${reportId}`);
    return response.data;
  },

  runReconciliation: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/api/v1/admin/reconciliation/run', data);
    return response.data;
  },

  discrepancies: async (filters?: Record<string, unknown>) => {
    const response = await apiClient.get('/api/v1/admin/discrepancies', {
      params: filters,
    });
    const data = response.data as {
      data?: Record<string, unknown>[];
      meta?: PaginationMeta;
    };

    return {
      discrepancies: (data.data ?? []).map(normalizeDiscrepancy),
      meta: data.meta,
    };
  },

  resolveDiscrepancy: async (discrepancyId: string, data: Record<string, unknown>) => {
    const response = await apiClient.post(
      `/api/v1/admin/discrepancies/${discrepancyId}/adjustment`,
      data
    );
    return response.data;
  },

  // --- Reports ---
  reportSchedules: async () =>
    reportScheduleListResponseSchema.parse(
      (await apiClient.get('/api/v1/admin/report-generation-schedules')).data
    ),
  createReportSchedule: async (input: ReportScheduleInput) => {
    const schedule = reportScheduleInputSchema.parse(input);
    return reportScheduleResponseSchema.parse(
      (await apiClient.post('/api/v1/admin/report-generation-schedules', { schedule })).data
    );
  },
  updateReportSchedule: async (id: string, status: 'active' | 'paused') =>
    reportScheduleResponseSchema.parse(
      (
        await apiClient.patch(`/api/v1/admin/report-generation-schedules/${id}`, {
          schedule: { status },
        })
      ).data
    ),
  removeReportSchedule: async (id: string) =>
    reportScheduleResponseSchema.parse(
      (await apiClient.delete(`/api/v1/admin/report-generation-schedules/${id}`)).data
    ),
  reports: async (filters?: { category?: string; page?: number; per_page?: number }) => {
    const response = await apiClient.get('/api/v1/admin/reports', { params: filters });
    return reportCatalogResponseSchema.parse(response.data);
  },
  report: async (id: string) =>
    reportDetailResponseSchema.parse((await apiClient.get(`/api/v1/admin/reports/${id}`)).data),
  executeReport: async (
    id: string,
    report: { parameters: ReportParameterValues; format: ReportFormat },
    key: string
  ) => {
    const response = await apiClient.post(
      `/api/v1/admin/reports/${id}/execute`,
      { report },
      { headers: { 'Idempotency-Key': key } }
    );
    return reportQueuedResponseSchema.parse(response.data);
  },
  reportExecutions: async (filters?: {
    page?: number;
    per_page?: number;
    status?: string;
    report_definition_id?: string;
  }) => {
    const response = await apiClient.get('/api/v1/admin/report-executions', { params: filters });
    return reportHistoryResponseSchema.parse(response.data);
  },
  reportExecution: async (id: string) =>
    reportExecutionResponseSchema.parse(
      (await apiClient.get(`/api/v1/admin/report-executions/${id}`)).data
    ),
  downloadReportOutput: async (id: string, outputId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/api/v1/admin/report-executions/${id}/outputs/${outputId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },
  exportAuditEvents: async (filters?: Record<string, unknown>) => {
    const response = await apiClient.get('/api/v1/admin/reports/audit-events', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },
};

// ============================================================================
// JAAK IDENTITY VERIFICATION API
// ============================================================================
//
// SECURITY NOTE — DO NOT EXPOSE THE JAAK API KEY IN THE CLIENT BUNDLE.
//
// `VITE_*` env vars are compiled into the public JavaScript bundle by Vite and
// are therefore readable by any visitor. The production KYC flow MUST proxy
// JAAK calls through the Tankef backend, which holds `JAAK_API_KEY`
// server-side and authenticates the user with the platform access token.
//
// This client lives here only to serve legacy/sandbox paths that may run
// against a local JAAK stub; the real onboarding uses
// `/api/v1/onboarding/sessions/{id}/kyc/*`. We actively refuse to ship a real
// Bearer token to the browser via the guard below so a misconfigured CI
// (`VITE_JAAK_API_KEY=<real_key>`) cannot silently leak credentials.

function createJaakClient(): AxiosInstance {
  const jaakConfig = getJaakConfig();

  if (jaakConfig.apiKey) {
    // Refuse to instantiate the client if a non-empty API key is present in
    // the browser bundle. This prevents accidental credential exposure in
    // production builds. KYC must go through the Tankef backend proxy.
    throw new Error(
      '[JAAK] Refusing to initialize browser-side JAAK client with an API key. ' +
        'Move all JAAK calls to the Tankef backend and stop setting VITE_JAAK_API_KEY.'
    );
  }

  const client = axios.create({
    baseURL: jaakConfig.apiUrl,
    timeout: 60000, // 60s timeout for image processing
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Request interceptor. We deliberately do NOT set an Authorization header
  // here — see the security note above. If a future implementation needs to
  // authenticate, it must do so via the Tankef backend proxy.
  client.interceptors.request.use(
    requestConfig => {
      requestConfig.headers['X-Correlation-ID'] = crypto.randomUUID();
      logger.debug(`JAAK API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`);
      return requestConfig;
    },
    error => {
      logger.error('JAAK API Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    response => {
      logger.debug(`JAAK API Response: ${response.status} ${response.config.url}`);
      return response;
    },
    (error: AxiosError) => {
      if (error.response) {
        logger.error(
          `JAAK API Error: ${error.response.status} ${error.config?.url}`,
          error.response.data
        );
      } else if (error.request) {
        logger.error('JAAK API Error: No response received', error.request);
      } else {
        logger.error('JAAK API Error:', error.message);
      }
      return Promise.reject(error);
    }
  );

  return client;
}

// Lazy initialization to avoid issues during module load
let _jaakClient: AxiosInstance | null = null;
function getJaakClient(): AxiosInstance {
  if (!_jaakClient) {
    _jaakClient = createJaakClient();
  }
  return _jaakClient;
}

export const jaakApi = {
  /**
   * Create a new verification session
   */
  createSession: async (data?: JaakCreateSessionRequest): Promise<JaakCreateSessionResponse> => {
    const response = await getJaakClient().post('/v1/kyc/sessions', data || {});
    return response.data;
  },

  /**
   * Full identity verification - document + selfie + liveness
   */
  verifyIdentity: async (data: JaakVerificationRequest): Promise<JaakVerificationResponse> => {
    const response = await getJaakClient().post('/v1/kyc/verify', {
      front_image: data.front_image,
      back_image: data.back_image,
      selfie_image: data.selfie_image,
      document_type: data.document_type,
    });
    return response.data;
  },

  /**
   * Analyze a document (front or back)
   */
  analyzeDocument: async (
    data: JaakDocumentAnalysisRequest
  ): Promise<JaakDocumentAnalysisResponse> => {
    const response = await getJaakClient().post('/v1/document/analyze', {
      image: data.image,
      side: data.side,
      document_type: data.document_type,
    });
    return response.data;
  },

  /**
   * Check liveness from a selfie
   */
  checkLiveness: async (data: JaakLivenessRequest): Promise<JaakLivenessResponse> => {
    const response = await getJaakClient().post('/v1/liveness/check', {
      image: data.image,
    });
    return response.data;
  },

  /**
   * Compare faces between document photo and selfie
   */
  matchFaces: async (data: JaakFaceMatchRequest): Promise<JaakFaceMatchResponse> => {
    const response = await getJaakClient().post('/v1/one-to-one/compare', {
      document_image: data.document_image,
      selfie_image: data.selfie_image,
    });
    return response.data;
  },

  /**
   * Get verification result by ID
   */
  getVerification: async (verificationId: string): Promise<JaakVerificationResponse> => {
    const response = await getJaakClient().get(`/v1/kyc/verifications/${verificationId}`);
    return response.data;
  },
};

// ============================================================================
// HTTP ERROR HELPERS (re-export from ./errors.ts)
// ============================================================================
export {
  getHttpStatus,
  getResponseData,
  isHttpStatus,
  is401,
  is409,
  is422,
  is429,
  is5xx,
  getErrorCode,
  getBackendErrorCode,
  getErrorMessage,
  extractBackendErrorMessage,
} from './errors';
